/**
 * SAP CPI Monitoring Dashboard - Integration Content Module
 * Copyright (c) 2026 David Jahn
 * Contact: david.jahnxx@gmail.com
 */
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useTranslation, Trans } from "react-i18next";
import i18n from "i18next";
import { useApiQuery } from "./hooks/useApiQuery";
import useContentRuntime from "./hooks/useContentRuntime";
import { contentVersion, contentRuntimeDisplay } from "./utils/contentRuntime";
import { sauberesHtml } from "./utils/sanitizeHtml";
import useDashboardStore from "./store/useDashboardStore";
import MuiButton from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiChip from "@mui/material/Chip";
import MuiPaper from "@mui/material/Paper";
import MuiTabs from "@mui/material/Tabs";
import MuiTab from "@mui/material/Tab";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MuiIconButton from "@mui/material/IconButton";
import MuiTooltip from "@mui/material/Tooltip";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import MuiCard from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import FilterListIcon from "@mui/icons-material/FilterList";
import DescriptionIcon from "@mui/icons-material/Description";
import ChatIcon from "@mui/icons-material/Chat";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BpmnCompareDialog from "./components/BpmnCompareDialog";
import IflowDiagram from "./components/IflowDiagram";
import { IflowDocumentationButton, IflowDocumentationPreview, useIflowDocumentationIndex } from "./components/IflowDocumentation";
import { readDocumentationStream } from "./utils/iflowDocumentationBindings";
// === Design System (Phase 0) ===
import { api, v } from "./utils/helpers";
import { hasEmptyRequiredParam } from "./utils/transport-config-helpers";
import { FONT, MONO } from "./theme/tokens";
import { useAppTheme } from "./theme/ThemeContext";
const stripHtml = (s) => {
  if (!s) return "";
  const tmp = s.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&[a-z]+;/gi, " ");
  return tmp.trim();
};

// Parse Content Modifier XML table format into structured data
// SAP CPI stores table XML as HTML-encoded entities inside ifl:property values
function parseContentModifierTable(xmlStr) {
  if (!xmlStr || typeof xmlStr !== "string") return null;
  // Decode HTML entities (SAP CPI encodes XML within XML: &lt;row&gt; → <row>)
  const decoded = xmlStr
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  const trimmed = decoded.trim();
  if (!trimmed.includes("<row>") && !trimmed.includes("<row ")) return null;
  const rows = [];
  const rowRe = /<row[^>]*>([\s\S]*?)<\/row>/g;
  let rm;
  while ((rm = rowRe.exec(trimmed)) !== null) {
    // Handle both single and double quotes for cell id attribute
    const cellRe = /<cell\s+id=["']([^"']*)["'][^>]*>([\s\S]*?)<\/cell>|<cell\s+id=["']([^"']*)["'][^>]*\/>/g;
    const row = {};
    let cm;
    while ((cm = cellRe.exec(rm[1])) !== null) {
      const key = cm[1] || cm[3];
      const val = cm[2] !== undefined ? cm[2] : "";
      if (key) row[key] = val;
    }
    if (Object.keys(row).length > 0) rows.push(row);
  }
  return rows.length > 0 ? rows : null;
}

// Content Modifier table renderer
// Die Spaltenkennungen kommen aus dem SAP XML (cell id) und bleiben woertlich.
// Die Beschriftung laeuft ueber den Katalog: SAP zeigt diese Spalten in jeder
// Sprache englisch an, deshalb steht dort in beiden Faellen dasselbe Wort.
const CM_TABLE_COLS = ["Action", "Name", "Type", "Value", "Default", "Datatype"];
function ContentModifierTable({ rows }) {
  const { t } = useTranslation("content");
  const cols = CM_TABLE_COLS;
  return (
    <div style={{ borderRadius: 6, border: "1px solid var(--border-subtle)", overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: MONO }}>
        <thead>
          <tr style={{ background: "var(--surface-2)" }}>
            {cols.map(c => (
              <th key={c} style={{ textAlign: "left", padding: "4px 8px", color: "var(--text-3)", fontWeight: 600, fontSize: 10, borderBottom: "1px solid var(--border-subtle)" }}>{t(`cmTable.${c.toLowerCase()}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "var(--surface-1)" : "var(--surface-2)" }}>
              {cols.map(c => (
                <td key={c} style={{ padding: "3px 8px", color: "var(--text-1)", borderBottom: "1px solid var(--border-subtle)" }}>
                  {row[c] || "\u2014"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Simple Groovy/JS syntax highlighting
function highlightGroovyScript(code) {
  if (!code) return null;
  return code.split("\n").map((line, li) => {
    // Line comment
    const commentIdx = line.indexOf("//");
    const blockCommentStart = line.indexOf("/*");
    if (commentIdx >= 0 && (line.indexOf('"') === -1 || commentIdx < line.indexOf('"'))) {
      const before = line.substring(0, commentIdx);
      const comment = line.substring(commentIdx);
      return <div key={li}>{tokenizeLine(before)}<span style={{ color: "var(--text-3)", fontStyle: "italic" }}>{comment}</span></div>;
    }
    if (blockCommentStart >= 0) {
      return <div key={li}><span style={{ color: "var(--text-3)", fontStyle: "italic" }}>{line}</span></div>;
    }
    return <div key={li}>{tokenizeLine(line)}</div>;
  });
}

function tokenizeLine(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    // String literal
    const strMatch = remaining.match(/^(["'])(?:(?!\1|\\).|\\.)*\1/);
    if (strMatch) {
      parts.push(<span key={key++} style={{ color: "#059669" }}>{strMatch[0]}</span>);
      remaining = remaining.slice(strMatch[0].length);
      continue;
    }
    // Number
    const numMatch = remaining.match(/^\b\d+(\.\d+)?\b/);
    if (numMatch) {
      parts.push(<span key={key++} style={{ color: "#d97706" }}>{numMatch[0]}</span>);
      remaining = remaining.slice(numMatch[0].length);
      continue;
    }
    // Keyword
    const kwMatch = remaining.match(/^\b(def|import|class|return|if|else|for|while|try|catch|finally|new|null|true|false|void|static|final)\b/);
    if (kwMatch) {
      parts.push(<span key={key++} style={{ color: "#7c3aed" }}>{kwMatch[0]}</span>);
      remaining = remaining.slice(kwMatch[0].length);
      continue;
    }
    // Normal char
    const nextSpecial = remaining.slice(1).search(/["'0-9]|\b(def|import|class|return|if|else|for|while|try|catch|finally|new|null|true|false|void|static|final)\b/);
    if (nextSpecial >= 0) {
      parts.push(<span key={key++} style={{ color: "var(--text-1)" }}>{remaining.slice(0, nextSpecial + 1)}</span>);
      remaining = remaining.slice(nextSpecial + 1);
    } else {
      parts.push(<span key={key++} style={{ color: "var(--text-1)" }}>{remaining}</span>);
      remaining = "";
    }
  }
  return parts;
}

// Parse ifl:properties directly from raw BPMN XML for a given element ID
function extractIflPropsFromXml(bpmnXmlStr, elementId) {
  if (!bpmnXmlStr || !elementId) return {};
  try {
    const idEscaped = elementId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const elMatch = bpmnXmlStr.match(
      new RegExp(`<(?:bpmn2:)?(?:callActivity|serviceTask|scriptTask|sendTask|receiveTask|task)[^>]*id="${idEscaped}"[\\s\\S]*?<\\/(?:bpmn2:)?(?:callActivity|serviceTask|scriptTask|sendTask|receiveTask|task)>`)
    );
    if (!elMatch) return {};
    const props = {};
    const propRe = /<ifl:property>\s*<key>([^<]+)<\/key>\s*<value>([^<]*)<\/value>/g;
    let m;
    while ((m = propRe.exec(elMatch[0])) !== null) {
      if (m[1]) props[m[1].trim()] = m[2]?.trim() || '';
    }
    return props;
  } catch { return {}; }
}

// ============================================================
// CHAT PANEL
// ============================================================
function ChatPanel({ systemContext, placeholder }) {
  const { t } = useTranslation("content");
  const { theme: cpTheme } = useAppTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [toolStatus, setToolStatus] = useState(""); // MCP tool progress
  const chatEnd = useRef(null);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamText]);

  const renderMd = (text) => sauberesHtml(String(text ?? "")
    .replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${cpTheme==="light"?"#3B8ED8":"var(--accent-light)"}">$1</strong>`)
    .replace(/`([^`]+)`/g, `<code style="background:${cpTheme==="light"?"#E4ECF5":"var(--surface-0)"};padding:1px 5px;border-radius:3px;font-size:11px;color:${cpTheme==="light"?"#1D3557":"#c4b5fd"}">$1</code>`));

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs); setInput(""); setLoading(true); setStreamText(""); setToolStatus("");
    try {
      const resp = await fetch("/api/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("cpi_token") },
        body: JSON.stringify({ messages: newMsgs, iflowContext: systemContext })
      });
      if (!resp.ok) throw new Error("Stream fehlgeschlagen");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const d = line.slice(6);
            if (d === "[DONE]") break;
            try {
              const j = JSON.parse(d);
              if (j.text) { full += j.text; setStreamText(full); setToolStatus(""); }
              if (j.error) { full = t("chat.fehler", { fehler: j.error }); setStreamText(full); }
              if (j.tool && j.status === "running") setToolStatus(t("chat.ladeWerkzeug", { werkzeug: j.tool.replace(/_/g," ") }));
              if (j.tool && j.status === "done") setToolStatus(t("chat.analysiereKurz"));
              if (j.ping) setToolStatus(prev => prev || t("chat.warten"));
            } catch {}
          }
        }
      }
      setMessages([...newMsgs, { role: "assistant", content: full || t("chat.keineAntwort") }]);
      setStreamText(""); setToolStatus("");
    } catch (e) {
      // Fallback to non-streaming
      try {
        const data = await api("/api/chat", { method: "POST", body: JSON.stringify({ messages: newMsgs, iflowContext: systemContext }) });
        setMessages([...newMsgs, { role: "assistant", content: data.response }]);
      } catch (e2) { setMessages([...newMsgs, { role: "assistant", content: t("chat.fehler", { fehler: e2.message }) }]); }
      setStreamText("");
    }
    setLoading(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.length === 0 && !streamText && <div style={{ textAlign: "center", padding: 20, color: "var(--text-2)", fontSize: 13 }}>{placeholder || t("chat.standard")}</div>}
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: 12, fontSize: 13, lineHeight: 1.6,
              background: m.role === "user" ? (cpTheme==="light"?"#1B2A4A":"var(--accent)") : (cpTheme==="light"?"#fff":"var(--surface-2)"),
              color: m.role === "user" ? "#fff" : "var(--text-1)",
              border: m.role === "assistant" && cpTheme==="light" ? "1px solid var(--border)" : "none" }}
              dangerouslySetInnerHTML={{ __html: m.role === "assistant" ? renderMd(m.content).replace(/\n/g, "<br>") : m.content.replace(/\n/g, "<br>") }} />
          </div>
        ))}
        {streamText && <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div style={{ maxWidth: "85%", padding: "10px 14px", borderRadius: 12, fontSize: 13, lineHeight: 1.6,
            background: cpTheme==="light"?"#fff":"var(--surface-2)", color: "var(--text-1)",
            border: cpTheme==="light" ? "1px solid var(--border)" : "none" }}
            dangerouslySetInnerHTML={{ __html: renderMd(streamText).replace(/\n/g, "<br>") }} />
        </div>}
        {loading && !streamText && <div style={{ padding: "10px 14px", borderRadius: 12, background: "var(--surface-2)", color: "var(--text-2)", fontSize: 13, width: "fit-content" }}>
          <span style={{ display: "inline-block", animation: "pulse 1.5s infinite" }}>{toolStatus || t("chat.analysiere")}</span>
        </div>}
        <div ref={chatEnd} />
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${cpTheme==="light"?"var(--border)":"var(--surface-2)"}`, display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }}
          placeholder={t("chat.eingabe")} style={{ flex: 1, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 14px", color: "var(--text-1)", fontSize: 13, outline: "none" }} />
        <button onClick={send} disabled={!input.trim() || loading} style={{
          background: cpTheme==="light"?"#1B2A4A":"var(--accent)", border: "none", borderRadius: 8,
          color: "#fff", padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (!input.trim() || loading) ? 0.5 : 1 }}>→</button>
      </div>
    </div>
  );
}

// ============================================================
// Pipeline Konzept - Deploy Reihenfolge Helpers (Modul 3)
// Consumer iFlows MÜSSEN vor Producer deployed werden (SAP Vorgabe)
// ============================================================
const _PIPELINE_ROLE_ORDER = { script: 0, generic: 1, rai: 2, mapping: 3, other: 4, normal: 5, sai: 6 };
const _classifyPipelineRole = (name) => {
  if (/Pipeline v2 - Script Collection/i.test(name)) return "script";
  if (/Pipeline v2 (Generic Step|Template|Integrated Messaging Runtime)/i.test(name)) return "generic";
  if (/\bRAI\b|Receiver\s+Adapter\s+iFlow/i.test(name)) return "rai";
  if (/\bSAI\b|Sender\s+Adapter\s+iFlow/i.test(name)) return "sai";
  if (/[Mm]apping.*[Pp]ipeline|[Pp]ipeline.*[Mm]apping/i.test(name)) return "mapping";
  if (/[Pp]ipeline/i.test(name)) return "other";
  return "normal";
};

// ============================================================
// Access Policy Compliance Pre Flight Gate
// Liefert {ok, info}. ok=true bedeutet Transport darf weitergehen.
// Defensiv: bei jedem Fehler oder fehlendem Cache returnt ok=true,
// damit der bestehende Transport Flow nicht blockiert wird.
// ============================================================
async function runComplianceGate(targetId, pkg, artefakte) {
  if (!targetId || !pkg) return { ok: true, info: { status: "skipped" } };
  try {
    const r = await api("/api/transport/compliance-check", {
      method: "POST",
      body: JSON.stringify({
        targetInstanceId: targetId,
        targetPackage: { id: pkg.Id || pkg.id, name: pkg.Name || pkg.name },
        artefakte: (artefakte || []).map(a => ({
          id: a.Id || a.id,
          name: a.Name || a.name,
          type: a.type || "IntegrationFlow",
        })),
      }),
    });
    if (r.status === "ok" || r.status === "unavailable" || r.status === "disabled" || r.status === "skipped") return { ok: true, info: r };
    // Modulweite Hilfsfunktion ohne Hook: dieselbe Instanz wie in App.jsx.
    const lines = [];
    if (r.status === "no_match") lines.push(i18n.t("content:gate.keinTreffer"));
    if (r.status === "ambiguous") lines.push(i18n.t("content:gate.mehrdeutig"));
    if (r.status === "artefacts_outside") lines.push(i18n.t("content:gate.artefakteAusserhalb", { policy: r.detectedPolicy?.roleName || "" }));
    if (r.hint) lines.push(r.hint);
    if (Array.isArray(r.misfits) && r.misfits.length) {
      lines.push(i18n.t("content:gate.betroffen", { namen: r.misfits.map(m => m.name).join(", ") }));
    }
    lines.push("");
    lines.push(i18n.t("content:gate.trotzdem"));
    return { ok: window.confirm(lines.join("\n")), info: r };
  } catch (e) {
    // Defensiv: Pre Flight darf den Transport nicht beschädigen
    console.warn("[ComplianceGate] check failed, transport proceeds without policy check:", e.message);
    return { ok: true, info: { status: "error", error: e.message } };
  }
}

// ============================================================
// MAIN: Integration Content
// ============================================================
export default function IntegrationContent({ instance, hiddenFlows = [], iflows: runtimeFlows = [], targetFlowName, onClearTarget, onFlowSelect, permissions={transport:true,content:true,alerting:true}, isVisible=true }) {
  const hasBeenVisible = useRef(false);
  if(isVisible) hasBeenVisible.current = true;
  const { t, i18n: i18nInst } = useTranslation(["content", "gemeinsam"]);
  // Zahlen und Datumsangaben folgen der gewaehlten Sprache. Ein fest
  // eingestelltes "de-DE" macht aus 18.859 auf Englisch achtzehn Komma acht.
  const gebiet = i18nInst.language === "en" ? "en-GB" : "de-DE";
  const { theme } = useAppTheme();
  const [packages, setPackages] = useState([]);
  const [allArtifacts, setAllArtifacts] = useState([]); // accumulated across all packages for pipeline detection
  const [pkgArtifactCounts, setPkgArtifactCounts] = useState({});
  const [pkgArtsByPkg, setPkgArtsByPkg] = useState({});
  const [pkgSearch, setPkgSearch] = useState("");
  const [hiddenPkgs, setHiddenPkgs] = useState([]);
  const [hiddenFlowsByPkg, setHiddenFlowsByPkg] = useState({});
  const [showPkgFilter, setShowPkgFilter] = useState(false);
  const [showFlowFilter, setShowFlowFilter] = useState(false);
  const [selPkg, setSelPkg] = useState(null);
  const [flowList, setFlowList] = useState([]);
  const [selFlow, setSelFlow] = useState(null);
  const contentRuntime = useContentRuntime(instance.id, isVisible && !!selPkg);
  const documentation = useIflowDocumentationIndex(instance.id, isVisible && !!selPkg, selPkg?.Id);
  const [documentationPreview, setDocumentationPreview] = useState(null);
  useEffect(() => { setDocumentationPreview(null); }, [instance.id, selPkg?.Id]);
  const [extracted, setExtracted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  // Panels
  const [bottomPanel, setBottomPanel] = useState(false);
  const [bottomTab, setBottomTab] = useState("details");
  const [rightPanel, setRightPanel] = useState(false);
  const [docPanel, setDocPanel] = useState(false);
  const [docContent, setDocContent] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docSaveError, setDocSaveError] = useState(false);
  const [docEstimate, setDocEstimate] = useState(null); // { seconds, label, phases }
  const [docUsage, setDocUsage] = useState(null);       // { inputTokens, outputTokens, rounds }
  const [showDocDebug, setShowDocDebug] = useState(false);
  const [docStyle, setDocStyle] = useState("technical");
  const [showDocStylePicker, setShowDocStylePicker] = useState(false); // header picker
  const [showRegenPicker, setShowRegenPicker] = useState(false); // inside doc panel
  const [docCache, setDocCache] = useState({}); // { "technical": "...", "executive": "..." }
  const [scriptModal, setScriptModal] = useState(null); // { name, content } for fullscreen view
  // Resizable panels (desktop only)
  const [docHeight, setDocHeight] = useState(55);
  const [rightWidth, setRightWidth] = useState(380);
  const docDrag = useRef(null);
  const rightDrag = useRef(null);
  const onDocDragStart = (e) => { if (isMobile) return; e.preventDefault(); docDrag.current = { startY: e.clientY, startH: docHeight }; const onMove = (ev) => { const dy = docDrag.current.startY - ev.clientY; const container = document.querySelector("[data-bpmn-container]"); if (!container) return; const pct = ((docDrag.current.startH / 100) * container.clientHeight + dy) / container.clientHeight * 100; setDocHeight(Math.max(20, Math.min(85, pct))); }; const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); }; document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp); };
  const onRightDragStart = (e) => { if (isMobile) return; e.preventDefault(); rightDrag.current = { startX: e.clientX, startW: rightWidth }; const onMove = (ev) => { const dx = rightDrag.current.startX - ev.clientX; setRightWidth(Math.max(280, Math.min(700, rightDrag.current.startW + dx))); }; const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); }; document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp); };

  // === TanStack Query (nur aktiv wenn experimentalCaching=true) ===
  const { data: optimizedPkgs, isOptimized: isPkgsOptimized } = useApiQuery(
    ["instance", instance.id, "packages"],
    `/api/${instance.id}/content/packages`,
    { staleTime: 120000, enabled: !!instance.id && hasBeenVisible.current }
  );
  const { data: optimizedFilters, isOptimized: isFiltersOptimized } = useApiQuery(
    ["instance", instance.id, "filters"],
    `/api/instances/${instance.id}/filters`,
    { staleTime: 300000, enabled: !!instance.id && hasBeenVisible.current }
  );

  // Optimized-Daten in lokalen State übernehmen
  useEffect(() => {
    if (!isFiltersOptimized || !optimizedFilters) return;
    setHiddenPkgs(optimizedFilters.hiddenPkgs || []);
    setHiddenFlowsByPkg(optimizedFilters.hiddenFlowsByPkg || {});
  }, [isFiltersOptimized, optimizedFilters]);

  // Load persistent filters (Legacy - nur wenn NICHT optimiert)
  useEffect(() => {
    if (isFiltersOptimized) return;
    if(!hasBeenVisible.current) return;
    let cancelled=false;
    api(`/api/instances/${instance.id}/filters`).then(f => {
      if(cancelled) return;
      setHiddenPkgs(f.hiddenPkgs || []);
      setHiddenFlowsByPkg(f.hiddenFlowsByPkg || {});
    }).catch(() => {});
    return ()=>{cancelled=true;};
  }, [instance.id, isVisible, isFiltersOptimized]);
  const saveFilters = (pkgs, flowsByPkg) => {
    api(`/api/instances/${instance.id}/filters`, { method: "PUT", body: JSON.stringify({ hiddenPkgs: pkgs, hiddenFlowsByPkg: flowsByPkg }) }).catch(() => {});
  };
  const togglePkg = (pkgId) => {
    const nxt = hiddenPkgs.includes(pkgId) ? hiddenPkgs.filter(x => x !== pkgId) : [...hiddenPkgs, pkgId];
    setHiddenPkgs(nxt); saveFilters(nxt, hiddenFlowsByPkg);
  };
  const toggleFlowInPkg = (pkgId, flowId) => {
    const cur = hiddenFlowsByPkg[pkgId] || [];
    const nxt = cur.includes(flowId) ? cur.filter(x => x !== flowId) : [...cur, flowId];
    const updated = { ...hiddenFlowsByPkg, [pkgId]: nxt };
    setHiddenFlowsByPkg(updated); saveFilters(hiddenPkgs, updated);
  };

  const bulkLoadGen = useRef(0);
  const [bulkLoadTrigger, setBulkLoadTrigger] = useState(0);
  const loadAllFlows = useCallback((pkgs, cancelled) => {
    if (!pkgs || pkgs.length === 0) { setAllArtifacts([]); return; }
    const gen = ++bulkLoadGen.current;
    const artAccumulator = [];
    let pending = pkgs.length;
    const queue=[...pkgs];let running=0;const MAX_CONCURRENT=5;
    const runNext=()=>{while(running<MAX_CONCURRENT&&queue.length){running++;const pkg=queue.shift();
      api(`/api/${instance.id}/content/packages/${encodeURIComponent(pkg.Id)}/flows`).then(flows=>{
        if(cancelled?.current || gen !== bulkLoadGen.current) return;
        setPkgArtifactCounts(prev=>({...prev,[pkg.Id]:flows.length}));
        setPkgArtsByPkg(prev=>({...prev,[pkg.Id]:flows}));
        flows.forEach(f=>artAccumulator.push(f));
      }).catch(()=>{}).finally(()=>{running--;pending--;
        if(gen === bulkLoadGen.current && !cancelled?.current){if(pending===0)setAllArtifacts([...artAccumulator]);else runNext();}
      });
    }};
    runNext();
  }, [instance.id]);
  // Packages-Sync bei aktivem experimentalCaching
  useEffect(() => {
    if (!isPkgsOptimized || !optimizedPkgs) return;
    setPackages(optimizedPkgs);
    if (!targetFlowName) loadAllFlows(optimizedPkgs, {current:false});
    setLoading(false);
  }, [isPkgsOptimized, optimizedPkgs, loadAllFlows, targetFlowName]);

  // Legacy Packages-Load (nur wenn NICHT optimiert)
  useEffect(() => {
    if (isPkgsOptimized) return;
    if(!hasBeenVisible.current) return;
    const cancelled={current:false};
    setLoading(true);
    setPkgArtifactCounts({});setPkgArtsByPkg({});setAllArtifacts([]);
    api(`/api/${instance.id}/content/packages`).then(pkgs=>{
      if(cancelled.current) return;
      setPackages(pkgs);
      if(targetFlowName) { setLoading(false); return; }
      loadAllFlows(pkgs, cancelled);
    }).catch(e => {if(!cancelled.current)setError(e.message);}).finally(() => {if(!cancelled.current)setLoading(false);});
    return ()=>{cancelled.current=true;};
  }, [instance.id, isVisible, loadAllFlows, isPkgsOptimized]);
  useEffect(() => {
    if (bulkLoadTrigger > 0 && packages.length > 0) loadAllFlows(packages, {current:false});
  }, [bulkLoadTrigger, packages, loadAllFlows]);

  const selectPkg = useCallback(async (pkg) => {
    setSelPkg(pkg); setSelFlow(null); setExtracted(null); setFlowList([]);
    if(onFlowSelect) onFlowSelect(null);
    try { setFlowList(await api(`/api/${instance.id}/content/packages/${encodeURIComponent(pkg.Id)}/flows`)); } catch (e) { setError(e.message); }
  }, [instance.id, onFlowSelect]);

  const selectFlow = useCallback(async (flow) => {
    setDocSaveError(false);
    setSelFlow(flow); setLoadingFlow(true); setExtracted(null); setSelectedStep(null); setBottomPanel(false); setRightPanel(false);
    if(onFlowSelect) onFlowSelect(flow.Name);
    // Strikte Instanz-Trennung: Flow-Kontext für den AI-Chat (Multi-Agent) registrieren
    useDashboardStore.getState().setCurrentFlowContext({
      instanceId: instance.id, instanceName: instance.name,
      flowId: flow.Id, flowName: flow.Name,
    });
    try {
      const data = await api(`/api/${instance.id}/content/flows/${encodeURIComponent(flow.Id)}/extract`);
      setExtracted(data);
    } catch (e) { setError(e.message); }
    setLoadingFlow(false);
    // Load existing documentation for all styles
    const cache = {};
    for (const style of ["technical", "executive", "onboarding"]) {
      try {
        const doc = await api(`/api/docs/${instance.id}/${encodeURIComponent(flow.Id)}:${style}`);
        if (doc?.content) cache[style] = doc.content;
      } catch {}
    }
    setDocCache(cache);
    // Show first available doc
    if (cache.technical) { setDocContent(cache.technical); setDocStyle("technical"); }
    else if (cache.executive) { setDocContent(cache.executive); setDocStyle("executive"); }
    else if (cache.onboarding) { setDocContent(cache.onboarding); setDocStyle("onboarding"); }
    else { setDocContent(null); }
    // Load alerts for this iFlow
    try{const al=await api(`/api/${instance.id}/alerts/${encodeURIComponent(flow.Name)}`);const al2=await api(`/api/${instance.id}/alerts/${encodeURIComponent(flow.Id)}`).catch(()=>[]);const all=[...al];for(const a of al2){if(!all.find(x=>x.id===a.id))all.push(a);}setAlertList(all);}catch{setAlertList([]);}
  }, [instance.id, onFlowSelect]);

  // Flow-Kontext beim Verlassen des Content-Moduls clearen (Page-Wechsel)
  useEffect(() => () => { useDashboardStore.getState().clearCurrentFlowContext(); }, []);

  // Alert management
  const loadAlerts=async(flowName)=>{try{const al=await api(`/api/${instance.id}/alerts/${encodeURIComponent(flowName)}`);setAlertList(al);}catch{setAlertList([]);}};
  // Also load by Id
  const loadAlertsFull=async(flow)=>{try{
    const[byName,byId]=await Promise.all([
      api(`/api/${instance.id}/alerts/${encodeURIComponent(flow.Name)}`).catch(()=>[]),
      api(`/api/${instance.id}/alerts/${encodeURIComponent(flow.Id)}`).catch(()=>[]),
    ]);
    const all=[...byName];
    for(const a of byId){if(!all.find(x=>x.id===a.id))all.push(a);}
    setAlertList(all);
  }catch{setAlertList([]);}};
  const createAlert=async()=>{
    if(!alertEmail.trim()||!selFlow) return;
    setAlertSaving(true);
    try{
      const emails=alertEmail.split(",").map(e=>e.trim()).filter(Boolean);
      await api(`/api/${instance.id}/alerts`,{method:"POST",body:JSON.stringify({iflowName:selFlow.Name,iflowId:selFlow.Id,emails,useAi:alertUseAi,customText:alertCustomText})});
      await loadAlerts(selFlow.Name);
      setAlertEmail("");setAlertCustomText("");
    }catch(e){alert(t("alerting.fehler",{fehler:e.message}));}
    setAlertSaving(false);
  };
  const deleteAlert=async(alertId)=>{
    try{await api(`/api/${instance.id}/alerts/${alertId}`,{method:"DELETE"});
    setAlertList(p=>p.filter(a=>a.id!==alertId));}catch{}
  };
  const toggleAlert=async(alertId,enabled)=>{
    try{await api(`/api/${instance.id}/alerts/${alertId}`,{method:"PUT",body:JSON.stringify({enabled})});
    setAlertList(p=>p.map(a=>a.id===alertId?{...a,enabled}:a));}catch{}
  };

  // Auto-navigate to target iFlow (from message detail "iFlow einsehen" or Pipeline Topology Webapp link)
  const lastNavTarget = useRef(null);
  useEffect(() => {
    if (!targetFlowName) return;
    if (lastNavTarget.current === targetFlowName) return;
    lastNavTarget.current = targetFlowName;
    (async () => {
      try {
        const resolved = await api(`/api/${instance.id}/resolve-package/${encodeURIComponent(targetFlowName)}`);
        if (resolved?.packageId) {
          setSelPkg({ Id: resolved.packageId, Name: resolved.packageName || resolved.packageId });
          const flows = await api(`/api/${instance.id}/content/packages/${encodeURIComponent(resolved.packageId)}/flows`);
          setFlowList(flows);
          const flow = flows.find(f => f.Name === targetFlowName || f.Id === targetFlowName);
          if (flow) selectFlow(flow);
        }
      } catch (err) { console.warn("[AutoNav] failed:", err); }
      if (onClearTarget) onClearTarget();
      setBulkLoadTrigger(t => t + 1);
    })();
  }, [targetFlowName, instance.id]);

  // Der Schluessel (technical/executive/onboarding) geht an den Server und
  // bleibt woertlich, die Beschriftung kommt aus dem Katalog.
  const DOC_STYLES = {
    technical: { label: t("doku.technischLabel"), desc: t("doku.technischDesc"), icon: "wrench" },
    executive: { label: t("doku.executiveLabel"), desc: t("doku.executiveDesc"), icon: "chart" },
    onboarding: { label: t("doku.onboardingLabel"), desc: t("doku.onboardingDesc"), icon: "users" },
  };
  const DOC_ICONS={
    wrench:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>,
    chart:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>,
    users:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
    custom:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    doc:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    chat:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
    code:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    confluence:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h16"/><path d="M4 6h16"/><path d="M4 18h16"/><circle cx="8" cy="6" r="1"/><circle cx="16" cy="12" r="1"/><circle cx="10" cy="18" r="1"/></svg>,
  };
  // Custom templates
  const[customTemplates,setCustomTemplates]=useState([]);
  const[showCustomForm,setShowCustomForm]=useState(false);
  const[customName,setCustomName]=useState("");const[customPrompt,setCustomPrompt]=useState("");
  const[confluencePublishing,setConfluencePublishing]=useState(false);const[confluenceResult,setConfluenceResult]=useState(null);
  // Transport Wizard
  const[showTransport,setShowTransport]=useState(false);const[transportStep,setTransportStep]=useState(0);
  const[transportConfigs,setTransportConfigs]=useState([]);const[transportPackages,setTransportPackages]=useState([]);
  const[transportTarget,setTransportTarget]=useState("");const[transportPkg,setTransportPkg]=useState("");
  const[transportDeployAfter,setTransportDeployAfter]=useState(false);const[transportLoading,setTransportLoading]=useState(false);
  const[transportResult,setTransportResult]=useState(null);const[transportApprovalNote,setTransportApprovalNote]=useState("");const[transportNewPkg,setTransportNewPkg]=useState("");const[transportNewPkgDesc,setTransportNewPkgDesc]=useState("");
  // Alert state
  const[showAlertModal,setShowAlertModal]=useState(false);
  const[alertList,setAlertList]=useState([]);
  const[alertEmail,setAlertEmail]=useState("");
  const[alertUseAi,setAlertUseAi]=useState(true);
  const[alertCustomText,setAlertCustomText]=useState("");
  const[alertSaving,setAlertSaving]=useState(false);
  const[alertDebugLog,setAlertDebugLog]=useState("");
  const[allInstances,setAllInstances]=useState([]);
  const[transportFlowIdOverride,setTransportFlowIdOverride]=useState("");
  const[precheck,setPrecheck]=useState(null);const[precheckLoading,setPrecheckLoading]=useState(false);
  const[transportWithCreds,setTransportWithCreds]=useState(false);
  const[transportCreds,setTransportCreds]=useState([{name:"",kind:"basic",user:"",password:""}]);
  const[transportSchedule,setTransportSchedule]=useState("");const[transportScheduleEnabled,setTransportScheduleEnabled]=useState(false);
  const[credAliases,setCredAliases]=useState([]);
  // Package Transport Wizard
  const[pkgTransport,setPkgTransport]=useState(null); // source package object or null
  const[pkgTrStep,setPkgTrStep]=useState(0);
  const[pkgTrTarget,setPkgTrTarget]=useState("");
  const[pkgTrComparison,setPkgTrComparison]=useState(null);
  const[pkgTrLoading,setPkgTrLoading]=useState(false);
  const[pkgTrGlobalRefs,setPkgTrGlobalRefs]=useState(null);
  const[pkgTrGlobalLoading,setPkgTrGlobalLoading]=useState(false);
  const[pkgTrDebugRefs,setPkgTrDebugRefs]=useState(null);
  const[pkgTrDebugLoading,setPkgTrDebugLoading]=useState(false);
  const[pkgTrGlobalExpanded,setPkgTrGlobalExpanded]=useState({});
  const globalRefsParamsRef=useRef(null);
  const[pkgTrResult,setPkgTrResult]=useState(null);const[pkgTrApprovalNote,setPkgTrApprovalNote]=useState("");
  // slzApprover war die Weiche der alten Vier-Augen-Schleuse. Die Schleuse ist ab 2026-08-17
  // aufgeloest; alle Freigaben laufen jetzt ueber die CI/CD-Pipeline (/api/cicd/transport/release
  // in TransportApprovals.jsx). Die Wizard-Zweige unten sind in der aktuellen Oberflaeche nicht
  // mehr erreichbar. Bis sie ausgebaut sind, bleibt der Approver-Pfad hart auf true - damit sie
  // keinen Netzruf mehr auf den entfernten Endpunkt /api/transport-requests machen.
  const slzApprover=true;
  const[pkgTrDeploy,setPkgTrDeploy]=useState(false);
  const[pkgTrDeployRuntimes,setPkgTrDeployRuntimes]=useState([]);
  const[pkgTrWerkeRuntimes,setPkgTrWerkeRuntimes]=useState({});
  const[pkgTrCustomId,setPkgTrCustomId]=useState(false);
  const[pkgTrTargetId,setPkgTrTargetId]=useState("");
  const[pkgTrTargetName,setPkgTrTargetName]=useState("");
  const[pkgTrProgress,setPkgTrProgress]=useState([]);
  // Multi-target transport
  const[pkgTrMode,setPkgTrMode]=useState("single"); // "single" or "multi"
  const[pkgTrTargets,setPkgTrTargets]=useState([]); // multi: selected target IDs
  const[pkgTrMultiChecks,setPkgTrMultiChecks]=useState({}); // {instId__kennung: comparisonResult}
  const[pkgTrRelease,setPkgTrRelease]=useState(null); // Draft-Release-Editor {artId,loading,floor,suggested,source,version,error}
  const[pkgTrActiveTab,setPkgTrActiveTab]=useState(""); // active target tab in step 1
  const[pkgTrMultiResults,setPkgTrMultiResults]=useState({}); // {instId__kennung: result}
  // Werke selection: {instId: [{name, kennung, selected}]}
  const[pkgTrWerke,setPkgTrWerke]=useState({});
  const[werkExpanded,setWerkExpanded]=useState({});// {werkKey: bool}
  const[pkgTrConfigs,setPkgTrConfigs]=useState({}); // {artifactId: [{ParameterKey,ParameterValue,DataType,newValue}]}
  const[pkgTrConfigOpen,setPkgTrConfigOpen]=useState(null); // artifact ID currently being configured
  const[pkgTrConfigLoading,setPkgTrConfigLoading]=useState(false);
  // Undeploy before transport
  const[pkgTrUndeploy,setPkgTrUndeploy]=useState(false);
  const[pkgTrUndeployList,setPkgTrUndeployList]=useState([]); // deployed iFlows on target
  const[pkgTrUndeploySel,setPkgTrUndeploySel]=useState([]); // selected IDs to undeploy
  const[pkgTrUndeploySearch,setPkgTrUndeploySearch]=useState("");
  const[pkgTrUndeployLoading,setPkgTrUndeployLoading]=useState(false);
  const[pkgTrBpmnCompare,setPkgTrBpmnCompare]=useState(null);
  // Pipeline phases for Step 3 (live transport)
  const[pkgTrPhases,setPkgTrPhases]=useState([]); // [{id,label,status:"waiting"|"active"|"done"|"error",detail,duration}]
  const[pkgTrElapsed,setPkgTrElapsed]=useState(0);
  const[pkgTrTimer,setPkgTrTimer]=useState(null);
  // Partner Directory Transport (Feature 3)
  const[pkgTrPdEnabled,setPkgTrPdEnabled]=useState(false);
  const[pkgTrPdEntries,setPkgTrPdEntries]=useState([]); // [{Pid, stringCount, binaryCount, altCount, selected}]
  const[pkgTrPdLoading,setPkgTrPdLoading]=useState(false);
  // Orphan Selection (Artefakte nur im Ziel, per Default ausgewählt zum Löschen)
  const[pkgTrOrphanSel,setPkgTrOrphanSel]=useState({});// {artifactId: true/false} or {artifactId__werkKey: true/false}
  // Pipeline Pre-Flight Check
  const[preflightChecks,setPreflightChecks]=useState(null); // null=nicht geprüft, array=ergebnisse
  const[preflightLoading,setPreflightLoading]=useState(false);
  const runPreflightCheck=async(targetIid)=>{
    setPreflightLoading(true);setPreflightChecks(null);
    try{const r=await api(`/api/${instance.id}/pipeline-preflight?targetIid=${encodeURIComponent(targetIid)}`);setPreflightChecks(r.checks||[]);}
    catch(e){setPreflightChecks([{id:"error",category:"Error",label:"Fehler",status:"error",detail:e.message,critical:false}]);}
    setPreflightLoading(false);
  };
  const pkgTrHasDraftDeps=(pkgTrGlobalRefs?.references||[]).some(r=>r.draft);
  // Helper Funktionen muessen VOR pkgTrHasUnconfiguredNew stehen, weil die IIFE
  // unten sie synchron beim Render aufruft. Sonst Temporal Dead Zone Error.
  const getActiveTargetIdForKey=()=>pkgTrMode==="multi"?pkgTrActiveTab:pkgTrTarget;
  const computeConfigCacheKey=(artId,werkKey)=>{
    const activeWerk=werkKey||pkgTrActiveTab;
    if(activeWerk)return `${artId}__${activeWerk}`;
    const tgtId=getActiveTargetIdForKey();return tgtId?`${artId}__${tgtId}`:artId;
  };
  // Check if any selected "new" iFlow has unconfigured required sender/receiver params
  const pkgTrHasUnconfiguredNew=(()=>{
    const hasWerke=Object.keys(pkgTrMultiChecks).length>0;
    const allComps=hasWerke?Object.entries(pkgTrMultiChecks):pkgTrComparison?.comparison?[["single",{comparison:pkgTrComparison.comparison}]]:[];
    for(const[key,check] of allComps){
      if(!check?.comparison)continue;
      for(const art of check.comparison){
        if(!art.selected||art.draft||art.status!=="new")continue;
        const ck=hasWerke?`${art.Id}__${key}`:computeConfigCacheKey(art.Id);
        const cfgs=pkgTrConfigs[ck];
        if(!cfgs)return true; // Config not loaded yet → must open first
        // Pflicht nur, was SAP in parameters.propdef mit isRequired=true definiert
        if(hasEmptyRequiredParam(cfgs))return true;
      }
    }
    return false;
  })();
  useEffect(()=>{if(!hasBeenVisible.current)return;let c=false;api("/api/doc-templates").then(t=>{if(!c)setCustomTemplates(t);}).catch(()=>{});return()=>{c=true;};},[isVisible]);
  const addCustomTemplate=async()=>{
    if(!customName.trim()||!customPrompt.trim())return;
    try{const t=await api("/api/doc-templates",{method:"POST",body:JSON.stringify({name:customName,prompt:customPrompt})});
    setCustomTemplates(p=>[...p,t]);setCustomName("");setCustomPrompt("");setShowCustomForm(false);}catch{}
  };
  const deleteCustomTemplate=async(id)=>{
    try{await api(`/api/doc-templates/${id}`,{method:"DELETE"});setCustomTemplates(p=>p.filter(t=>t.id!==id));}catch{}
  };
  const publishToConfluence=async()=>{
    if(!docContent)return;
    setConfluencePublishing(true);setConfluenceResult(null);
    try{
      const title=`${selFlow.Name} - ${DOC_STYLES[docStyle]?.label||t("detail.dokuTitel")}`;
      const r=await api("/api/confluence/publish",{method:"POST",body:JSON.stringify({title,content:docContent})});
      setConfluenceResult({success:true,url:r.url});
    }catch(e){setConfluenceResult({success:false,error:e.message});}
    setConfluencePublishing(false);
  };
  // Transport wizard functions
  const openTransportWizard=async()=>{
    setShowTransport(true);setTransportStep(0);setTransportResult(null);setTransportConfigs([]);setTransportPackages([]);setTransportTarget("");setTransportPkg("");setTransportNewPkg("");setTransportNewPkgDesc("");setTransportFlowIdOverride("");setPrecheck(null);setTransportWithCreds(false);setTransportCreds([]);setTransportSchedule("");setTransportScheduleEnabled(false);setCredAliases([]);
    // Load configs + credential aliases from source in parallel
    try{
      const[configs,aliases]=await Promise.all([
        api(`/api/${instance.id}/transport/configurations/${encodeURIComponent(selFlow.Id)}`).catch(()=>[]),
        api(`/api/${instance.id}/transport/credentials/${encodeURIComponent(selFlow.Id)}`).catch(()=>[])
      ]);
      setTransportConfigs(configs.map(c=>({...c,newValue:c.ParameterValue})));
      setCredAliases(aliases);
      if(aliases.length) setTransportCreds(aliases.map(a=>({name:a.name||a,kind:a.authType||"basic",user:"",password:"",tokenUrl:"",clientId:"",clientSecret:"",scope:"",value:"",adapterType:a.adapterType||""})));
    }catch{}
    // Load all instances
    try{
      const spaces=await api("/api/spaces");
      const insts=[];
      for(const sp of spaces){try{const is=await api(`/api/spaces/${sp.id}/instances`);is.forEach(i=>insts.push({...i,spaceName:sp.name}));}catch{}}
      setAllInstances(insts.filter(i=>i.id!==instance.id));
    }catch{}
  };
  const loadTargetPackages=async(instId)=>{
    setTransportTarget(instId);setTransportPkg("");setTransportPackages([]);
    try{const pkgs=await api(`/api/${instId}/transport/packages`);setTransportPackages(pkgs);}catch{}
  };
  const[transportError,setTransportError]=useState("");
  const createTargetPackage=async()=>{
    if(!transportNewPkg.trim()||!transportNewPkgDesc.trim()||!transportTarget)return;
    setTransportError("");
    try{const pkg=await api(`/api/${transportTarget}/transport/create-package`,{method:"POST",body:JSON.stringify({name:transportNewPkg,description:transportNewPkgDesc})});
    setTransportPackages(p=>[...p,pkg]);setTransportPkg(pkg.Id);setTransportNewPkg("");setTransportNewPkgDesc("");}catch(e){setTransportError(e.message);}
  };
  const runPrecheck=async()=>{
    if(!transportTarget||!transportPkg)return;
    setPrecheckLoading(true);setPrecheck(null);setTransportFlowIdOverride("");setTransportApprovalNote("");
    try{const r=await api("/api/transport/precheck",{method:"POST",body:JSON.stringify({targetInstanceId:transportTarget,flowId:selFlow.Id,targetPackageId:transportPkg})});
    setPrecheck(r);}catch{}
    setPrecheckLoading(false);setTransportStep(2);
  };
  const executeTransport=async()=>{
    setTransportLoading(true);setTransportResult(null);
    try{
      const modifiedConfigs=transportConfigs.filter(c=>c.newValue!==c.ParameterValue).map(c=>({ParameterKey:c.ParameterKey,ParameterValue:c.newValue,DataType:c.DataType}));
      const validCreds=transportWithCreds?transportCreds.filter(c=>c.name&&c.kind!=="none"&&(c.kind==="basic"?(c.user&&c.password):c.kind==="oauth2_client_credentials"?(c.clientId&&c.clientSecret):c.kind==="secure_param"?c.value:true)):[];
      const r=await api("/api/transport/execute",{method:"POST",timeout:600000,body:JSON.stringify({
        approvalNote:transportApprovalNote.trim(),sourceInstanceId:instance.id,targetInstanceId:transportTarget,flowId:selFlow.Id,flowName:selFlow.Name,
        targetPackageId:transportPkg,configurations:modifiedConfigs.length?modifiedConfigs:null,deployAfter:transportDeployAfter,
        flowIdOverride:transportFlowIdOverride||undefined,credentials:validCreds.length?validCreds:undefined,
        scheduledAt:transportScheduleEnabled&&transportSchedule?new Date(transportSchedule).toISOString():undefined
      })});
      // Schleuse: Transport wurde als Auftrag eingereiht (Vier-Augen-Freigabe)
      if(r?.queued&&r?.approvalRequired){setTransportResult({success:true,queued:true,requestId:r.requestId});}
      else setTransportResult(r);
    }catch(e){setTransportResult({success:false,error:e.message});}
    setTransportLoading(false);
  };

  // ── Package Transport Wizard functions ──
    const openPkgTransport=async(pkg)=>{
    setPkgTransport(pkg);setPkgTrStep(0);setPkgTrTarget("");setPkgTrComparison(null);setPkgTrGlobalRefs(null);setPkgTrRefsLastRefresh(null);
    setPkgTrResult(null);setPkgTrDeploy(false);setPkgTrDeployRuntimes([]);setPkgTrWerkeRuntimes({});setPkgTrCustomId(false);
    setPkgTrTargetId(pkg.Id);setPkgTrTargetName(pkg.Name);setPkgTrProgress([]);
    setPkgTrConfigs({});setPkgTrConfigOpen(null);setPkgTrApprovalNote("");
    setPkgTrMode("single");setPkgTrTargets([]);setPkgTrMultiChecks({});setPkgTrActiveTab("");setPkgTrMultiResults({});
    setPkgTrWerke({});setWerkExpanded({});
    setPkgTrUndeploy(false);setPkgTrUndeployList([]);setPkgTrUndeploySel([]);setPkgTrUndeploySearch("");
    setPkgTrPhases([]);setPkgTrElapsed(0);
    setPkgTrPdEnabled(false);setPkgTrPdEntries([]);setPkgTrPdLoading(false);
    setPkgTrOrphanSel({});
    if(allInstances.length===0){
      try{
        const spaces=await api("/api/spaces");
        const insts=[];
        for(const sp of spaces){try{const is=await api(`/api/spaces/${sp.id}/instances`);is.forEach(i=>insts.push({...i,spaceName:sp.name}));}catch{}}
        setAllInstances(insts);// include ALL instances (including source) for Werk-based transport
      }catch{}
    }
  };
  const getActiveTargetId=()=>pkgTrMode==="multi"?pkgTrActiveTab:pkgTrTarget;
  const getActiveComparison=()=>pkgTrMode==="multi"?pkgTrMultiChecks[pkgTrActiveTab]:pkgTrComparison;
  const loadArtifactConfigs=async(artId,werkKey,kennung)=>{
    const tgtId=werkKey?werkKey.split("__")[0]:getActiveTargetId();
    const activeWerk=werkKey||pkgTrActiveTab;
    const comp=activeWerk&&pkgTrMultiChecks[activeWerk]?pkgTrMultiChecks[activeWerk]:getActiveComparison();
    const art=comp?.comparison?.find(a=>a.Id===artId);
    // Cache key MUST include werkKey to keep Werke configs separate
    const cacheKey=activeWerk?`${artId}__${activeWerk}`:(tgtId?`${artId}__${tgtId}`:artId);
    if(pkgTrConfigs[cacheKey]){setPkgTrConfigOpen(cacheKey);return;}
    setPkgTrConfigLoading(true);setPkgTrConfigOpen(cacheKey);
    try{
      // Source artifact ID (without kennung suffix)
      const sourceArtId=art?.originalId||artId;
      // Target artifact ID: USE the suffixedId from precheck (already verified to exist on target)
      // DO NOT reconstruct - precheck tried multiple patterns and found the correct one
      const targetArtId=art?.suffixedId||(kennung?`${sourceArtId}${kennung}`:artId);
      const isUpdate=art?.status==="update";
      const tgtParam=tgtId&&isUpdate?`?targetInstanceId=${tgtId}&targetFlowId=${encodeURIComponent(targetArtId)}`:"";
      // Debug: [CONFIG] Loading: source=${sourceArtId} target=${targetArtId} suffixedId=${art?.suffixedId} cache=${cacheKey} isUpdate=${isUpdate}
      const configs=await api(`/api/${instance.id}/transport/artifact-configs/${encodeURIComponent(sourceArtId)}${tgtParam}`);
      setPkgTrConfigs(prev=>({...prev,[cacheKey]:configs.map(c=>({...c,
        newValue:c.targetValue||"",
        fromTarget:!!c.targetValue,
        defaultValue:c.ParameterValue
      }))}));
    }catch{setPkgTrConfigs(prev=>({...prev,[cacheKey]:[]}));}
    setPkgTrConfigLoading(false);
  };
  const getConfigCacheKey=(artId,werkKey)=>{
    const activeWerk=werkKey||pkgTrActiveTab;
    if(activeWerk)return `${artId}__${activeWerk}`;
    const tgtId=getActiveTargetId();return tgtId?`${artId}__${tgtId}`:artId;
  };
  const updateArtifactConfig=(cacheKey,key,newVal)=>{
    setPkgTrConfigs(prev=>({...prev,[cacheKey]:(prev[cacheKey]||[]).map(c=>c.ParameterKey===key?{...c,newValue:newVal,fromTarget:false}:c)}));
  };
  // Build configurations payload: use newValue, fall back to defaultValue for empty "other" params
  const buildConfigPayload=(cfgs)=>{
    if(!cfgs)return undefined;
    const modified=cfgs.filter(c=>{
      const effectiveValue=c.newValue||(c.group!=="sender"&&c.group!=="receiver"?c.defaultValue:"");
      return effectiveValue&&effectiveValue!==c.defaultValue;
    }).map(c=>({
      ParameterKey:c.ParameterKey,
      ParameterValue:c.newValue||(c.group!=="sender"&&c.group!=="receiver"?c.defaultValue:""),
      DataType:c.DataType
    }));
    return modified.length?modified:undefined;
  };
  // Load deployed iFlows on target for undeploy selection
  const loadTargetRuntime=async(targetId)=>{
    if(!targetId){console.warn("loadTargetRuntime: no targetId");return;}
    setPkgTrUndeployLoading(true);setPkgTrUndeployList([]);setPkgTrUndeploySel([]);
    try{const flows=await api(`/api/${targetId}/iflows?all=true`);
      const list=Array.isArray(flows)?flows.map(f=>({Id:f.Id,Name:f.Name||f.Id,Version:f.Version||"",Status:f.Status||"STARTED",runtimeLocations:f.runtimeLocations||[{name:"Cloud Integration",locationId:null}]})):[];
      setPkgTrUndeployList(list);
    }catch(e){console.error("loadTargetRuntime failed for",targetId,e);setPkgTrUndeployList([]);}
    setPkgTrUndeployLoading(false);
  };
  // === Phased transport execution (Pipeline) ===
  const executePkgTransportPipeline=async()=>{
    // Validate: nur Parameter, die SAP in parameters.propdef mit isRequired=true definiert, dürfen nicht leer sein
    const configErrors=[];
    for(const[ck,cfgs] of Object.entries(pkgTrConfigs)){
      if(!cfgs||!cfgs.length)continue;
      for(const c of cfgs){
        if(!c.newValue&&c.required===true){
          const artName=ck.split("__")[0];
          configErrors.push(t("transport.pflichtfeldZeile",{artefakt:artName,feld:c.fieldLabel||c.ParameterKey,gruppe:c.group}));
        }
      }
    }
    if(configErrors.length>0){
      alert(t("transport.pflichtfelderAlert",{felder:configErrors.join("\n")}));
      return;
    }
    // Orphan deletion confirmation (Single + Werke-Mode)
    const orphansToDelete=pkgTrComparison?.orphans?.filter(o=>pkgTrOrphanSel[o.Id]!==false)||[];
    const werkeOrphansToDelete=Object.entries(pkgTrMultiChecks).flatMap(([wk,c])=>(c?.orphans||[]).filter(o=>pkgTrOrphanSel[`${o.Id}__${wk}`]!==false).map(o=>({...o,werkKey:wk})));
    const allOrphansToDelete=[...orphansToDelete,...werkeOrphansToDelete];
    if(allOrphansToDelete.length>0){
      const names=allOrphansToDelete.map(o=>o.Name||o.Id).join(", ");
      if(!window.confirm(t("transport.orphanBestaetigung",{count:allOrphansToDelete.length,namen:names})))return;
    }
    const delay=ms=>new Promise(r=>setTimeout(r,ms));
    const hasWerkeChecks=Object.keys(pkgTrMultiChecks).length>0;
    const hasUndeploy=pkgTrUndeploy&&pkgTrDeploy&&pkgTrUndeploySel.length>0;
    // Resolve target for undeploy
    let undeployTargetId=pkgTrTarget;
    if(!undeployTargetId&&pkgTrActiveTab)undeployTargetId=pkgTrActiveTab.split("__")[0];
    if(!undeployTargetId){const k=Object.keys(pkgTrMultiChecks)[0];if(k)undeployTargetId=k.split("__")[0];}

    // Pipeline Konzept: Ordered deploy only for single-mode when instance has hasPipelineConcept
    const _selectedForPipelineCheck = hasWerkeChecks ? [] : (pkgTrComparison?.comparison?.filter(a=>a.selected&&!a.draft)||[]);
    const isPipelineTransport = instance.hasPipelineConcept && !hasWerkeChecks &&
      _selectedForPipelineCheck.some(a => _classifyPipelineRole(a.Name||"") !== "normal");

    // Build phases
    const phases=[];
    const hasGlobalRefs=(pkgTrGlobalRefs?.references||[]).some(r=>r.selected);
    const hasPdReplication=instance.hasPipelineConcept&&pkgTrPdEntries.some(e=>e.selected);
    if(hasPdReplication)phases.push({id:"pd-replicate",label:t("phase.pd"),status:"waiting",detail:t("phase.pdDetail",{count:pkgTrPdEntries.filter(e=>e.selected).length})});
    if(hasUndeploy)phases.push({id:"undeploy",label:t("phase.undeploy"),status:"waiting",detail:t("phase.undeployDetail",{count:pkgTrUndeploySel.length})});
    if(hasGlobalRefs)phases.push({id:"globalrefs",label:t("phase.globalrefs"),status:"waiting",detail:t("phase.globalrefsDetail",{count:(pkgTrGlobalRefs?.references||[]).filter(r=>r.selected).length})});
    phases.push({id:"upload",label:t("phase.upload"),status:"waiting",detail:hasWerkeChecks?t("phase.uploadZiele",{count:Object.keys(pkgTrMultiChecks).length}):""});
    if(pkgTrDeploy){
      const rtNames=pkgTrDeployRuntimes.filter(r=>r.selected).map(r=>r.name).join(", ");
      const deployDetail=isPipelineTransport?t("phase.deployReihenfolge"):pkgTrDeployRuntimes.length>1&&rtNames?t("phase.deployAuf",{ziele:rtNames}):t("phase.deployStandard");
      phases.push({id:"deploy",label:isPipelineTransport?t("phase.deployGeordnet"):t("phase.deploy"),status:"waiting",detail:deployDetail});
    }
    // Orphan-Cleanup phase
    const selectedOrphanCount=hasWerkeChecks
      ?Object.entries(pkgTrOrphanSel).filter(([k,val])=>val&&k.includes("__")).length
      :Object.entries(pkgTrOrphanSel).filter(([k,val])=>val&&!k.includes("__")).length;
    if(selectedOrphanCount>0)phases.push({id:"cleanup",label:t("phase.cleanup"),status:"waiting",detail:t("phase.cleanupDetail",{count:selectedOrphanCount})});
    if(hasUndeploy)phases.push({id:"redeploy",label:t("phase.redeploy"),status:"waiting",detail:t("phase.redeployDetail",{count:pkgTrUndeploySel.length})});

    setPkgTrStep(3);setPkgTrPhases([...phases]);setPkgTrLoading(true);setPkgTrElapsed(0);setPkgTrResult(null);
    const startTime=Date.now();
    const timer=setInterval(()=>setPkgTrElapsed(Math.round((Date.now()-startTime)/1000)),1000);
    const updatePhase=(id,upd)=>setPkgTrPhases(prev=>prev.map(p=>p.id===id?{...p,...upd}:p));
    const elapsed=()=>`${Math.round((Date.now()-startTime)/1000)}s`;

    try{
      // Phase: Undeploy
      if(hasUndeploy&&undeployTargetId){
        updatePhase("undeploy",{status:"active",detail:t("phase.undeployAktiv")});
        for(const flowId of pkgTrUndeploySel){
          const flow=pkgTrUndeployList.find(f=>f.Id===flowId);
          const locs=flow?.runtimeLocations||[{name:"Cloud Integration",locationId:null}];
          for(const loc of locs){
            updatePhase("undeploy",{detail:t("phase.undeployFlow",{flow:flowId,ort:loc.name})});
            try{
              const locParam=loc.locationId?`?locationId=${encodeURIComponent(loc.locationId)}`:'';
              await api(`/api/${undeployTargetId}/undeploy/${encodeURIComponent(flowId)}${locParam}`,{method:"POST"});
            }catch(e){
              updatePhase("undeploy",{status:"error",detail:t("phase.undeployFehler",{flow:flowId,ort:loc.name,fehler:e.message})});
              throw new Error(t("phase.undeployFehlerKurz",{flow:flowId,ort:loc.name,fehler:e.message}));
            }
          }
        }
        updatePhase("undeploy",{detail:t("phase.wartePropagierung")});
        await delay(4000);
        for(const flowId of pkgTrUndeploySel){
          const flow=pkgTrUndeployList.find(f=>f.Id===flowId);
          const locs=flow?.runtimeLocations||[{name:"Cloud Integration",locationId:null}];
          for(const loc of locs){
            updatePhase("undeploy",{detail:t("phase.pruefe",{flow:flowId,ort:loc.name})});
            try{
              const locParam=loc.locationId?`?locationId=${encodeURIComponent(loc.locationId)}`:'';
              const st=await api(`/api/${undeployTargetId}/runtime-status/${encodeURIComponent(flowId)}${locParam}`);
              if(st.deployed){updatePhase("undeploy",{detail:t("phase.nochAktiv",{flow:flowId,ort:loc.name})});await delay(3000);
                const retry=await api(`/api/${undeployTargetId}/runtime-status/${encodeURIComponent(flowId)}${locParam}`);
                if(retry.deployed)throw new Error(`${flowId} still deployed on ${loc.name}`);
              }
            }catch(e){if(e.message.includes("still deployed")){updatePhase("undeploy",{status:"error",detail:e.message});throw e;}}
          }
        }
        updatePhase("undeploy",{status:"done",detail:t("phase.undeployFertig",{count:pkgTrUndeploySel.length}),duration:elapsed()});
      }

      // Phase: PD Replication (shown as active during transport - backend handles it)
      if(hasPdReplication)updatePhase("pd-replicate",{status:"active",detail:t("phase.pdAktiv")});

      // Phase: Transport (Upload)
      const uploadStart=Date.now();
      if(hasGlobalRefs)updatePhase("globalrefs",{status:"active",detail:t("phase.globalrefsAktiv")});
      else updatePhase("upload",{status:"active",detail:t("phase.uploadAktiv")});

      if(hasWerkeChecks){
        // Multi/Werke transport
        const result=await executePkgTransport();
        const uploadDur=`${Math.round((Date.now()-uploadStart)/1000)}s`;
        if(hasGlobalRefs)updatePhase("globalrefs",{status:"done",detail:t("phase.globalrefsFertig",{count:(pkgTrGlobalRefs?.references||[]).filter(r=>r.selected).length}),duration:uploadDur});
        if(result?.success){
          updatePhase("upload",{status:"done",detail:t("phase.uploadZieleFertig",{count:Object.keys(result.results||{}).length}),duration:uploadDur});
          if(pkgTrDeploy)updatePhase("deploy",{status:"done",detail:t("phase.deployImTransport"),duration:uploadDur});
        }else{
          const anyWorked=Object.values(result?.results||{}).some(r=>r.success||r.partialSuccess);
          if(anyWorked){
            const failedCount=Object.values(result?.results||{}).filter(r=>!r.success&&!r.partialSuccess).length;
            updatePhase("upload",{status:"warning",detail:t("phase.uploadTeilweise",{ok:Object.keys(result.results||{}).length-failedCount,fehler:failedCount}),duration:uploadDur});
            if(pkgTrDeploy)updatePhase("deploy",{status:"done",detail:t("phase.deployImTransport"),duration:uploadDur});
          }else{
            updatePhase("upload",{status:"error",detail:result?.error||t("gemeinsam:zustand.fehler"),duration:uploadDur});
          }
        }
        // Update cleanup phase for multi/werke results - always, regardless of transport success
        if(selectedOrphanCount>0){
          const allDel=Object.values(result?.results||{}).flatMap(r=>r?.deleteResults||[]);
          if(allDel.length>0){
            const delOk=allDel.filter(d=>d.status==="deleted").length;
            const delFail=allDel.filter(d=>d.status==="failed").length;
            updatePhase("cleanup",{status:delFail>0?"error":"done",detail:delFail?t("phase.cleanupFertigMitFehlern",{ok:delOk,fehler:delFail}):t("phase.cleanupFertig",{count:delOk}),duration:uploadDur});
          }else{
            updatePhase("cleanup",{status:"done",detail:t("phase.cleanupNichts"),duration:uploadDur});
          }
        }
        if(!result?.success&&!Object.values(result?.results||{}).some(r=>r.success||r.partialSuccess)){
          throw new Error(result?.error||t("transport.fehlgeschlagen"));
        }
        // Re-deploy after werke transport
        if(hasUndeploy&&undeployTargetId){
          const rdStart=Date.now();
          updatePhase("redeploy",{status:"active",detail:t("phase.redeployAktiv")});
          for(const flowId of pkgTrUndeploySel){
            const flow=pkgTrUndeployList.find(f=>f.Id===flowId);
            const locs=flow?.runtimeLocations||[{name:"Cloud Integration",locationId:null}];
            for(const loc of locs){
              updatePhase("redeploy",{detail:t("phase.redeployFlow",{flow:flowId,ort:loc.name})});
              try{
                const locParam=loc.locationId?`?locationId=${encodeURIComponent(loc.locationId)}`:'';
                await api(`/api/${undeployTargetId}/deploy/${encodeURIComponent(flowId)}${locParam}`,{method:"POST"});
              }catch(e){
                updatePhase("redeploy",{status:"error",detail:t("phase.redeployFehler",{flow:flowId,ort:loc.name,fehler:e.message})});
              }
            }
          }
          await delay(3000);
          updatePhase("redeploy",{status:"done",detail:t("phase.redeployFertig",{count:pkgTrUndeploySel.length}),duration:`${Math.round((Date.now()-rdStart)/1000)}s`});
        }
        setPkgTrResult(result);
      }else{
        // Single transport
        const comp=pkgTrComparison;
        if(!comp?.comparison){
          const result=await executePkgTransport();
          updatePhase("upload",{status:result?.success?"done":"error",detail:result?.success?t("transport.abgeschlossen"):result?.error||t("gemeinsam:zustand.fehler"),duration:elapsed()});
          setPkgTrResult(result);
        }else{
          const selected=comp.comparison.filter(a=>a.selected&&!a.draft);
          if(!selected.length){clearInterval(timer);setPkgTrLoading(false);return;}
          const arts=selected.map(art=>{
            const ck=getConfigCacheKey(art.Id);const cfgs=pkgTrConfigs[ck];
            const modified=buildConfigPayload(cfgs);
            return{...art,configurations:modified?.length?modified:undefined};
          });
          const selectedGlobalRefs=(pkgTrGlobalRefs?.references||[]).filter(r=>r.selected);
          // Collect selected PD entries for replication (Pipeline only)
          const selectedPds=instance.hasPipelineConcept?pkgTrPdEntries.filter(e=>e.selected).map(e=>({pid:e.Pid,mode:"merge"})):[];
          // Access Policy Pre Flight (defensiv, blockiert nur bei explizitem User Abbruch)
          const _gate1=await runComplianceGate(pkgTrTarget,{Id:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,Name:pkgTrCustomId?pkgTrTargetName:pkgTransport.Name},arts);
          if(!_gate1.ok){clearInterval(timer);setPkgTrLoading(false);return;}
          const r=await api("/api/transport/package-execute",{method:"POST",timeout:600000,body:JSON.stringify({
            approvalNote:pkgTrApprovalNote.trim(),sourceInstanceId:instance.id,targetInstanceId:pkgTrTarget,sourcePackageId:pkgTransport.Id,
            targetPackageId:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,
            targetPackageName:pkgTrCustomId?pkgTrTargetName:pkgTransport.Name,
            selectedArtifacts:arts,globalReferences:selectedGlobalRefs.length?selectedGlobalRefs:undefined,
            ...(selectedPds.length>0?{replicatePD:selectedPds}:{}),
            deleteOrphans:pkgTrComparison?.orphans?.filter(o=>pkgTrOrphanSel[o.Id]!==false)||undefined,
            // Pipeline-Transport: deploy separately in ordered phases; normal: backend handles deploy
            deployAfter:pkgTrDeploy&&!isPipelineTransport,createPackage:!comp.targetExists,
            deployRuntimes:pkgTrDeployRuntimes.filter(rt=>rt.selected).map(rt=>({name:rt.name,locationId:rt.locationId}))
          })});
          // Schleuse: als Transportauftrag eingereiht - Phasen-Flow beenden
          if(r?.queued&&r?.approvalRequired){clearInterval(timer);const q={success:true,queued:true,requestId:r.requestId};setPkgTrResult(q);setPkgTrLoading(false);return q;}
          setPkgTrProgress(r.results||[]);
          const uploadDur=`${Math.round((Date.now()-uploadStart)/1000)}s`;
          // Update PD phase
          if(hasPdReplication){
            const pdOk=r.pdResults?.filter(p=>!p.error).length||0;
            const pdErr=r.pdResults?.filter(p=>p.error).length||0;
            updatePhase("pd-replicate",{status:pdErr>0?"error":"done",detail:pdErr>0?t("phase.pdTeilweise",{ok:pdOk,fehler:pdErr}):t("phase.pdFertig",{count:pdOk}),duration:uploadDur});
          }
          // Update globalrefs phase if present
          if(hasGlobalRefs){
            const gFailed=r.globalResults?.filter(g=>g.status==="failed").length||0;
            if(gFailed>0){
              updatePhase("globalrefs",{status:"error",detail:t("phase.globalrefsFehler",{count:gFailed}),duration:uploadDur});
              throw new Error(r.error||t("phase.globalrefsFehlerKurz",{count:gFailed}));
            }
            const gCount=r.globalResults?.length||selectedGlobalRefs.length;
            updatePhase("globalrefs",{status:"done",detail:r.globalResults?.some(g=>g.deployed)?t("phase.globalrefsFertigDeployed",{count:gCount}):t("phase.globalrefsFertig",{count:gCount}),duration:uploadDur});
            updatePhase("upload",{status:"active",detail:t("phase.uploadPaketArtefakte")});
          }
          if(r.success||r.partialSuccess){
            updatePhase("upload",{status:r.partialSuccess?"warning":"done",detail:t("phase.uploadFertig",{erstellt:r.summary?.created||0,aktualisiert:r.summary?.updated||0}),duration:uploadDur});
          }else{
            updatePhase("upload",{status:"error",detail:r.error||t("gemeinsam:zustand.fehler"),duration:uploadDur});
          }
          // Orphan cleanup phase update - always, regardless of transport success
          if(r.deleteResults?.length>0){
            const delOk=r.deleteResults.filter(d=>d.status==="deleted").length;
            const delFail=r.deleteResults.filter(d=>d.status==="failed").length;
            updatePhase("cleanup",{status:delFail>0?"error":"done",detail:delFail?t("phase.cleanupFertigMitFehlern",{ok:delOk,fehler:delFail}):t("phase.cleanupFertig",{count:delOk}),duration:uploadDur});
          }else if(selectedOrphanCount>0){
            updatePhase("cleanup",{status:"done",detail:t("phase.cleanupNichts"),duration:uploadDur});
          }
          if(!r.success&&!r.partialSuccess){throw new Error(r.error||t("gemeinsam:zustand.fehler"));}
          if(pkgTrDeploy){
            if(isPipelineTransport){
              // Geordneter Pipeline-Deploy: Consumer vor Producer (RAI → Mapping → Generic Steps → SAI)
              updatePhase("deploy",{status:"active",detail:t("phase.deployBerechnen")});
              const orderedArts=[...selected].sort((a,b)=>
                (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(a.Name||"")]??5) -
                (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(b.Name||"")]??5)
              );
              let deployErrors=0;
              const selectedRts=pkgTrDeployRuntimes.filter(r2=>r2.selected);
              const rtsToUse=selectedRts.length>0?selectedRts:[{name:"Cloud Integration",locationId:null}];
              for(const art of orderedArts){
                const role=_classifyPipelineRole(art.Name||"");
                updatePhase("deploy",{detail:t("phase.deployArtefakt",{rolle:role,name:art.Name||art.Id})});
                for(const rt of rtsToUse){
                  try{
                    const locParam=rt.locationId?`?locationId=${encodeURIComponent(rt.locationId)}`:'';
                    await api(`/api/${pkgTrTarget}/deploy/${encodeURIComponent(art.Id)}${locParam}`,{method:"POST"});
                    await delay(2500);
                  }catch(de){
                    deployErrors++;
                    console.warn(`[Pipeline-Deploy] ${art.Id} → ${rt.name}: ${de.message}`);
                  }
                }
              }
              const depDur=`${Math.round((Date.now()-uploadStart)/1000)}s`;
              if(deployErrors>0)updatePhase("deploy",{status:"error",detail:t("phase.deployFehlerPipeline",{count:deployErrors}),duration:depDur});
              else updatePhase("deploy",{status:"done",detail:t("phase.deployFertigPipeline",{count:orderedArts.length}),duration:depDur});
            }else{
              updatePhase("deploy",{status:"active",detail:t("phase.deployAktiv")});await delay(2000);
              const failedDeploy=r.results?.filter(x=>x.deployError).length||0;
              const depDur=`${Math.round((Date.now()-uploadStart)/1000)}s`;
              if(failedDeploy>0)updatePhase("deploy",{status:"error",detail:t("phase.deployFehler",{count:failedDeploy}),duration:depDur});
              else{
                const rtNames=pkgTrDeployRuntimes.filter(r2=>r2.selected).map(r2=>r2.name).join(", ");
                const depCount=r.summary?.deployed||selected.length;
                const doneDetail=pkgTrDeployRuntimes.length>1&&rtNames?t("phase.deployFertigAuf",{count:depCount,ziele:rtNames}):t("phase.deployFertig",{count:depCount});
                updatePhase("deploy",{status:"done",detail:doneDetail,duration:depDur});
              }
            }
          }
          // Re-deploy
          if(hasUndeploy&&undeployTargetId){
            const rdStart=Date.now();
            updatePhase("redeploy",{status:"active",detail:t("phase.redeployAktiv")});
            for(const flowId of pkgTrUndeploySel){
              const flow=pkgTrUndeployList.find(f=>f.Id===flowId);
              const locs=flow?.runtimeLocations||[{name:"Cloud Integration",locationId:null}];
              for(const loc of locs){
                updatePhase("redeploy",{detail:t("phase.redeployFlow",{flow:flowId,ort:loc.name})});
                try{
                  const locParam=loc.locationId?`?locationId=${encodeURIComponent(loc.locationId)}`:'';
                  await api(`/api/${undeployTargetId}/deploy/${encodeURIComponent(flowId)}${locParam}`,{method:"POST"});
                }catch(e){
                  updatePhase("redeploy",{status:"error",detail:t("phase.redeployFehler",{flow:flowId,ort:loc.name,fehler:e.message})});
                }
              }
            }
            await delay(3000);
            updatePhase("redeploy",{status:"done",detail:t("phase.redeployFertig",{count:pkgTrUndeploySel.length}),duration:`${Math.round((Date.now()-rdStart)/1000)}s`});
          }
          setPkgTrResult(r);
        }
      }
    }catch(e){
      if(!pkgTrResult)setPkgTrResult({success:false,error:e.message});
    }
    // Partner Directory Replikation (NACH dem Haupt-Transport, separat)
    if(pkgTrPdEnabled) {
      const selectedPids = pkgTrPdEntries.filter(e=>e.selected).map(e=>e.Pid);
      if(selectedPids.length>0) {
        // Alle unique Ziel Instanz-IDs sammeln
        const targetInstIds = [...new Set((pkgTrTargets||[]).map(t=>typeof t==='string'?t:(t.instanceId||t.id)).filter(Boolean))];
        // Bei Werke-Transport: aus pkgTrMultiChecks-Keys extrahieren
        if(targetInstIds.length===0&&Object.keys(pkgTrMultiChecks||{}).length>0)
          [...new Set(Object.keys(pkgTrMultiChecks).map(k=>k.split("__")[0]))].forEach(id=>targetInstIds.push(id));
        // Falls single-transport: pkgTrTarget verwenden
        if(targetInstIds.length===0&&pkgTrTarget) targetInstIds.push(pkgTrTarget);
        let pdOk=0,pdErr=0;
        for(const tgtId of targetInstIds) {
          try {
            await api("/api/transport/replicate-partner-directory", {
              method:"POST",
              body:JSON.stringify({sourceInstanceId:instance.id, targetInstanceId:tgtId, pids:selectedPids, mode:"merge"})
            });
            pdOk++;
          } catch(e) {
            pdErr++;
            console.warn("PD-Replikation fehlgeschlagen:", e.message);
          }
        }
        if(hasPdReplication)updatePhase("pd-replicate",{status:pdErr>0?"error":"done",detail:pdErr>0?t("phase.pdTeilweise",{ok:pdOk,fehler:pdErr}):t("phase.pdZieleFertig",{count:targetInstIds.length}),duration:elapsed()});
      } else if(hasPdReplication) {
        updatePhase("pd-replicate",{status:"done",detail:t("phase.pdKeine"),duration:elapsed()});
      }
    } else if(hasPdReplication) {
      updatePhase("pd-replicate",{status:"done",detail:t("phase.uebersprungen"),duration:elapsed()});
    }
    clearInterval(timer);setPkgTrTimer(null);setPkgTrLoading(false);
  };
  // Load global references (Script Collections, Message Mappings from other packages)
  const loadGlobalRefs=async(targetInstId,artifactIds)=>{
    if(!artifactIds||!artifactIds.length){setPkgTrGlobalRefs(null);return;}
    setPkgTrGlobalLoading(true);
    try{
      const r=await api("/api/transport/package-references",{method:"POST",body:JSON.stringify({
        sourceInstanceId:instance.id,targetInstanceId:targetInstId,
        artifactIds,sourcePackageId:pkgTransport.Id
      })});
      setPkgTrGlobalRefs(r);
      // Store params for auto-refresh
      globalRefsParamsRef.current={targetInstId,artifactIds};
    }catch(e){console.error("[GlobalRefs]",e);setPkgTrGlobalRefs(null);}
    setPkgTrGlobalLoading(false);
  };
  // Manual refresh for global refs (replaces old 15s auto-poll)
  const[pkgTrRefsLastRefresh,setPkgTrRefsLastRefresh]=useState(null);
  const refreshGlobalRefs=async()=>{
    if(!globalRefsParamsRef.current)return;
    const{targetInstId,artifactIds}=globalRefsParamsRef.current;
    await loadGlobalRefs(targetInstId,artifactIds);
    setPkgTrRefsLastRefresh(new Date());
  };
  const pkgTrToggleGlobalRef=(refId)=>{
    setPkgTrGlobalRefs(prev=>{if(!prev?.references)return prev;
      return{...prev,references:prev.references.map(r=>r.Id===refId&&r.status!=="identical"?{...r,selected:!r.selected}:r)};});
  };
  const debugGlobalRefs=async()=>{
    // Collect source artifact IDs from either single or Werke mode
    let sourceArtIds=[];
    const hasWerke=Object.keys(pkgTrMultiChecks).length>0;
    if(hasWerke){
      const firstCheck=Object.values(pkgTrMultiChecks)[0];
      if(firstCheck?.comparison){
        sourceArtIds=[...new Set(firstCheck.comparison.filter(a=>a.selected&&!a.draft).map(a=>{
          const origId=a.Id;const k=firstCheck.kennung||"";
          return k&&origId.endsWith(k)?origId.slice(0,-k.length):origId;
        }))];
      }
    }else if(pkgTrComparison?.comparison){
      sourceArtIds=pkgTrComparison.comparison.filter(a=>a.selected&&!a.draft).map(a=>a.Id);
    }
    if(!sourceArtIds.length){setPkgTrDebugRefs({error:t("transport.keineArtefakte")});return;}
    setPkgTrDebugLoading(true);setPkgTrDebugRefs(null);
    const results=[];
    for(const artId of sourceArtIds){
      try{
        const r=await api(`/api/${instance.id}/transport/debug-refs/${encodeURIComponent(artId)}`);
        results.push({artId,files:r.files,manifest:r.manifest,bpmnRefs:r.bpmnRefs,referenceFiles:r.referenceFiles,requireCapability:r.requireCapability,requireBundle:r.requireBundle,error:null});
      }catch(e){results.push({artId,error:e.message,files:[],manifest:null,bpmnRefs:[],referenceFiles:[],requireCapability:[],requireBundle:[]});}
    }
    setPkgTrDebugRefs({results});
    setPkgTrDebugLoading(false);
  };

  // Partner Directory Einträge für Transport laden
  const loadPdEntriesForTransport = async() => {
    if(!instance?.id) return;
    setPkgTrPdLoading(true);
    try {
      const data = await api(`/api/${instance.id}/partner-directory`);
      setPkgTrPdEntries((data.partners||[]).map(p=>({
        Pid: p.Pid,
        stringCount: p.stringParams.length,
        binaryCount: p.binaryParams.length,
        altCount: p.altPartners.length,
        selected: false,
      })));
    } catch(e) {
      setPkgTrPdEntries([]);
    }
    setPkgTrPdLoading(false);
  };

  // === Render: Global References Section (used in both single and Werke mode) ===
  const renderGlobalRefsSection=()=>{
    if(!pkgTrGlobalLoading&&pkgTrGlobalRefs===null&&!pkgTrComparison?.comparison?.some(a=>a.selected)&&!Object.values(pkgTrMultiChecks).some(c=>c?.comparison?.some(a=>a.selected)))return null;
    const refs=pkgTrGlobalRefs?.references||[];
    const hasDrafts=refs.some(r=>r.draft);
    return <>
      <div style={{height:1,background:"var(--border)",margin:"14px 0"}}/>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
        <span style={{fontSize:13,fontWeight:600,color:"var(--text-1)"}}>{t("refs.titel")}</span>
        {refs.length>0&&<span style={{fontSize:9,padding:"2px 7px",borderRadius:4,fontWeight:600,background:v("status-ok-bg"),color:v("success")}}>{t("refs.erkannt",{count:refs.length})}</span>}
        {pkgTrHasDraftDeps&&<span style={{fontSize:9,padding:"2px 7px",borderRadius:4,fontWeight:600,background:v("status-fail-bg"),color:v("danger")}}>{t("refs.draftAbhaengigkeit")}</span>}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
          {pkgTrRefsLastRefresh&&<span style={{fontSize:10,color:"var(--text-3)"}}>{t("refs.aktualisiertVor",{count:Math.round((Date.now()-pkgTrRefsLastRefresh.getTime())/1000)})}</span>}
          <MuiTooltip title={t("refs.neuLaden")}><span>
            <MuiIconButton size="small" onClick={refreshGlobalRefs} disabled={pkgTrGlobalLoading}
              sx={{width:26,height:26,color:"var(--text-2)","&:hover":{color:"var(--accent)"},
                ...(pkgTrGlobalLoading?{animation:"spin 1s linear infinite","@keyframes spin":{"0%":{transform:"rotate(0deg)"},"100%":{transform:"rotate(360deg)"}}}:{})
              }}>
              <RefreshIcon sx={{fontSize:16}}/>
            </MuiIconButton>
          </span></MuiTooltip>
        </div>
      </div>

      {pkgTrGlobalLoading&&<div style={{padding:16,textAlign:"center",color:"var(--text-3)",fontSize:12}}>
        <span style={{display:"inline-block",animation:"pulse 1.5s infinite"}}>{t("refs.analysiere")}</span>
      </div>}

      {!pkgTrGlobalLoading&&pkgTrGlobalRefs&&refs.length===0&&<div style={{padding:"10px 14px",borderRadius:6,fontSize:11,color:"var(--text-3)",background:"var(--surface-2)"}}>
        {t("refs.keine")}
      </div>}

      {/* Draft Warning Banner */}
      {!pkgTrGlobalLoading&&hasDrafts&&<div style={{padding:"10px 14px",borderRadius:8,marginBottom:8,display:"flex",alignItems:"center",gap:8,
        background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,color:v("danger"),fontSize:12}}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <div>
          <div style={{fontWeight:600}}>{t("refs.draftBlockiert",{count:refs.filter(r=>r.draft).length})}</div>
          <div style={{fontSize:11,marginTop:2,opacity:.8}}>{t("refs.draftHinweis")}</div>
        </div>
      </div>}

      {/* Refs Table */}
      {!pkgTrGlobalLoading&&refs.length>0&&<div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"28px 1fr 110px 90px 60px 60px",padding:"8px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
          <div></div><div>{t("refs.spalteArtifact")}</div><div>{t("refs.spalteQuellPaket")}</div><div>{t("refs.spalteTyp")}</div><div>{t("refs.spalteQuelle")}</div><div>{t("refs.spalteZiel")}</div>
        </div>
        {refs.map(ref=>{
          const isExp=pkgTrGlobalExpanded[ref.Id];
          return <Fragment key={ref.Id}>
            <div style={{display:"grid",gridTemplateColumns:"28px 1fr 110px 90px 60px 60px",padding:"8px 12px",
              borderTop:"1px solid var(--border)",alignItems:"center",cursor:"pointer",
              background:ref.draft?v("status-fail-bg"):ref.selected?v("status-total-bg"):"transparent",
              opacity:ref.status==="identical"?0.5:1}}
              onClick={()=>setPkgTrGlobalExpanded(p=>({...p,[ref.Id]:!p[ref.Id]}))}>
              <div style={{display:"flex",alignItems:"center"}}>
                {ref.draft
                  ?<WarningAmberIcon sx={{fontSize:14,color:v("danger")}}/>
                  :<input type="checkbox" checked={ref.selected} onChange={(e)=>{e.stopPropagation();pkgTrToggleGlobalRef(ref.Id);}}
                    disabled={ref.status==="identical"} style={{width:14,height:14,accentColor:"var(--accent)",cursor:ref.status==="identical"?"not-allowed":"pointer"}}
                    onClick={e=>e.stopPropagation()}/>}
              </div>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{transition:"transform .15s",transform:isExp?"rotate(90deg)":"rotate(0deg)",flexShrink:0,color:"var(--text-3)"}}><polyline points="9 18 15 12 9 6"/></svg>
                  <span style={{fontSize:13,color:ref.draft?v("danger"):(ref.status==="identical"?"var(--text-3)":"var(--text-1)"),fontWeight:500}}>{ref.Name}</span>
                  {ref.draft&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:v("status-fail-bg"),color:v("danger"),fontWeight:600}}>{t("refs.draft")}</span>}
                </div>
                <div style={{fontSize:10,color:"var(--text-3)",fontFamily:MONO,marginLeft:16}}>{ref.Id}</div>
              </div>
              <div><span style={{fontSize:9,padding:"2px 7px",borderRadius:4,fontWeight:500,background:v("status-ok-bg"),color:v("success")}}>{ref.packageName}</span></div>
              <div style={{fontSize:11,color:"var(--text-2)"}}>{ref.Type==="Script Collection"?t("refs.scriptColKurz"):ref.Type}</div>
              <div style={{fontSize:11,fontFamily:MONO,color:ref.draft?v("danger"):"var(--text-1)"}}>{ref.draft?t("refs.draft"):ref.Version?`v${ref.Version}`:"-"}</div>
              <div style={{fontSize:11,fontFamily:MONO}}>
                {ref.status==="draft"&&<span style={{color:v("danger")}}>-</span>}
                {ref.status==="new"&&<span style={{color:v("success")}}>{t("refs.neu")}</span>}
                {ref.status==="update"&&<span style={{color:v("accent")}}>{ref.targetVersion?`v${ref.targetVersion}`:"-"}</span>}
                {ref.status==="identical"&&<span style={{color:"var(--text-3)"}}>{ref.targetVersion?`v${ref.targetVersion}`:t("refs.ok")}</span>}
              </div>
            </div>
            {/* Expanded: show scripts inside */}
            {isExp&&<div style={{gridColumn:"1/-1",borderTop:"1px solid var(--border-subtle)",background:"var(--surface-1)",padding:"8px 12px 8px 42px"}}>
              {ref.scripts?.length>0?ref.scripts.map((s,j)=>(
                <div key={j} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:11}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{color:"var(--text-3)",flexShrink:0}}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                  <span style={{color:"var(--text-1)",fontWeight:500}}>{s.name}</span>
                  <span style={{color:"var(--text-3)",fontSize:10}}>{s.type}</span>
                  <span style={{color:"var(--text-3)",fontSize:10,fontFamily:MONO}}>{s.size>1024?`${(s.size/1024).toFixed(1)} KB`:`${s.size} B`}</span>
                </div>
              )):<div style={{fontSize:11,color:"var(--text-3)",fontStyle:"italic"}}>
                {ref.draft?t("refs.scriptsDraft"):t("refs.keineScripts")}
              </div>}
            </div>}
          </Fragment>;
        })}
      </div>}

      {/* Info box for selected refs */}
      {!pkgTrGlobalLoading&&!hasDrafts&&refs.some(r=>r.selected)&&<div style={{
        padding:"8px 12px",borderRadius:6,fontSize:11,marginTop:8,display:"flex",alignItems:"center",gap:6,
        background:"var(--status-total-bg)",border:`1px solid var(--status-total-border)`,color:v("accent")}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
        {t("refs.vorabHinweis",{namen:refs.filter(r=>r.selected).map(r=>`${r.Name} (v${r.Version})`).join(", ")})}
      </div>}

      {/* Debug buttons */}
      <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:6}}>
        <button onClick={debugGlobalRefs} disabled={pkgTrDebugLoading} style={{padding:"4px 10px",borderRadius:5,fontSize:10,cursor:"pointer",fontFamily:FONT,border:"1px solid var(--border)",background:"var(--surface-0)",color:"var(--text-3)"}}>
          {pkgTrDebugLoading?t("debug.zipLaeuft"):t("debug.zipPruefen")}
        </button>
        {refs.length>0&&<button onClick={async()=>{
          setPkgTrDebugLoading(true);
          const refResults=[];
          for(const ref of refs){
            try{
              const r=await api(`/api/${instance.id}/transport/debug-global-ref/${encodeURIComponent(ref.Id)}`);
              refResults.push({refId:ref.Id,strategies:r.strategies});
            }catch(e){refResults.push({refId:ref.Id,error:e.message});}
          }
          setPkgTrDebugRefs(prev=>({...prev,globalRefDebug:refResults}));
          setPkgTrDebugLoading(false);
        }} disabled={pkgTrDebugLoading} style={{padding:"4px 10px",borderRadius:5,fontSize:10,cursor:"pointer",fontFamily:FONT,border:"1px solid var(--border)",background:"var(--surface-0)",color:"var(--text-3)"}}>
          {t("debug.refAufloesung")}
        </button>}
        <button onClick={()=>{
          // Build complete transport verification report from current state
          const hasWerke=Object.keys(pkgTrMultiChecks).length>0;
          const report={timestamp:new Date().toISOString(),werke:[],singleMode:null,globalRefs:[],configKeys:[]};
          if(hasWerke){
            for(const[werkKey,check] of Object.entries(pkgTrMultiChecks)){
              const parts=werkKey.split("__");const tId=parts[0];const kennung=parts[1]||null;
              const tgtInst=allInstances.find(i=>i.id===tId);
              const basePkgId=pkgTrCustomId?pkgTrTargetId:pkgTransport?.Id;
              const werkReport={werkKey,kennung,targetInstanceId:tId,targetInstanceName:tgtInst?.name||tId,
                targetPackageId:kennung?`${basePkgId}${kennung}`:basePkgId,
                artifacts:(check.comparison||[]).map(a=>{
                  const actualTargetId=a.suffixedId||(kennung?`${a.Id}${kennung}`:a.Id);
                  const cfgKey=getConfigCacheKey(a.Id,werkKey);
                  const cfgCount=pkgTrConfigs[cfgKey]?.length||0;
                  const cfgFromTarget=pkgTrConfigs[cfgKey]?.filter(c=>c.fromTarget).length||0;
                  report.configKeys.push({artId:a.Id,werkKey,cfgKey,cfgCount,cfgFromTarget,targetId:actualTargetId});
                  return{sourceId:a.Id,targetId:actualTargetId,suffixedId:a.suffixedId,name:a.Name,version:a.Version,targetVersion:a.targetVersion,status:a.status,selected:a.selected,draft:a.draft,cfgKey,cfgCount,cfgFromTarget};
                })};
              report.werke.push(werkReport);
            }
          }else if(pkgTrComparison?.comparison){
            report.singleMode={targetInstanceId:pkgTrTarget,targetPackageId:pkgTrCustomId?pkgTrTargetId:pkgTransport?.Id,
              artifacts:pkgTrComparison.comparison.map(a=>{
                const cfgKey=getConfigCacheKey(a.Id);
                return{sourceId:a.Id,targetId:a.Id,version:a.Version,targetVersion:a.targetVersion,status:a.status,selected:a.selected,cfgKey,cfgCount:pkgTrConfigs[cfgKey]?.length||0};
              })};
          }
          for(const ref of refs){report.globalRefs.push({id:ref.Id,name:ref.Name,version:ref.Version,packageId:ref.packageId,packageName:ref.packageName,status:ref.status,selected:ref.selected,draft:ref.draft});}
          setPkgTrDebugRefs(prev=>({...prev,verification:report}));
        }} style={{padding:"4px 10px",borderRadius:5,fontSize:10,cursor:"pointer",fontFamily:FONT,border:"1px solid var(--border)",background:"var(--surface-0)",color:"var(--text-3)"}}>
          {t("debug.verifizierung")}
        </button>
      </div>

      {/* Debug output */}
      {pkgTrDebugRefs&&<div style={{marginTop:8,border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",fontSize:11}}>
        <div style={{padding:"6px 10px",background:"var(--surface-2)",fontWeight:600,color:"var(--text-2)",fontSize:10}}>{t("debug.kopf")}</div>

        {/* Transport Verification Report */}
        {pkgTrDebugRefs.verification&&<div style={{borderTop:"2px solid var(--accent)",padding:10}}>
          <div style={{fontWeight:600,color:"var(--accent)",marginBottom:8}}>{t("debug.verifizierung")} <span style={{fontSize:9,color:"var(--text-3)",fontWeight:400}}>{pkgTrDebugRefs.verification.timestamp}</span></div>

          {/* Werke mapping */}
          {pkgTrDebugRefs.verification.werke?.map((w,i)=>(
            <div key={i} style={{marginBottom:10,padding:8,background:"var(--surface-2)",borderRadius:6}}>
              <div style={{fontWeight:600,color:"var(--text-1)",marginBottom:4}}>
                {t("debug.werk",{name:w.kennung||t("transport.werkStandard")})} → <span style={{fontFamily:MONO,color:"var(--accent)"}}>{w.targetInstanceName}</span>
              </div>
              <div style={{fontSize:10,fontFamily:MONO,color:"var(--text-3)",marginBottom:6}}>
                {t("debug.zielPaket",{id:w.targetPackageId})}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 70px 50px 60px",gap:2,fontSize:9,fontFamily:MONO}}>
                <div style={{fontWeight:600,color:"var(--text-2)",padding:2}}>{t("debug.spalteQuelleId")}</div>
                <div style={{fontWeight:600,color:"var(--text-2)",padding:2}}>{t("debug.spalteZielId")}</div>
                <div style={{fontWeight:600,color:"var(--text-2)",padding:2}}>{t("debug.spalteStatus")}</div>
                <div style={{fontWeight:600,color:"var(--text-2)",padding:2}}>{t("debug.spalteCfg")}</div>
                <div style={{fontWeight:600,color:"var(--text-2)",padding:2}}>{t("debug.spalteZielCfg")}</div>
                {w.artifacts?.map((a,j)=>(
                  <Fragment key={j}>
                    <div style={{padding:2,color:a.selected?"var(--text-1)":"var(--text-3)",background:a.selected?"rgba(99,102,241,.05)":"transparent"}}>{a.sourceId}</div>
                    <div style={{padding:2,color:a.selected?"var(--accent)":"var(--text-3)",background:a.selected?"rgba(99,102,241,.05)":"transparent"}}>{a.targetId}</div>
                    <div style={{padding:2,color:a.status==="new"?"var(--success)":a.status==="update"?v("accent"):"var(--text-3)"}}>{a.status}{a.draft?" (DRAFT)":""}</div>
                    <div style={{padding:2}}>{a.cfgCount||"-"}</div>
                    <div style={{padding:2,color:a.cfgFromTarget?"var(--success)":"var(--text-3)"}}>{a.cfgFromTarget||"-"}</div>
                  </Fragment>
                ))}
              </div>
            </div>
          ))}

          {/* Single mode */}
          {pkgTrDebugRefs.verification.singleMode&&<div style={{marginBottom:10,padding:8,background:"var(--surface-2)",borderRadius:6}}>
            <div style={{fontWeight:600,color:"var(--text-1)",marginBottom:4}}>{t("debug.singleMode")}</div>
            <div style={{fontSize:10,fontFamily:MONO,color:"var(--text-3)",marginBottom:4}}>{t("debug.zielPaket",{id:pkgTrDebugRefs.verification.singleMode.targetPackageId})}</div>
            {pkgTrDebugRefs.verification.singleMode.artifacts?.map((a,j)=>(
              <div key={j} style={{fontSize:9,fontFamily:MONO,padding:"2px 0",color:a.selected?"var(--text-1)":"var(--text-3)"}}>
                {a.sourceId} → {a.targetId} [{a.status}] cfg:{a.cfgCount}
              </div>
            ))}
          </div>}

          {/* Global refs */}
          {pkgTrDebugRefs.verification.globalRefs?.length>0&&<div style={{marginBottom:6,padding:8,background:"var(--surface-2)",borderRadius:6}}>
            <div style={{fontWeight:600,color:"var(--text-1)",marginBottom:4}}>{t("debug.globaleReferenzen")}</div>
            {pkgTrDebugRefs.verification.globalRefs.map((g,j)=>(
              <div key={j} style={{fontSize:9,fontFamily:MONO,padding:"2px 0",color:g.selected?"var(--text-1)":"var(--text-3)"}}>
                {g.id} v{g.version} → Package: {g.packageId} ({g.packageName}) [{g.status}]{g.draft?" [DRAFT]":""}
              </div>
            ))}
          </div>}

          {/* Config cache keys */}
          {pkgTrDebugRefs.verification.configKeys?.length>0&&<details>
            <summary style={{cursor:"pointer",fontSize:10,color:"var(--text-3)"}}>{t("debug.configKeys",{count:pkgTrDebugRefs.verification.configKeys.length})}</summary>
            <div style={{fontSize:9,fontFamily:MONO,color:"var(--text-3)",padding:4}}>
              {pkgTrDebugRefs.verification.configKeys.map((ck,j)=>(
                <div key={j} style={{padding:"1px 0",color:ck.cfgFromTarget?"var(--success)":"inherit"}}>
                  {t("debug.configKeyZeile",{schluessel:ck.cfgKey,anzahl:ck.cfgCount,vomZiel:ck.cfgFromTarget})}
                </div>
              ))}
            </div>
          </details>}
        </div>}

        {/* iFlow ZIP analysis */}
        {pkgTrDebugRefs.results?.map((r,i)=>(
          <div key={i} style={{padding:10,borderTop:"1px solid var(--border)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
              <span style={{fontWeight:600,color:"var(--text-1)"}}>{r.artId}</span>
              <button onClick={async()=>{
                try{const resp=await fetch(`/api/${instance.id}/transport/download-zip/${encodeURIComponent(r.artId)}`,{headers:{Authorization:`Bearer ${localStorage.getItem("token")}`}});
                  if(!resp.ok)throw new Error(resp.statusText);const blob=await resp.blob();const url=URL.createObjectURL(blob);
                  const a=document.createElement("a");a.href=url;a.download=`${r.artId}.zip`;a.click();URL.revokeObjectURL(url);
                }catch(e){alert(t("debug.downloadFehler",{fehler:e.message}));}
              }} style={{fontSize:9,color:"var(--accent)",background:"none",border:"none",textDecoration:"underline",cursor:"pointer",padding:0,fontFamily:FONT}}>{t("debug.iflowZip")}</button>
            </div>
            {r.manifest&&<details style={{marginBottom:4}}><summary style={{cursor:"pointer",color:"var(--accent)",fontSize:10}}>{t("debug.manifest")}</summary><pre style={{padding:6,background:"var(--surface-2)",borderRadius:4,fontSize:9,overflow:"auto",maxHeight:150,whiteSpace:"pre-wrap",color:"var(--text-2)",fontFamily:MONO,marginTop:2}}>{r.manifest}</pre></details>}
            {r.requireCapability?.length>0&&<div style={{padding:"4px 8px",borderRadius:4,background:"rgba(34,197,94,.06)",marginBottom:4}}>
              <span style={{fontWeight:500,color:v("success"),fontSize:10}}>{t("debug.requireCapability",{count:r.requireCapability.length})}</span>
              {r.requireCapability.map((c,j)=>(<div key={j} style={{fontSize:9,fontFamily:MONO,color:"var(--accent)",marginLeft:8}}>{c.refId} ({c.isScriptCollection?"SC":c.isMessageMapping?"MM":"?"})</div>))}
            </div>}
            <details><summary style={{cursor:"pointer",fontSize:10,color:"var(--text-3)"}}>{t("debug.dateien",{count:r.files?.length||0})}</summary><div style={{fontSize:9,fontFamily:MONO,color:"var(--text-3)",maxHeight:100,overflow:"auto"}}>{r.files?.map((f,j)=><div key={j}>{f.name} ({f.size}b)</div>)}</div></details>
          </div>))}

        {/* Global ref resolution debug */}
        {pkgTrDebugRefs.globalRefDebug?.map((gr,i)=>(
          <div key={`gr-${i}`} style={{padding:10,borderTop:"2px solid var(--border)",background:"var(--surface-1)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{fontWeight:600,color:"var(--text-1)"}}>{t("debug.refAufloesungVon",{id:gr.refId})}</span>
              <button onClick={async()=>{
                try{const resp=await fetch(`/api/${instance.id}/transport/download-zip/${encodeURIComponent(gr.refId)}`,{headers:{Authorization:`Bearer ${localStorage.getItem("token")}`}});
                  if(!resp.ok)throw new Error(resp.statusText);const blob=await resp.blob();const url=URL.createObjectURL(blob);
                  const a=document.createElement("a");a.href=url;a.download=`${gr.refId}.zip`;a.click();URL.revokeObjectURL(url);
                }catch(e){alert(t("debug.downloadFehler",{fehler:e.message}));}
              }} style={{fontSize:9,color:"var(--accent)",background:"none",border:"none",textDecoration:"underline",cursor:"pointer",padding:0,fontFamily:FONT}}>{t("debug.zip")}</button>
            </div>
            {gr.error&&<div style={{color:v("danger"),fontSize:11}}>{t("debug.fehler",{fehler:gr.error})}</div>}
            {gr.strategies?.map((s,j)=>(
              <div key={j} style={{padding:"4px 8px",marginBottom:4,borderRadius:4,
                background:s.success?"rgba(34,197,94,.06)":"rgba(220,38,38,.04)",
                border:`1px solid ${s.success?"rgba(34,197,94,.15)":"rgba(220,38,38,.1)"}`}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{color:s.success?v("success"):v("danger"),fontWeight:600,fontSize:10}}>{s.success?t("debug.ok"):t("debug.fail")} {s.name}</span>
                  {s.status&&<span style={{fontSize:9,color:"var(--text-3)"}}>{t("debug.http",{status:s.status})}</span>}
                </div>
                {s.data&&<pre style={{fontSize:9,fontFamily:MONO,color:"var(--text-2)",marginTop:4,whiteSpace:"pre-wrap"}}>{JSON.stringify(s.data,null,2)}</pre>}
                {s.error&&!s.success&&<div style={{fontSize:9,color:"var(--text-3)",marginTop:2}}>{s.error.substring(0,200)}</div>}
              </div>
            ))}
          </div>
        ))}
      </div>}
    </>;
  };

  // === Render: Pipeline Checks Section (PD + JMS) - only when hasPipelineConcept ===
  const renderPipelineChecksSection=()=>{
    if(!instance.hasPipelineConcept)return null;
    // Get PD/JMS data from the active comparison result
    const activeComp=pkgTrMode==="multi"?pkgTrMultiChecks[pkgTrActiveTab]:pkgTrComparison;
    const pdDeps=activeComp?.pdDependencies;
    const jmsDeps=activeComp?.jmsDependencies;
    if(!pdDeps&&!jmsDeps)return null;
    const hasPdMissing=pdDeps?.some(pd=>!pd.existsOnTarget||pd.missingParams?.length>0);
    const hasJmsMissing=jmsDeps?.some(jq=>!jq.existsOnTarget);
    const hasJmsCritical=jmsDeps?.some(jq=>jq.critical);
    return <>
      {/* Partner Directory Check */}
      {pdDeps&&pdDeps.length>0&&<>
        <div style={{height:1,background:"var(--border)",margin:"14px 0"}}/>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          <span style={{fontSize:13,fontWeight:600,color:"var(--text-1)"}}>{t("pipelineCheck.pdTitel")}</span>
          {!hasPdMissing&&<span style={{fontSize:10,color:v("success")}}>{t("pipelineCheck.pdAlleDa")}</span>}
          {hasPdMissing&&<span style={{fontSize:10,color:v("danger")}}>{t("pipelineCheck.pdFehlen",{count:pdDeps.filter(p=>!p.existsOnTarget).length})}</span>}
        </div>
        {hasPdMissing&&<div style={{padding:"10px 14px",borderRadius:8,marginBottom:8,display:"flex",alignItems:"flex-start",gap:8,
          background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,fontSize:12}}>
          <WarningAmberIcon sx={{fontSize:16,color:v("danger"),mt:"1px",flexShrink:0}}/>
          <div>
            <div style={{fontWeight:600,color:v("danger")}}>{t("pipelineCheck.pdFehlenTitel")}</div>
            <div style={{fontSize:11,marginTop:2,color:"var(--text-2)"}}>{t("pipelineCheck.pdFehlenText")}</div>
          </div>
        </div>}
        <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"28px 1fr 80px 80px 90px",padding:"8px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
            <div></div><div>PID</div><div>{t("pipelineCheck.spalteString")}</div><div>{t("pipelineCheck.spalteBinary")}</div><div>{t("pipelineCheck.spalteStatus")}</div>
          </div>
          {pdDeps.map(pd=>(
            <div key={pd.pid} style={{display:"grid",gridTemplateColumns:"28px 1fr 80px 80px 90px",padding:"8px 12px",
              borderTop:"1px solid var(--border)",alignItems:"center",
              background:!pd.existsOnTarget?v("status-fail-bg"):"transparent"}}>
              <div>
                {!pd.existsOnTarget?<input type="checkbox"
                  checked={pkgTrPdEntries.find(e=>e.Pid===pd.pid)?.selected||false}
                  onChange={()=>setPkgTrPdEntries(prev=>prev.map(e=>e.Pid===pd.pid?{...e,selected:!e.selected}:e))}
                  style={{width:14,height:14,accentColor:"var(--accent)",cursor:"pointer"}}
                />:<CheckIcon sx={{fontSize:14,color:v("success")}}/>}
              </div>
              <div>
                <span style={{fontSize:13,fontWeight:500,color:pd.existsOnTarget?"var(--text-1)":v("danger")}}>{pd.pid}</span>
                {pd.missingParams?.length>0&&<div style={{fontSize:10,color:"var(--text-3)",marginTop:1}}>
                  {t("pipelineCheck.fehlendeParameter",{namen:pd.missingParams.join(", ")})}
                </div>}
              </div>
              <div style={{fontSize:11,color:"var(--text-2)"}}>{pd.targetStringCount||0}</div>
              <div style={{fontSize:11,color:"var(--text-2)"}}>{pd.targetBinaryCount||0}</div>
              <div style={{fontSize:11}}>
                {pd.existsOnTarget
                  ?<span style={{color:v("success")}}>{t("pipelineCheck.vorhanden")}</span>
                  :<span style={{color:v("danger")}}>{t("pipelineCheck.fehlt")}</span>}
              </div>
            </div>
          ))}
        </div>
      </>}

      {/* JMS Queue Check */}
      {jmsDeps&&jmsDeps.length>0&&<>
        <div style={{height:1,background:"var(--border)",margin:"14px 0"}}/>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          <span style={{fontSize:13,fontWeight:600,color:"var(--text-1)"}}>{t("pipelineCheck.jmsTitel")}</span>
          {!hasJmsMissing&&<span style={{fontSize:10,color:v("success")}}>{t("pipelineCheck.jmsAlleDa")}</span>}
          {hasJmsMissing&&<span style={{fontSize:10,color:hasJmsCritical?v("danger"):v("warning")}}>{t("pipelineCheck.jmsFehlen",{count:jmsDeps.filter(j=>!j.existsOnTarget).length})}</span>}
        </div>
        {hasJmsMissing&&<div style={{padding:"10px 14px",borderRadius:8,marginBottom:8,display:"flex",alignItems:"flex-start",gap:8,
          background:hasJmsCritical?v("status-fail-bg"):v("status-warn-bg"),
          border:`1px solid ${hasJmsCritical?v("status-fail-border"):v("status-warn-border")}`,fontSize:12}}>
          <WarningAmberIcon sx={{fontSize:16,color:hasJmsCritical?v("danger"):v("warning"),mt:"1px",flexShrink:0}}/>
          <div>
            <div style={{fontWeight:600,color:hasJmsCritical?v("danger"):v("warning")}}>{t("pipelineCheck.jmsFehlenTitel")}</div>
            <div style={{fontSize:11,marginTop:2,color:"var(--text-2)"}}>{t("pipelineCheck.jmsFehlenText")}</div>
          </div>
        </div>}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {jmsDeps.map(jq=>(
            <div key={jq.queue} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 12px",borderRadius:6,fontSize:12,
              background:jq.existsOnTarget?"var(--surface-1)":jq.critical?v("status-fail-bg"):v("status-warn-bg"),
              border:`1px solid ${jq.existsOnTarget?"var(--border)":jq.critical?v("status-fail-border"):v("status-warn-border")}`}}>
              {jq.existsOnTarget
                ?<CheckIcon sx={{fontSize:14,color:v("success")}}/>
                :<WarningAmberIcon sx={{fontSize:14,color:jq.critical?v("danger"):v("warning")}}/>}
              <span style={{fontFamily:MONO,fontSize:12,fontWeight:500,color:"var(--text-1)"}}>{jq.queue}</span>
              <span style={{fontSize:10,color:"var(--text-3)"}}>
                {jq.existsOnTarget?t("pipelineCheck.vorhanden"):jq.critical?t("pipelineCheck.fehltKritisch"):t("pipelineCheck.fehlt")}
              </span>
              {jq.referencedBy?.length>0&&<span style={{fontSize:10,color:"var(--text-3)",marginLeft:"auto"}}>
                {t("pipelineCheck.referenziertVon",{namen:jq.referencedBy.join(", ")})}
              </span>}
            </div>
          ))}
        </div>
      </>}
    </>;
  };

  const pkgTrRunPrecheck=async(targetInstId)=>{
    // Single-mode precheck: check each selected werk
    setPkgTrLoading(true);setPkgTrComparison(null);setPkgTrMultiChecks({});setPkgTrOrphanSel({});
    const selectedWerke=(pkgTrWerke[targetInstId]||[]).filter(w=>w.selected);
    if(selectedWerke.length===0){
      // No werke: classic single precheck
      try{
        const r=await api("/api/transport/package-precheck",{method:"POST",timeout:120000,body:JSON.stringify({
          sourceInstanceId:instance.id,targetInstanceId:targetInstId,
          sourcePackageId:pkgTransport.Id,targetPackageId:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,
          ...(instance.hasPipelineConcept?{deepScan:true}:{})
        })});
        setPkgTrComparison(r);
        // Orphan-Selection Default: alle Orphans ausgewählt
        if(r.orphans?.length>0){
          const sel={};r.orphans.forEach(o=>{sel[o.Id]=true;});setPkgTrOrphanSel(prev=>({...prev,...sel}));
        }
        // Populate PD entries from deepScan results for transport selection
        if(r.pdDependencies){
          setPkgTrPdEntries(r.pdDependencies.filter(pd=>!pd.existsOnTarget).map(pd=>({
            Pid:pd.pid,stringCount:0,binaryCount:0,altCount:0,selected:true
          })));
          setPkgTrPdEnabled(r.pdDependencies.some(pd=>!pd.existsOnTarget));
        }
        // Auto-load global references for selected artifacts
        const selectedIds=(r.comparison||[]).filter(a=>a.selected&&!a.draft).map(a=>a.Id);
        loadGlobalRefs(targetInstId,selectedIds);
      }catch(e){setPkgTrComparison({error:e.message});}
    }else{
      // Werke-based: precheck per werk with suffix
      const checks={};
      const firstKey=`${targetInstId}__${selectedWerke[0].kennung}`;
      setPkgTrActiveTab(firstKey);
      for(const w of selectedWerke){
        const key=`${targetInstId}__${w.kennung}`;
        const basePkgId=pkgTrCustomId?pkgTrTargetId:pkgTransport.Id;
        try{
          const r=await api("/api/transport/package-precheck",{method:"POST",timeout:120000,body:JSON.stringify({
            sourceInstanceId:instance.id,targetInstanceId:targetInstId,
            sourcePackageId:pkgTransport.Id,targetPackageId:`${basePkgId}${w.kennung}`,
            werkKennung:w.kennung,
            ...(instance.hasPipelineConcept?{deepScan:true}:{})
          })});
          checks[key]={...r,werk:w};
          if(r.orphans?.length>0){const sel={};r.orphans.forEach(o=>{sel[`${o.Id}__${key}`]=true;});setPkgTrOrphanSel(prev=>({...prev,...sel}));}
        }catch(e){checks[key]={error:e.message,werk:w};}
        setPkgTrMultiChecks({...checks});
      }
      // Werke-spezifische Runtimes initialisieren
      const werkRuntimes={};
      for(const [key,check] of Object.entries(checks)){
        const rts=check.availableRuntimes||[{name:"Cloud Integration",locationId:null}];
        werkRuntimes[key]=rts.map((rt,i)=>({...rt,selected:i===0}));
      }
      setPkgTrWerkeRuntimes(werkRuntimes);
      // Load global refs for Werke mode (use source artifact IDs from first check)
      const firstCheck=Object.values(checks)[0];
      if(firstCheck?.comparison){
        const srcIds=[...new Set(firstCheck.comparison.filter(a=>a.selected&&!a.draft).map(a=>{
          // Strip kennung suffix to get original source ID
          const origId=a.Id;const k=firstCheck.kennung||"";
          return k&&origId.endsWith(k)?origId.slice(0,-k.length):origId;
        }))];
        if(srcIds.length>0)loadGlobalRefs(targetInstId,srcIds);
      }
    }
    setPkgTrLoading(false);
  };
  const pkgTrRunMultiPrechecks=async()=>{
    setPkgTrLoading(true);setPkgTrMultiChecks({});
    const checks={};let firstKey="";
    for(const tId of pkgTrTargets){
      const selectedWerke=(pkgTrWerke[tId]||[]).filter(w=>w.selected);
      if(selectedWerke.length===0){
        // Instance without werke
        const key=tId;
        if(!firstKey)firstKey=key;
        try{
          const r=await api("/api/transport/package-precheck",{method:"POST",timeout:120000,body:JSON.stringify({
            sourceInstanceId:instance.id,targetInstanceId:tId,
            sourcePackageId:pkgTransport.Id,targetPackageId:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,
            ...(instance.hasPipelineConcept?{deepScan:true}:{})
          })});
          checks[key]=r;
          if(r.orphans?.length>0){const sel={};r.orphans.forEach(o=>{sel[o.Id]=true;});setPkgTrOrphanSel(prev=>({...prev,...sel}));}
        }catch(e){checks[key]={error:e.message};}
        setPkgTrMultiChecks({...checks});
      }else{
        for(const w of selectedWerke){
          const key=`${tId}__${w.kennung}`;
          if(!firstKey)firstKey=key;
          const basePkgId=pkgTrCustomId?pkgTrTargetId:pkgTransport.Id;
          try{
            const r=await api("/api/transport/package-precheck",{method:"POST",timeout:120000,body:JSON.stringify({
              sourceInstanceId:instance.id,targetInstanceId:tId,
              sourcePackageId:pkgTransport.Id,targetPackageId:`${basePkgId}${w.kennung}`,
              werkKennung:w.kennung,
              ...(instance.hasPipelineConcept?{deepScan:true}:{})
            })});
            checks[key]={...r,werk:w};
            if(r.orphans?.length>0){const sel={};r.orphans.forEach(o=>{sel[`${o.Id}__${key}`]=true;});setPkgTrOrphanSel(prev=>({...prev,...sel}));}
          }catch(e){checks[key]={error:e.message,werk:w};}
          setPkgTrMultiChecks({...checks});
        }
      }
    }
    // Werke-spezifische Runtimes initialisieren (Multi-Target)
    const werkRuntimes={};
    for(const [key,check] of Object.entries(checks)){
      const rts=check.availableRuntimes||[{name:"Cloud Integration",locationId:null}];
      werkRuntimes[key]=rts.map((rt,i)=>({...rt,selected:i===0}));
    }
    setPkgTrWerkeRuntimes(werkRuntimes);
    setPkgTrActiveTab(firstKey);
    setPkgTrLoading(false);
    // Load global refs for multi-mode
    const firstChk=Object.values(checks)[0];
    if(firstChk?.comparison){
      const srcIds=[...new Set(firstChk.comparison.filter(a=>a.selected&&!a.draft).map(a=>{
        const origId=a.Id;const k=firstChk.kennung||"";
        return k&&origId.endsWith(k)?origId.slice(0,-k.length):origId;
      }))];
      const firstTgtId=Object.keys(checks)[0]?.split("__")[0]||pkgTrTargets[0];
      if(srcIds.length>0&&firstTgtId)loadGlobalRefs(firstTgtId,srcIds);
    }
  };
  const pkgTrToggleArtifact=(artId)=>{
    if(pkgTrMode==="multi"){
      setPkgTrMultiChecks(prev=>{const c={...prev};const tab=pkgTrActiveTab;if(!c[tab]?.comparison)return prev;
        c[tab]={...c[tab],comparison:c[tab].comparison.map(a=>a.Id===artId&&!a.draft?{...a,selected:!a.selected}:a)};return c;});
    }else{
      setPkgTrComparison(prev=>{if(!prev?.comparison)return prev;
        return{...prev,comparison:prev.comparison.map(a=>a.Id===artId&&!a.draft?{...a,selected:!a.selected}:a)};});
    }
  };

  // ===== Draft-Release: Version releasen direkt aus dem Wizard =====
  const cmpArtVer=(a,b)=>{const A=String(a||"").split(".").map(n=>parseInt(n,10)||0),B=String(b||"").split(".").map(n=>parseInt(n,10)||0);
    for(let i=0;i<Math.max(A.length,B.length);i++){const d=(A[i]||0)-(B[i]||0);if(d)return d;}return 0;};
  const openReleaseEditor=async(art)=>{
    setPkgTrRelease({artId:art.Id,loading:"info",version:"",floor:null,suggested:null,source:null,error:null});
    try{
      const d=await api(`/api/${instance.id}/transport/artifact-last-version/${encodeURIComponent(art.Id)}?packageId=${encodeURIComponent(pkgTransport?.Id||"")}`);
      setPkgTrRelease(p=>p&&p.artId===art.Id?{...p,loading:null,floor:d.floor||null,suggested:d.suggested||"1.0.1",source:d.source||null,version:d.suggested||"1.0.1"}:p);
    }catch(e){
      setPkgTrRelease(p=>p&&p.artId===art.Id?{...p,loading:null,floor:null,suggested:"1.0.1",version:"1.0.1",error:t("release.nichtErmittelbar",{fehler:e.message})}:p);
    }
  };
  const doReleaseVersion=async(art)=>{
    const st=pkgTrRelease;if(!st||st.artId!==art.Id)return;
    const ver=(st.version||"").trim();
    if(!/^\d+(\.\d+){1,3}$/.test(ver)){setPkgTrRelease({...st,error:t("release.formatFehler")});return;}
    if(st.floor&&cmpArtVer(ver,st.floor)<=0){setPkgTrRelease({...st,error:t("release.zuKlein",{version:st.floor})});return;}
    setPkgTrRelease({...st,loading:"release",error:null});
    try{
      await api(`/api/${instance.id}/transport/release-version`,{method:"POST",body:JSON.stringify({flowId:art.Id,version:ver,packageId:pkgTransport?.Id||null,floor:st.floor||null})});
      // Zeilen in ALLEN Vergleichen aktualisieren: neue Version, Draft weg, Status neu bewerten, auswaehlen
      const upd=(a)=>{if(a.Id!==art.Id)return a;
        const status=a.targetVersion?(a.targetVersion===ver?"identical":"update"):"new";
        return {...a,Version:ver,draft:false,status,selected:status!=="identical"};};
      setPkgTrComparison(p=>p?.comparison?{...p,comparison:p.comparison.map(upd)}:p);
      setPkgTrMultiChecks(p=>{if(!p)return p;const n={};for(const k of Object.keys(p)){n[k]=p[k]?.comparison?{...p[k],comparison:p[k].comparison.map(upd)}:p[k];}return n;});
      setPkgTrRelease(null);
    }catch(e){
      setPkgTrRelease(p=>p&&p.artId===art.Id?{...p,loading:null,error:e.message}:p);
    }
  };
  // Draft-Hinweisstreifen + Inline-Editor unter der Artefakt-Zeile (beide Wizard-Listen)
  const renderDraftReleaseStrip=(art)=>{
    if(!art.draft&&pkgTrRelease?.artId!==art.Id)return null;
    const editing=pkgTrRelease?.artId===art.Id;
    // Schluessel kommt vom Server und bleibt woertlich, die Anzeige uebersetzt
    const srcLabel={manifest:t("release.quelleManifest"),runtime:t("release.quelleRuntime"),designtime:t("release.quelleDesigntime")};
    return <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",padding:"6px 12px 8px 44px",borderTop:"1px dashed var(--border)",background:"var(--surface-1)"}}>
      {!editing&&<>
        <span style={{fontSize:11,color:v("danger")}}>{t("release.draftHinweis")}</span>
        <button onClick={e=>{e.stopPropagation();openReleaseEditor(art);}} style={{background:"none",border:"none",cursor:"pointer",padding:"2px 6px",fontSize:11,fontFamily:FONT,color:v("accent"),fontWeight:600,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:4}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
          {t("release.knopf")}
        </button>
      </>}
      {editing&&(pkgTrRelease.loading==="info"
        ?<span style={{fontSize:12,color:"var(--text-3)"}}>{t("release.ermittle")}</span>
        :<>
          <span style={{fontSize:12,color:"var(--text-2)"}}>
            {pkgTrRelease.floor
              ?<>{t("release.letzteVersion")} <b style={{color:"var(--text-1)"}}>{pkgTrRelease.floor}</b>{pkgTrRelease.source&&<span style={{color:"var(--text-3)"}}> ({srcLabel[pkgTrRelease.source]||pkgTrRelease.source})</span>}</>
              :t("release.keineVersion")}
          </span>
          <input value={pkgTrRelease.version} onChange={e=>setPkgTrRelease(p=>({...p,version:e.target.value,error:null}))} placeholder={pkgTrRelease.suggested||"1.0.1"}
            style={{width:110,background:"var(--surface-0)",border:`1px solid ${pkgTrRelease.error?v("danger"):"var(--border)"}`,borderRadius:6,padding:"5px 8px",color:"var(--text-1)",fontSize:12,fontFamily:FONT,outline:"none"}}/>
          <button disabled={pkgTrRelease.loading==="release"} onClick={e=>{e.stopPropagation();doReleaseVersion(art);}}
            style={{padding:"5px 12px",borderRadius:6,border:"none",background:v("accent"),color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",opacity:pkgTrRelease.loading==="release"?.6:1}}>
            {pkgTrRelease.loading==="release"?t("release.laeuft"):t("release.knopf")}
          </button>
          <button onClick={e=>{e.stopPropagation();setPkgTrRelease(null);}} style={{padding:"5px 10px",borderRadius:6,border:"1px solid var(--border)",background:"transparent",color:"var(--text-2)",fontSize:12,cursor:"pointer",fontFamily:FONT}}>{t("gemeinsam:aktion.abbrechen")}</button>
          {pkgTrRelease.error&&<span style={{fontSize:12,color:v("danger"),flexBasis:"100%"}}>{pkgTrRelease.error}</span>}
        </>)}
    </div>;
  };
  const pkgTrSelectAll=(val)=>{
    if(pkgTrMode==="multi"){
      setPkgTrMultiChecks(prev=>{const c={...prev};const tab=pkgTrActiveTab;if(!c[tab]?.comparison)return prev;
        c[tab]={...c[tab],comparison:c[tab].comparison.map(a=>({...a,selected:a.draft?false:val}))};return c;});
    }else{
      setPkgTrComparison(prev=>{if(!prev?.comparison)return prev;
        return{...prev,comparison:prev.comparison.map(a=>({...a,selected:a.draft?false:val}))};});
    }
  };
  const executePkgTransport=async(retryKeys)=>{
    setPkgTrLoading(true);setPkgTrResult(null);
    if(!retryKeys){setPkgTrProgress([]);setPkgTrMultiResults({});}
    const hasWerkeChecks=Object.keys(pkgTrMultiChecks).length>0;
    const delay=ms=>new Promise(r=>setTimeout(r,ms));
    if(hasWerkeChecks){
      const allResults={};
      const checkKeysToRun=retryKeys||Object.keys(pkgTrMultiChecks);
      let idx=0;
      const globalRefsSentToInstances=new Set(); // Track which instances already received global refs
      for(const checkKey of checkKeysToRun){
        const check=pkgTrMultiChecks[checkKey];if(!check?.comparison)continue;
        const selected=check.comparison.filter(a=>a.selected&&!a.draft);if(!selected.length)continue;
        if(idx>0)await delay(3000);
        idx++;
        const parts=checkKey.split("__");
        const tId=parts[0];const kennung=parts[1]||null;
        const basePkgId=pkgTrCustomId?pkgTrTargetId:pkgTransport.Id;
        const basePkgName=pkgTrCustomId?pkgTrTargetName:pkgTransport.Name;
        const targetPkgId=kennung?`${basePkgId}${kennung}`:basePkgId;
        const targetPkgName=kennung?`${basePkgName}_${kennung}`:basePkgName;
        const arts=selected.map(art=>{
          const ck=`${art.Id}__${checkKey}`;const cfgs=pkgTrConfigs[ck];
          const modified=buildConfigPayload(cfgs);
          const artId=kennung?`${art.Id}${kennung}`:art.Id;
          const artName=kennung?`${art.Name}_${kennung}`:art.Name;
          return{...art,Id:artId,Name:artName,originalId:art.Id,configurations:modified?.length?modified:undefined};
        });
        try{
          // Send global refs to each UNIQUE target instance (not per Werk, but per instance)
          const sendGlobalRefs=!globalRefsSentToInstances.has(tId);
          const selectedGlobalRefs=sendGlobalRefs?(pkgTrGlobalRefs?.references||[]).filter(r=>r.selected):[];
          if(sendGlobalRefs&&selectedGlobalRefs.length>0)globalRefsSentToInstances.add(tId);
          // Access Policy Pre Flight (defensiv, blockiert nur bei explizitem User Abbruch)
          const _gate2=await runComplianceGate(tId,{Id:targetPkgId,Name:targetPkgName},arts);
          if(!_gate2.ok){allResults[checkKey]={success:false,error:t("transport.abgebrochenCompliance")};continue;}
          const r=await api("/api/transport/package-execute",{method:"POST",timeout:600000,body:JSON.stringify({
            approvalNote:pkgTrApprovalNote.trim(),sourceInstanceId:instance.id,targetInstanceId:tId,sourcePackageId:pkgTransport.Id,
            targetPackageId:targetPkgId,targetPackageName:targetPkgName,
            selectedArtifacts:arts,globalReferences:selectedGlobalRefs.length?selectedGlobalRefs:undefined,
            deleteOrphans:check.orphans?.filter(o=>pkgTrOrphanSel[`${o.Id}__${checkKey}`]!==false)||undefined,
            deployAfter:pkgTrDeploy,createPackage:!check.targetExists,
            werkKennung:kennung||null,
            deployRuntimes:(pkgTrWerkeRuntimes[checkKey]||pkgTrDeployRuntimes).filter(rt=>rt.selected).map(rt=>({name:rt.name,locationId:rt.locationId}))
          })});
          // Schleuse: als Transportauftrag eingereiht
          if(r?.queued&&r?.approvalRequired){allResults[checkKey]={success:true,queued:true,requestId:r.requestId};continue;}
          allResults[checkKey]=r;
        }catch(e){allResults[checkKey]={success:false,error:e.message};}
        setPkgTrMultiResults(prev=>({...prev,...allResults}));
      }
      // Merge with previous results if retrying
      const merged=retryKeys?{...pkgTrMultiResults,...allResults}:allResults;
      setPkgTrMultiResults(merged);
      const totalOk=Object.values(merged).every(r=>r.success||r.partialSuccess);
      // Schleuse: wenn alle Fahrten als Auftrag eingereiht wurden, das klar ausweisen
      const queuedEntry=Object.values(merged).find(r=>r.queued);
      const allQueued=Object.values(merged).length>0&&Object.values(merged).every(r=>r.queued);
      const finalResult={success:totalOk,multi:true,results:merged,...(allQueued?{queued:true,requestId:queuedEntry?.requestId}:{})};
      setPkgTrResult(finalResult);
      setPkgTrLoading(false);
      return finalResult;
    }else{
      const comp=pkgTrComparison;
      if(!comp?.comparison){setPkgTrLoading(false);return{success:false,error:t("transport.keinVergleich")};}
      const selected=comp.comparison.filter(a=>a.selected&&!a.draft);if(!selected.length){setPkgTrLoading(false);return{success:false,error:t("transport.keineArtefakte")};}
      const arts=selected.map(art=>{
        const ck=getConfigCacheKey(art.Id);const cfgs=pkgTrConfigs[ck];
        const modified=buildConfigPayload(cfgs);
        return{...art,configurations:modified?.length?modified:undefined};
      });
      try{
        const selectedGlobalRefs=(pkgTrGlobalRefs?.references||[]).filter(r=>r.selected);
        // Access Policy Pre Flight (defensiv, blockiert nur bei explizitem User Abbruch)
        const _gate3=await runComplianceGate(pkgTrTarget,{Id:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,Name:pkgTrCustomId?pkgTrTargetName:pkgTransport.Name},arts);
        if(!_gate3.ok){setPkgTrLoading(false);return{success:false,error:t("transport.abgebrochenCompliance")};}
        const r=await api("/api/transport/package-execute",{method:"POST",timeout:600000,body:JSON.stringify({
          approvalNote:pkgTrApprovalNote.trim(),sourceInstanceId:instance.id,targetInstanceId:pkgTrTarget,sourcePackageId:pkgTransport.Id,
          targetPackageId:pkgTrCustomId?pkgTrTargetId:pkgTransport.Id,
          targetPackageName:pkgTrCustomId?pkgTrTargetName:pkgTransport.Name,
          selectedArtifacts:arts,globalReferences:selectedGlobalRefs.length?selectedGlobalRefs:undefined,
          deleteOrphans:pkgTrComparison?.orphans?.filter(o=>pkgTrOrphanSel[o.Id]!==false)||undefined,
          deployAfter:pkgTrDeploy,createPackage:!comp.targetExists,
          deployRuntimes:pkgTrDeployRuntimes.filter(rt=>rt.selected).map(rt=>({name:rt.name,locationId:rt.locationId}))
        })});
        // Schleuse: als Transportauftrag eingereiht
        if(r?.queued&&r?.approvalRequired){const q={success:true,queued:true,requestId:r.requestId};setPkgTrResult(q);setPkgTrLoading(false);return q;}
        setPkgTrResult(r);setPkgTrProgress(r.results||[]);
        setPkgTrLoading(false);
        return r;
      }catch(e){const err={success:false,error:e.message};setPkgTrResult(err);setPkgTrLoading(false);return err;}
    }
  };
  const retryFailedTransports=()=>{
    const failedKeys=Object.entries(pkgTrMultiResults).filter(([_,r])=>!r.success).map(([k])=>k);
    if(!failedKeys.length)return;
    setPkgTrResult(null);
    executePkgTransport(failedKeys);
  };

  // System prompts for documentation generation
  const DOC_SYSTEM_PROMPTS = {
    technical: `You are a technical documentation assistant specialized in SAP Integration Suite, especially Cloud Integration iFlows.

Your task is to generate a clear, accurate, implementation-based technical documentation for exactly one iFlow using only the retrieved context provided to you.

PRIMARY RULES

1. Use only the provided source material. Do not invent configuration, process steps, payload fields, mappings, scripts, fallback logic, endpoints, credentials, error handling, or monitoring behavior that are not explicitly supported by the retrieved context.
2. If information is missing, say so explicitly. Use phrases such as: "Not available in the provided sources." or "This could not be confirmed from the retrieved context."
3. Prefer accuracy over completeness. If there is uncertainty or conflicting evidence, describe the uncertainty clearly.
4. Document the implementation as it exists. Do not redesign, optimize, or suggest alternatives unless asked.
5. Write the documentation in German. Use Markdown formatting. Use ## and ### headings consistently so a Table of Contents sidebar can be generated.
6. Explain scripts at block level, not line by line. Describe: purpose, inputs (body, headers, properties), main processing blocks, outputs, fallback behavior, error handling.
7. Be explicit about fallbacks. Differentiate between: fallback between trigger-specific data sources, fallback to constants/default values, implicit assumptions.
8. Preserve SAP terminology: iFlow, Content Modifier, Router, Groovy Script, Externalized Parameter, OData Adapter, IDoc, Message Mapping, Exchange Property, MPL, Exception Subprocess, etc.

FORMATTING RULES - VERY IMPORTANT:
- Use PROPER Markdown tables with | column | separators and |---|---| header rows for ALL structured data (parameters, configurations, header/property lists, field mappings). Example:
| Parameter | Wert |
|---|---|
| Name | Value |
- NEVER use bullet-list formatting for structured key-value data. ALWAYS use tables.
- Use code blocks with triple backticks for code, scripts, URLs, and technical values.
- Be thorough and detailed. This documentation should be complete enough that a new developer could maintain the iFlow without access to the SAP Integration Suite.

OUTPUT STRUCTURE (use exactly these headings):

# iFlow: [Name]

## 1. Basisinformationen
Table with: iFlow Name, Package, Zweck, Trigger, Sender-System, Empfänger-System(e)

## 2. Geschäftlicher und Technischer Zweck
Welches Geschäftsproblem wird gelöst, was ist das technische Integrationsziel.

## 3. Verarbeitungsübersicht
End-to-End Flow vom Trigger bis zur Ausgabe als ASCII-Diagramm. Nur die Hauptphasen.

## 4. Schritt-für-Schritt Verarbeitung
Für jeden Step oder logische Gruppe:
**Komponente:** z.B. Content Modifier, Groovy Script
**Zweck:** Was macht dieser Step
**Konfiguration:** Als Markdown-Tabelle mit Spalten Parameter/Wert (KEINE Bullet-Listen!)
**Ergebnis:** Output

## 5. Schnittstellen und Payloads
Inbound/Outbound Interfaces als Tabellen, Payload-Formate, relevante Felder für Routing/Mapping/Validierung.

## 6. Mapping und Transformationslogik
Source→Target Strukturen, wichtige Feld-Mappings, Enrichment, bedingte Mappings, Fallback-Verhalten.

## 7. Script-Logik
Für jedes Script: Name, Zweck, Inputs, Verarbeitungslogik (Block-Level), Outputs, Fallbacks, Error Handling.

## 8. Konfigurationsdetails
Adapter-Typen, Endpoints, Auth-Mechanismus, externalisierte Parameter, Landschafts-Variablen. Als Tabellen!

## 9. Fehlerbehandlung und Validierung
Validierungen, Exception Subprocesses, Retry, Error Propagation, fehlende Pflichtdaten.

## 10. Monitoring und Logging
MPL-Logging, Custom Properties, Payload-Logging, Hinweise für Support-Teams.

## 11. Fallbacks, Defaults und Annahmen
A. Bestätigte Fallbacks
B. Bestätigte Defaults/Konstanten
C. Unbestätigte oder mehrdeutige Fallbacks
D. Wichtige Implementierungsannahmen

## 12. Abhängigkeiten
Externe Systeme, Value Mappings, Scripts, Secure Parameters, Partner-Konfiguration.

## 13. Ergebnis
Was wird empfangen, wie verarbeitet, was wird gesendet, finales Integrationsergebnis.

## 14. Offene Punkte
Bereiche die nicht aus den Quellen verifiziert werden konnten.

QUALITY CHECK: Before producing output, verify: Only source-supported content? Unknowns labeled? Scripts at block level? Fallbacks documented? German language? Required heading structure? ALL structured data as Markdown tables (not bullet lists)?`,

    executive: `You are a management documentation assistant specialized in SAP Integration Suite.

Your task is to generate a Management Summary for an iFlow that is suitable for decision-makers, project managers, and non-technical stakeholders.

RULES:
1. Use only the provided source material. Do not invent business context.
2. Write in German. Use Markdown with ## headings for Table of Contents generation.
3. Focus on business value, cost implications, and strategic importance.
4. Include concrete numbers where possible: how many records, how often, which systems.
5. Highlight risks and dependencies in business terms.
6. Use PROPER Markdown tables (with | column | separators and |---|---| header rows) for ALL structured data. NEVER use bullet-list formatting for key-value data.

OUTPUT STRUCTURE:

# Management Summary: [iFlow Name]

## Executive Summary
2-3 Sätze: Was macht die Schnittstelle, welche Systeme, welcher Business Value.

## Geschäftlicher Nutzen
- Welches Problem wird gelöst?
- Welche manuellen Prozesse werden automatisiert?
- Geschätzte Zeitersparnis / Effizienzgewinn
- Welche Abteilungen/Teams profitieren?

## Kostenaspekte
- Welche Systeme/Lizenzen sind beteiligt?
- Betriebskosten (API-Aufrufe, Datenvolumen, Monitoring)
- Einsparungspotenzial durch Automatisierung
- ROI-relevante Faktoren

## Systemlandschaft
- Beteiligte Systeme (Sender/Empfänger) mit kurzer Erklärung
- Datenfluss vereinfacht dargestellt
- Abhängigkeiten zu anderen Schnittstellen

## Datenfluss
- Von wo nach wo fließen Daten?
- Welche Transformationen finden statt? (vereinfacht)
- Häufigkeit und Volumen der Datenübertragung

## Risiken und Abhängigkeiten
- Technische Risiken (System-Verfügbarkeit, Datenqualität)
- Geschäftliche Risiken (Was passiert bei Ausfall?)
- Externe Abhängigkeiten (Drittanbieter, Zertifikate, Token)

## KPIs und Monitoring
- Wie wird der Erfolg der Schnittstelle gemessen?
- Welche Metriken sind relevant?
- Wer ist verantwortlich für Monitoring?

## Empfehlung / Nächste Schritte
- Aktuelle Bewertung des iFlows
- Verbesserungspotenziale (falls sichtbar)
- Handlungsempfehlungen

Kein technischer Code. Fokus auf Business Value und Zahlen. Wenn Kosten-/Volumen-Informationen nicht aus den Quellen ableitbar sind, kennzeichne dies als "aus den Quellen nicht ersichtlich".`,

    onboarding: `You are an onboarding documentation assistant for SAP Integration Suite.

Your task is to create a beginner-friendly introduction to an iFlow that can be understood by team members without deep SAP CPI knowledge.

RULES:
1. Use only the provided source material.
2. Write in German. Use Markdown with ## headings for Table of Contents.
3. Use simple language. Explain SAP terms when first used.
4. Use analogies where helpful.
5. Avoid code unless absolutely necessary for understanding.
6. Use PROPER Markdown tables (with | column | separators and |---|---| header rows) for structured data like system overviews. NEVER use bullet-list formatting for key-value data.

OUTPUT STRUCTURE:

# Einführung: [iFlow Name]

## Was macht dieser iFlow?
Erkläre in 3-4 einfachen Sätzen was die Schnittstelle tut. Stell dir vor du erklärst es einem neuen Kollegen am ersten Tag.

## Warum gibt es diesen iFlow?
- Welches Geschäftsproblem wird gelöst?
- Was würde ohne diese Schnittstelle passieren?

## Welche Systeme sind beteiligt?
Für jedes System:
- Name und kurze Erklärung (was ist das System?)
- Rolle in dieser Integration (sendet/empfängt was?)

## Wie funktioniert der Prozess?
Schritt-für-Schritt in einfacher Sprache:
1. [Auslöser] → Was startet den Prozess?
2. [Daten holen] → Woher kommen die Daten?
3. [Verarbeiten] → Was passiert mit den Daten?
4. [Senden] → Wohin gehen die Daten?

Nutze Analogien: "Wie ein Übersetzer der..." oder "Stell dir vor..."

## Was passiert bei Fehlern?
- Welche typischen Fehler können auftreten?
- Was macht das System automatisch?
- Wann muss jemand eingreifen?

## Glossar
Erkläre die wichtigsten SAP-Begriffe die im iFlow vorkommen:
- Content Modifier: ...
- Groovy Script: ...
- Request Reply: ...
(nur die tatsächlich verwendeten Begriffe)

## Häufige Fragen
Stelle 3-5 Fragen die ein neuer Mitarbeiter wahrscheinlich stellen würde, und beantworte sie.

Vermeide Fachbegriffe oder erkläre sie sofort. Der Text soll ohne SAP-Vorwissen verständlich sein.`
  };
  const getDocPrompt = (style) => {
    // Custom templates return just the prompt - system prompt handling is separate
    const customT=customTemplates.find(t=>`custom_${t.id}`===style);
    if(customT) return {userMsg: "Generiere die Dokumentation für diesen iFlow basierend auf dem bereitgestellten Template.", sysPrompt: customT.prompt + "\n\nSchreibe auf Deutsch. Verwende Markdown mit ## Überschriften."};
    const sysPrompt = DOC_SYSTEM_PROMPTS[style] || DOC_SYSTEM_PROMPTS.technical;
    return {userMsg: "Generiere jetzt die vollständige Dokumentation für diesen iFlow basierend auf den bereitgestellten Quellen.", sysPrompt};
  };
  // Estimate documentation generation time based on iFlow complexity
  const estimateDocTime = (ext) => {
    if (!ext) return null;
    const stepCount   = ext.bpmn ? (ext.bpmn.match(/<(?:bpmn2:)?callActivity/g) || []).length : 0;
    const scriptCount = (ext.scripts || []).length;
    const configCount = (ext.configurations || []).length;

    // Option B: get_full_iflow_context loads everything in 1 tool round (~20s)
    // Streaming generation: ~40-60s depending on doc complexity
    // Total: roughly 60-90s regardless of script count
    const seconds = scriptCount > 10 ? 90 : stepCount > 15 ? 75 : 60;
    const label = t("doku.dauerLabel");

    // Phase breakdown for display
    const phases = [
      t("doku.phaseDaten"),
      stepCount > 0 ? t("doku.phaseSteps", { count: stepCount }) : null,
      scriptCount > 0 ? t("doku.phaseScripts", { count: scriptCount }) : null,
      t("doku.phaseSchreiben"),
    ].filter(Boolean);

    return { seconds, label, phases, stepCount, scriptCount, configCount };
  };

  const loadOrGenerateDoc = useCallback(async (style, forceRegenerate = false) => {
    if (!extracted || !selFlow) return;
    setDocStyle(style); setDocSaveError(false);
    setShowDocStylePicker(false); setShowRegenPicker(false);
    // Check cache first
    if (!forceRegenerate && docCache[style]) {
      setDocContent(docCache[style]);
      setDocPanel(true);
      return;
    }
    // Check backend
    if (!forceRegenerate) {
      try {
        const doc = await api(`/api/docs/${instance.id}/${encodeURIComponent(selFlow.Id)}:${style}`);
        if (doc?.content) {
          setDocCache(prev => ({ ...prev, [style]: doc.content }));
          setDocContent(doc.content);
          setDocPanel(true);
          return;
        }
      } catch {}
    }
    // Generate new via streaming - show estimate first
    const estimate = estimateDocTime(extracted);
    setDocEstimate(estimate);
    setDocPanel(true); setDocLoading(true); setDocContent(null);
    const {userMsg, sysPrompt: stylePrompt} = getDocPrompt(style);
    const sysPrompt = stylePrompt + (i18nInst.language === "en" ? "\n\nOutput language: English. Write all headings and prose in English." : "\n\nAusgabesprache: Deutsch. Schreiben Sie Überschriften und Fließtext auf Deutsch.");
    const selectedTemplate = customTemplates.find(template => `custom_${template.id}` === style);
    const saveDocument = async content => {
      try {
        await api(`/api/docs/${instance.id}/${encodeURIComponent(selFlow.Id)}:${style}`, { method: "PUT", timeout: 300000, body: JSON.stringify({ content,
          flowName: selFlow.Name || selFlow.Id, packageId: selPkg?.Id || "", language: i18nInst.language === "en" ? "en" : "de", bpmnXml: extracted.bpmnXml || extracted.bpmn || "",
          customPrompt: selectedTemplate?.prompt, styleTitle: selectedTemplate?.name }) });
        window.dispatchEvent(new Event("iflow-documentation-saved"));
      } catch { setDocSaveError(true); }
    };
    try {
      const resp = await fetch("/api/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("cpi_token") },
        body: JSON.stringify({
          messages: [{ role: "user", content: userMsg }],
          docSystemPrompt: sysPrompt,
          iflowContext: { instanceId: instance.id, flowId: selFlow.Id, flowName: selFlow.Name }
        })
      });
      if (!resp.ok) { const err = await resp.json().catch(() => ({})); throw new Error(err.error || "Streaming fehlgeschlagen"); }
      const full = await readDocumentationStream(resp, setDocContent, setDocUsage);
      if (full) {
        setDocCache(prev => ({ ...prev, [style]: full }));
        await saveDocument(full);
      }
    } catch (e) {
      // Fallback to non-streaming
      try {
        const data = await api("/api/chat", {
          method: "POST",
          body: JSON.stringify({
            messages: [{ role: "user", content: userMsg }],
            docSystemPrompt: sysPrompt,
            iflowContext: { instanceId: instance.id, flowId: selFlow.Id, flowName: selFlow.Name }
          })
        });
        setDocContent(data.response);
        setDocCache(prev => ({ ...prev, [style]: data.response }));
        await saveDocument(data.response);
      } catch (e2) { setDocContent(t("doku.fehler", { fehler: e2.message })); }
    }
    setDocLoading(false); setDocEstimate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extracted, selFlow, selPkg?.Id, instance.id, docCache, i18nInst.language, customTemplates]);
  // Backwards compat wrapper
  const generateDoc = useCallback((forceRegenerate = false) => loadOrGenerateDoc(docStyle, forceRegenerate), [loadOrGenerateDoc, docStyle]);

  const findLinkedScript = useCallback((step) => {
    if (!step || !extracted?.scripts?.length) return null;
    const stepName = step.name?.toLowerCase() || "";
    const stepId = step.id?.toLowerCase() || "";
    // 1. Check props for script file reference (e.g. /script/script4.groovy)
    const scriptFile = step.props?.script || step.props?.scriptFunction || step.props?.["Script File"] || "";
    if (scriptFile) {
      const fileName = scriptFile.split("/").pop().toLowerCase();
      const match = extracted.scripts.find(s => s.name.toLowerCase() === fileName);
      if (match) return match;
    }
    // 2. Check props for scriptBundleId
    const bundleId = step.props?.scriptBundleId || step.props?.["Script Bundle ID"] || "";
    if (bundleId) {
      const match = extracted.scripts.find(s => s.name.toLowerCase().includes(bundleId.toLowerCase()));
      if (match) return match;
    }
    // 3. Match by name similarity
    return extracted.scripts.find(s => {
      const sName = s.name.toLowerCase().replace(/\.(groovy|gsh|js)$/i, "");
      return stepName.includes(sName) || sName.includes(stepName.replace(/\s+/g, "")) || stepId.includes(sName) || sName.includes(stepId.replace(/[_-]/g, ""));
    }) || null;
  }, [extracted]);

  const handleElementClick = useCallback((element) => {
    if (element.type?.includes("Process") || element.type?.includes("Collaboration") || element.type?.includes("Participant")) return;
    // Try multiple key formats for stepProperties lookup
    const extraProps =
      extracted?.stepProperties?.[element.id] ||
      extracted?.stepProperties?.[element.name] ||
      extracted?.stepProperties?.[element.id?.replace(/-/g, '_')] ||
      extracted?.stepProperties?.[element.id?.split('_').pop()] ||
      {};
    // Also try direct XML extraction if still empty
    let xmlDirectProps = {};
    if (!Object.keys(extraProps).length && !Object.keys(element.props || {}).length && extracted?.bpmn) {
      xmlDirectProps = extractIflPropsFromXml(extracted.bpmn, element.id);
    }
    const merged = { ...element, props: { ...extraProps, ...xmlDirectProps, ...(element.props || {}) } };
    const cleanProps = {};
    for (const [k, val] of Object.entries(merged.props)) {
      if (k && val !== undefined && val !== null && k !== "undefined" && String(val) !== "undefined" && String(val).trim()) {
        cleanProps[k] = val;
      }
    }
    merged.props = cleanProps;
    // Auto-link script
    merged.linkedScript = findLinkedScript(merged);
    if (selectedStep?.id === merged.id && bottomPanel) { setBottomPanel(false); setSelectedStep(null); }
    else { setSelectedStep(merged); setBottomPanel(true); setBottomTab("details"); }
  }, [selectedStep, bottomPanel, extracted, findLinkedScript]);

  if (loading) return <div style={{ padding: 40, color: "var(--text-2)" }}>{t("paket.laedt")}</div>;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // ============================================================
  // Pipeline Konzept Erkennung
  // ============================================================
  const detectPipelinePackage = (pkgs, arts) => {
    const stepPattern = /Pipeline v2 (Generic Step|Template|Integrated Messaging Runtime)/i;
    const scriptCollection = "Pipeline v2 - Script Collection";

    const allArts = arts || [];
    // Only detect when actual Pipeline generic iFlows are deployed - don't trigger on package name alone
    const hasPipelineArt = allArts.some(a => stepPattern.test(a.Name||"") || (a.Name||"").includes(scriptCollection));
    if (!hasPipelineArt) return null;

    const hasIntegratedRuntime = allArts.some(a => /Integrated Messaging Runtime/i.test(a.Name||""));

    const steps = {
      inboundIdoc: allArts.some(a => /Step01.*Idoc|Inbound.*Idoc/i.test(a.Name||"")),
      inboundXI: allArts.some(a => /Step01.*XI|Inbound.*XI/i.test(a.Name||"")),
      integratedRuntime: allArts.some(a => /Integrated Messaging Runtime/i.test(a.Name||"")),
      inboundProcessing: allArts.some(a => /Step02.*Inbound Processing/i.test(a.Name||"")),
      receiverDet: allArts.some(a => /Step04|Receiver Determination/i.test(a.Name||"")),
      interfaceDet: allArts.some(a => /Step05|Interface Determination/i.test(a.Name||"")),
      outbound: allArts.some(a => /Step06|Step08|Outbound Processing/i.test(a.Name||"")),
      scriptCollection: allArts.some(a => (a.Name||"").includes(scriptCollection)),
    };

    // Count only iFlows that contain "Pipeline" in name but are NOT generic steps/script collection
    const scenarioCount = allArts.filter(a =>
      /Pipeline/i.test(a.Name||"") && !stepPattern.test(a.Name||"") && !(a.Name||"").includes(scriptCollection)
    ).length;

    return {
      isPipeline: true,
      type: hasIntegratedRuntime ? "integrated" : "decoupled",
      steps,
      scenarioCount
    };
  };

  // Pipeline label for a single iFlow artifact - stage-specific color + text
  const getFlowPipelineLabel = (flow) => {
    if (!instance.hasPipelineConcept) return null;
    const role = _classifyPipelineRole(flow.Name || "");
    if (role === "normal") return null;
    const ROLE_META = {
      script:  { text: "Script Collection", color: "#7c6fe0" },  // purple
      generic: { text: "Generic Step",      color: "#7c6fe0" },  // purple
      rai:     { text: "Receiver Adapter",  color: "var(--danger)" },  // red - consumer
      sai:     { text: "Sender Adapter",    color: "var(--accent)" },  // blue - producer
      mapping: { text: "Mapping",           color: "var(--success)" }, // green
      other:   { text: "Pipeline",          color: "#7c6fe0" },
    };
    return ROLE_META[role] || { text: "Pipeline", color: "#7c6fe0" };
  };

  // SAP Standard Pipeline Package names (whitelist)
  const SAP_PIPELINE_PKG_PATTERNS = [
    "cloud integration pipeline - generic inbound",
    "cloud integration pipeline - pipelines",
    "cloud integration pipeline - outbound",
    "cloud integration pipeline - templates",
    "cloud integration pipeline - script collection",
    "process integration pipeline - generic integration flows", // Legacy SAP Package
  ];
  const isSapStandardPipelinePkg = (pkg) => {
    const name = (pkg.Name || "").toLowerCase();
    return SAP_PIPELINE_PKG_PATTERNS.some(p => name.includes(p));
  };

  // Pipeline label for a package row - null if not pipeline-relevant
  const getPkgPipelineLabel = (pkg) => {
    if (!instance.hasPipelineConcept) return null;
    if (isSapStandardPipelinePkg(pkg)) return { text: "SAP Standard", color: "var(--accent)" };
    const arts = pkgArtsByPkg[pkg.Id];
    if (!arts) return null;
    const det = detectPipelinePackage(null, arts);
    if (!det) return null;
    if (det.type === "integrated") return { text: "Pipeline Foundation", color: "#7c6fe0" };
    return { text: "Pipeline", color: "#7c6fe0" };
  };

  // === Package List with Filter + Search ===
  if (!selPkg) {
    const visiblePkgs = packages.filter(p => !hiddenPkgs.includes(p.Id));
    const searchedPkgs = pkgSearch ? visiblePkgs.filter(p => p.Name?.toLowerCase().includes(pkgSearch.toLowerCase()) || p.Id?.toLowerCase().includes(pkgSearch.toLowerCase())) : visiblePkgs;
    return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: FONT, color: "var(--iio-text-1)" }}>{t("paket.titel")} <span style={{ fontSize: 12, fontWeight: 400, color: "var(--iio-text-3)" }}>{t("paket.zaehler", { sichtbar: visiblePkgs.length, gesamt: packages.length })}</span></div>
      </div>
      <div className="iio-content-filter-bar" style={{ marginBottom: 16 }}>
        <div className="iio-content-filter-bar__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={pkgSearch} onChange={e => setPkgSearch(e.target.value)} placeholder={t("paket.suche")} />
        </div>
        <div className="iio-content-filter-bar__sep" />
        <div className="iio-content-filter-bar__filter">
          <button type="button" onClick={() => setShowPkgFilter(!showPkgFilter)} className={`iio-content-filter-bar__trigger${showPkgFilter ? " is-active" : ""}`}>
            <FilterListIcon sx={{fontSize:14}}/>
            <span>{t("paket.filter")}</span>
            {hiddenPkgs.length > 0 && <span className="iio-content-filter-bar__badge">{hiddenPkgs.length}</span>}
          </button>
          {showPkgFilter && <div className="iio-content-filter-popover" role="dialog" aria-label={t("paket.filterDialog")}>
            <div className="iio-content-filter-popover__eyebrow">{t("paket.filterEyebrow")}</div>
            <div className="iio-content-filter-popover__actions">
              <MuiButton onClick={() => { setHiddenPkgs([]); saveFilters([], hiddenFlowsByPkg); }} variant="outlined" size="small">{t("paket.alleAnzeigen")}</MuiButton>
              <MuiButton onClick={() => { const all = packages.map(p => p.Id); setHiddenPkgs(all); saveFilters(all, hiddenFlowsByPkg); }} variant="outlined" size="small">{t("paket.alleAusblenden")}</MuiButton>
            </div>
            <div className="iio-content-filter-popover__list">
              {packages.map(p => (
                <label key={p.Id} className="iio-content-filter-popover__item">
                  <input type="checkbox" checked={!hiddenPkgs.includes(p.Id)} onChange={() => togglePkg(p.Id)} />
                  <span>{p.Name}</span>
                </label>
              ))}
            </div>
          </div>}
        </div>
      </div>
      {error && <div style={{ padding: "10px 14px", borderRadius: 4, background: "var(--iio-bg-2)", border: "1px solid var(--iio-critical)", color: "var(--iio-critical)", fontSize: 13, marginBottom: 12 }}>
        {/403|status code 403/i.test(error)
          ? <Trans t={t} i18nKey="paket.fehler403" />
          : error}
      </div>}


      {/* Package Table - Konzept D. Transport-Einstieg ENTFERNT: Transport laeuft ausschliesslich
          ueber die dedizierte /transport-App. Der Paket-Transport-Wizard-Code bleibt (dead)
          erhalten, wird aber nicht mehr getriggert. */}
      <div style={{ borderRadius: 8, border: "1px solid var(--iio-border)", overflow: "hidden", background: "var(--iio-bg-2)" }}>
        {/* Table Header */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 90px", padding: "10px 20px", background: "var(--iio-bg-3)", fontSize: 10, fontWeight: 500, color: "var(--iio-text-3)", textTransform: "uppercase", letterSpacing: ".14em", borderBottom: "1px solid var(--iio-border-subtle)" }}>
          <span>{t("paket.spaltePaket")}</span><span>{t("paket.spalteArtefakte")}</span><span>{t("paket.spalteVersion")}</span>
        </div>
        {searchedPkgs.map(pkg => (
          <div key={pkg.Id} onClick={() => { selectPkg(pkg); setPkgSearch(""); }} style={{ display: "grid", gridTemplateColumns: "1fr 110px 90px", padding: "14px 20px", alignItems: "center", background: "transparent", borderTop: "1px solid var(--iio-border-subtle)", cursor: "pointer", transition: "background .12s" }}
            onMouseEnter={e=>{e.currentTarget.style.background="var(--iio-bg-1)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
              <div className="iio-pkg-ico">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 9.4l-9-5.19"/><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--iio-text-1)" }}>{pkg.Name}</div>
                <div style={{ fontSize: 11, color: "var(--iio-text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>{pkg.Description ? stripHtml(pkg.Description).substring(0, 60) : pkg.Id}</div>
              </div>
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--iio-text-1)" }}>{pkgArtifactCounts[pkg.Id]!=null?pkgArtifactCounts[pkg.Id]:"…"}</span>
            <span style={{ fontSize: 13, color: "var(--iio-text-2)" }}>{pkg.Version||"1.0"}</span>
          </div>
        ))}
      </div>
      {searchedPkgs.length === 0 && !error && <div style={{ padding: 40, textAlign: "center", color: "var(--text-3)" }}>{pkgSearch ? t("paket.leerSuche") : hiddenPkgs.length > 0 ? t("paket.leerAusgeblendet") : t("paket.leer")}</div>}

      {/* Package Transport Wizard Modal */}
      {pkgTransport&&<MuiDialog open={!!pkgTransport} onClose={()=>setPkgTransport(null)} maxWidth="md" fullWidth PaperProps={{sx:{maxHeight:"90vh"}}}>
        <DialogContent sx={{p:3}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div>
              <div style={{fontSize:18,fontWeight:700,color:"var(--text-1)",fontFamily:FONT}}>{t("transport.titel")}</div>
              <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{pkgTransport.Name} ({pkgTransport.Id})</div>
            </div>
            <MuiIconButton size="small" onClick={()=>setPkgTransport(null)} sx={{color:"text.secondary"}}><CloseIcon fontSize="small"/></MuiIconButton>
          </div>
          <div style={{display:"flex",gap:4,marginBottom:20}}>
            {[t("transport.schrittZiel"),t("transport.schrittAbgleich"),t("transport.schrittOptionen"),t("transport.schrittTransport")].map((s,i)=>(
              <div key={i} style={{flex:1,padding:"6px 0",textAlign:"center",fontSize:11,fontWeight:pkgTrStep===i?600:400,
                color:pkgTrStep===i?v("accent"):v("text-3"),borderBottom:`2px solid ${pkgTrStep===i?v("accent"):pkgTrStep>i?v("success"):v("border")}`,
                fontFamily:FONT}}>{i+1}. {s}</div>
            ))}
          </div>
          {pkgTrStep===0&&<div>
            {/* Mode selection */}
            <div style={{display:"flex",gap:12,marginBottom:16}}>
              <button onClick={()=>setPkgTrMode("single")} style={{flex:1,padding:"12px",borderRadius:8,
                border:`1px solid ${pkgTrMode==="single"?v("accent"):v("border")}`,
                background:pkgTrMode==="single"?`${v("accent")}12`:v("surface-2"),color:"var(--text-1)",fontSize:13,cursor:"pointer",textAlign:"center"}}>
                <div style={{fontWeight:600}}>{t("transport.modusEinzelTitel")}</div>
                <div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>{t("transport.modusEinzelDesc")}</div>
              </button>
              <button onClick={()=>setPkgTrMode("multi")} disabled={allInstances.length<2}
                style={{flex:1,padding:"12px",borderRadius:8,
                border:`1px solid ${pkgTrMode==="multi"?v("accent"):v("border")}`,
                background:pkgTrMode==="multi"?`${v("accent")}12`:v("surface-2"),color:allInstances.length<2?v("text-3"):v("text-1"),
                fontSize:13,cursor:allInstances.length<2?"not-allowed":"pointer",textAlign:"center",opacity:allInstances.length<2?0.5:1}}>
                <div style={{fontWeight:600}}>{t("transport.modusMultiTitel")}</div>
                <div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>{t("transport.modusMultiDesc")}</div>
              </button>
            </div>
            {allInstances.length<2&&pkgTrMode!=="single"&&<div style={{padding:10,background:v("surface-2"),border:`1px solid ${v("warning")}`,borderRadius:8,fontSize:11,color:v("warning"),marginBottom:12}}>
              {t("transport.multiBrauchtZwei")}</div>}

            {/* Single mode: select instance + werke */}
            {pkgTrMode==="single"&&<div>
              <div style={{fontSize:12,color:"var(--text-2)",marginBottom:10}}>{t("transport.zielWaehlen")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {allInstances.map(ii=>{
                  const isSelected=pkgTrTarget===ii.id;
                  const werke=ii.werke||[];
                  return <div key={ii.id}>
                    <button onClick={()=>{
                      setPkgTrTarget(ii.id);setPreflightChecks(null);
                      if(werke.length>0){setPkgTrWerke(p=>({...p,[ii.id]:werke.map(w=>({...w,selected:true}))}));}
                      else{setPkgTrWerke(p=>({...p,[ii.id]:[]}));}
                      const defaultRuntimes=[{name:"Cloud Integration",locationId:null,selected:true}];
                      if(ii.runtimeLocations?.length){defaultRuntimes.push(...ii.runtimeLocations.map(rl=>({name:rl.name,locationId:rl.locationId,selected:false})));}
                      setPkgTrDeployRuntimes(defaultRuntimes);
                    }} style={{
                      width:"100%",padding:"10px 14px",borderRadius:8,textAlign:"left",
                      border:`1px solid ${isSelected?"var(--accent)":"var(--border)"}`,
                      background:isSelected?"var(--surface-1)":"var(--surface-0)",
                      color:"var(--text-1)",fontSize:13,cursor:"pointer",fontFamily:FONT
                    }}>
                      <div style={{fontWeight:500}}>{ii.name} <span style={{fontSize:10,color:"var(--text-3)",marginLeft:4}}>{ii.spaceName}</span></div>
                      {werke.length>0&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{t("transport.werkeKonfiguriert",{count:werke.length})}</div>}
                    </button>
                    {/* Werke checkboxes when selected */}
                    {isSelected&&werke.length>0&&<div style={{marginLeft:20,marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                      {(pkgTrWerke[ii.id]||werke.map(w=>({...w,selected:true}))).map((w,wi)=>(
                        <label key={wi} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 10px",borderRadius:6,
                          border:`1px solid ${w.selected?v("accent"):"var(--border)"}`,background:w.selected?v("status-total-bg"):"transparent",cursor:"pointer",fontSize:12}}>
                          <input type="checkbox" checked={w.selected} onChange={e=>{
                            const wk=[...(pkgTrWerke[ii.id]||werke.map(x=>({...x,selected:true})))];
                            wk[wi]={...wk[wi],selected:e.target.checked};
                            setPkgTrWerke(p=>({...p,[ii.id]:wk}));
                          }} style={{width:13,height:13,accentColor:"var(--accent)"}}/>
                          <span style={{color:"var(--text-1)",fontWeight:500}}>{w.name}</span>
                          <span style={{color:"var(--text-3)",fontFamily:FONT,fontSize:11}}>_{w.kennung}</span>
                        </label>
                      ))}
                    </div>}
                  </div>;
                })}
              </div>
              {pkgTrTarget&&<div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <button onClick={()=>{setPkgTrStep(1);pkgTrRunPrecheck(pkgTrTarget);}} style={{
                  padding:"8px 20px",borderRadius:8,border:"none",fontSize:13,fontWeight:600,cursor:"pointer",
                  background:"var(--accent)",color:"#fff"}}>{t("gemeinsam:aktion.weiter")}</button>
              </div>}
            </div>}

            {/* Multi mode: checkboxes + werke + confirm */}
            {pkgTrMode==="multi"&&<div>
              <div style={{fontSize:12,color:"var(--text-2)",marginBottom:10}}>{t("transport.zieleUndWerkeWaehlen")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {allInstances.map(ii=>{
                  const isSelected=pkgTrTargets.includes(ii.id);
                  const werke=ii.werke||[];
                  return <div key={ii.id}>
                    <label style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,
                      border:`1px solid ${isSelected?"var(--accent)":"var(--border)"}`,background:isSelected?"var(--surface-1)":"var(--surface-0)",cursor:"pointer"}}>
                      <input type="checkbox" checked={isSelected}
                        onChange={e=>{
                          if(e.target.checked){
                            setPkgTrTargets(p=>[...p,ii.id]);
                            if(werke.length>0)setPkgTrWerke(p=>({...p,[ii.id]:werke.map(w=>({...w,selected:true}))}));
                          }else{
                            setPkgTrTargets(p=>p.filter(x=>x!==ii.id));
                            setPkgTrWerke(p=>{const n={...p};delete n[ii.id];return n;});
                          }
                        }}
                        style={{width:14,height:14,accentColor:"var(--accent)"}}/>
                      <div>
                        <div style={{fontSize:13,color:"var(--text-1)",fontWeight:500}}>{ii.name} <span style={{fontSize:10,color:"var(--text-3)"}}>{ii.spaceName}</span></div>
                        {werke.length>0&&<div style={{fontSize:11,color:"var(--text-3)"}}>{t("transport.werke",{count:werke.length})}</div>}
                      </div>
                    </label>
                    {/* Werke when instance selected */}
                    {isSelected&&werke.length>0&&<div style={{marginLeft:32,marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
                      {(pkgTrWerke[ii.id]||werke.map(w=>({...w,selected:true}))).map((w,wi)=>(
                        <label key={wi} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 10px",borderRadius:6,
                          border:`1px solid ${w.selected?v("accent"):"var(--border)"}`,background:w.selected?v("status-total-bg"):"transparent",cursor:"pointer",fontSize:12}}>
                          <input type="checkbox" checked={w.selected} onChange={e=>{
                            const wk=[...(pkgTrWerke[ii.id]||werke.map(x=>({...x,selected:true})))];
                            wk[wi]={...wk[wi],selected:e.target.checked};
                            setPkgTrWerke(p=>({...p,[ii.id]:wk}));
                          }} style={{width:13,height:13,accentColor:"var(--accent)"}}/>
                          <span style={{color:"var(--text-1)",fontWeight:500}}>{w.name}</span>
                          <span style={{color:"var(--text-3)",fontFamily:FONT,fontSize:11}}>_{w.kennung}</span>
                        </label>
                      ))}
                    </div>}
                  </div>;
                })}
              </div>
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:16}}>
                <button onClick={()=>{setPkgTrStep(1);pkgTrRunMultiPrechecks();}} disabled={pkgTrTargets.length===0}
                  style={{padding:"8px 20px",borderRadius:8,border:"none",fontSize:13,fontWeight:600,cursor:pkgTrTargets.length?"pointer":"not-allowed",
                  background:pkgTrTargets.length?v("accent"):v("surface-3"),color:"#fff",
                  opacity:pkgTrTargets.length?1:0.4}}>{t("transport.weiterInstanzen",{count:pkgTrTargets.length})}</button>
              </div>
            </div>}

            {allInstances.length===0&&<div style={{padding:20,color:"var(--text-3)",fontSize:13}}>{t("transport.ladeInstanzen")}</div>}
          </div>}
          {pkgTrStep===1&&(()=>{
            const hasWerkeChecks=Object.keys(pkgTrMultiChecks).length>0;
            const hasAnySelected=hasWerkeChecks
              ?Object.values(pkgTrMultiChecks).some(c=>c?.comparison?.some(a=>a.selected))
              :pkgTrComparison?.comparison?.some(a=>a.selected);
            const checkKeys=Object.keys(pkgTrMultiChecks);
            const instGroups={};
            checkKeys.forEach(k=>{const parts=k.split("__");const iId=parts[0];if(!instGroups[iId])instGroups[iId]=[];instGroups[iId].push(k);});
            const instIds=Object.keys(instGroups);
            const activeInstId=pkgTrActiveTab?.split("__")[0]||instIds[0]||"";
            const activeWerkKeys=instGroups[activeInstId]||[];
            

            const renderArtRow=(art,checkKey,kennung)=>{
              const displayName=kennung?`${art.Name}_${kennung}`:art.Name;
              const displayId=kennung?`${art.Id}${kennung}`:art.Id;
              return <Fragment key={art.Id}><div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px 70px 40px",padding:"8px 12px",
                borderTop:"1px solid var(--border)",alignItems:"center",background:art.selected?v("status-total-bg"):"transparent",
                opacity:art.draft?0.4:1}}>
                <input type="checkbox" checked={art.selected} onChange={()=>{
                  if(art.draft)return;
                  if(checkKey){
                    setPkgTrMultiChecks(prev=>{const c={...prev};if(!c[checkKey]?.comparison)return prev;
                      c[checkKey]={...c[checkKey],comparison:c[checkKey].comparison.map(a=>a.Id===art.Id?{...a,selected:!a.selected}:a)};return c;});
                  }else{pkgTrToggleArtifact(art.Id);}
                }} disabled={art.draft} style={{width:14,height:14,accentColor:"var(--accent)",cursor:art.draft?"not-allowed":"pointer"}}/>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",minWidth:0}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:13,color:art.draft?"var(--text-3)":"var(--text-1)",fontWeight:500}}>{displayName}</div>
                    <div style={{fontSize:10,color:"var(--text-3)",fontFamily:FONT}}>{displayId}</div>
                  </div>
                  {art.status==="update"&&!art.draft&&<button onClick={(e)=>{e.stopPropagation();
                    const tgtId=checkKey?checkKey.split("__")[0]:getActiveTargetId();
                    setPkgTrBpmnCompare({artifactId:art.Id,targetArtifactId:art.suffixedId||displayId,artifactName:displayName,
                      sourceInstanceId:instance.id,targetInstanceId:tgtId,sourcePackageId:pkgTransport?.Id,sourceVersion:art.Version,targetVersion:art.targetVersion});
                  }} style={{background:"none",border:"none",cursor:"pointer",padding:"2px 8px",fontSize:10,fontFamily:FONT,
                    color:v("accent"),fontWeight:500,whiteSpace:"nowrap",borderRadius:4,
                    transition:"background .15s",display:"flex",alignItems:"center",gap:4}}
                    onMouseEnter={e=>e.currentTarget.style.background=v("accent-glow")}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3v18M3 12h18"/></svg>
                    {t("transport.aenderungenEinsehen")}
                  </button>}
                </div>
                <div style={{fontSize:11,color:"var(--text-2)"}}>{(art.Type||"").replace("Integration Flow","iFlow")}</div>
                <div style={{fontSize:11,color:art.draft?v("danger"):"var(--text-1)",fontFamily:FONT}}>{art.draft?t("transport.draft"):art.Version||"-"}</div>
                <div style={{fontSize:11,fontFamily:FONT}}>
                  {art.status==="new"&&<span style={{color:art.draft?"var(--text-3)":v("success")}}>{t("transport.statusNeu")}</span>}
                  {art.status==="update"&&<span style={{color:v("accent")}}>{art.targetVersion}</span>}
                  {art.status==="identical"&&<span style={{color:"var(--text-3)"}}>{art.targetVersion}</span>}
                </div>
                <div>{art.selected&&!art.draft&&(()=>{
                  const ck=getConfigCacheKey(art.Id,checkKey);
                  const cfgs=pkgTrConfigs[ck];
                  const isNew=art.status==="new";
                  const needsConfig=isNew&&(!cfgs||cfgs.some(c=>!c.newValue&&c.required===true));
                  const hasChanges=cfgs?.some(c=>c.newValue&&c.newValue!==c.defaultValue);
                  return <button onClick={(e)=>{e.stopPropagation();
                    if(pkgTrConfigOpen===ck){setPkgTrConfigOpen(null);}
                    else{loadArtifactConfigs(art.Id,checkKey,kennung);setPkgTrConfigOpen(ck);}
                  }} title={needsConfig?t("transport.paramPflichtTitel"):t("transport.paramTitel")} style={{
                    background:"none",border:"none",cursor:"pointer",padding:2,
                    color:needsConfig?v("danger"):pkgTrConfigOpen===ck?"var(--accent)":hasChanges?v("accent"):"var(--text-3)",
                    animation:needsConfig?"pulse 1.5s infinite":"none"}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={needsConfig?"2.5":"1.5"} strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  </button>;
                })()}</div>
              </div>
              {/* Draft-Release-Streifen (Version releasen) */}
              {renderDraftReleaseStrip(art)}
              {/* Inline config editor */}
              {pkgTrConfigOpen===getConfigCacheKey(art.Id,checkKey)&&(pkgTrConfigs[getConfigCacheKey(art.Id,checkKey)]?(()=>{
                const cfgKey=getConfigCacheKey(art.Id,checkKey);const cfgs=pkgTrConfigs[cfgKey]||[];
                const grps={sender:cfgs.filter(c=>c.group==="sender"),receiver:cfgs.filter(c=>c.group==="receiver"),other:cfgs.filter(c=>c.group==="other"||!c.group)};
                const ctabs=[];
                if(grps.sender.length)ctabs.push({key:"sender",label:t("transport.tabSender"),items:grps.sender,adapter:grps.sender[0]?.adapterType||""});
                if(grps.receiver.length)ctabs.push({key:"receiver",label:t("transport.tabReceiver"),items:grps.receiver,adapter:grps.receiver[0]?.adapterType||""});
                if(grps.other.length)ctabs.push({key:"other",label:ctabs.length?t("transport.tabSonstige"):t("transport.tabParameter"),items:grps.other,adapter:""});
                if(!ctabs.length)return null;
                const curCfgTab=ctabs.find(t=>t.key===(window._cfgTab||ctabs[0]?.key))||ctabs[0];
                return <div style={{gridColumn:"1/-1",background:"var(--surface-1)",borderTop:"1px solid var(--border)",padding:0}}>
                  {ctabs.length>1&&<div style={{display:"flex",gap:0,borderBottom:"1px solid var(--border)"}}>
                    {ctabs.map(t=><button key={t.key} onClick={()=>{window._cfgTab=t.key;setPkgTrConfigOpen(null);setTimeout(()=>setPkgTrConfigOpen(cfgKey),0);}} style={{
                      padding:"8px 16px",border:"none",fontSize:11,cursor:"pointer",fontWeight:curCfgTab.key===t.key?600:400,
                      background:"transparent",color:curCfgTab.key===t.key?"var(--accent)":"var(--text-3)",
                      borderBottom:curCfgTab.key===t.key?"2px solid var(--accent)":"2px solid transparent"}}>{t.label}</button>)}
                  </div>}
                  {curCfgTab.adapter&&<div style={{padding:"6px 14px",fontSize:10,color:"var(--text-3)",borderBottom:"1px solid var(--border)"}}>
                    {t("transport.adapter")} <span style={{color:"var(--text-2)",fontWeight:600}}>{curCfgTab.adapter}</span></div>}
                  {curCfgTab.items.map(c=>{
                    const isEmpty=!c.newValue;
                    const isFromTarget=c.fromTarget&&c.newValue===c.targetValue&&!!c.targetValue;
                    const hasError=isEmpty&&c.required===true;
                    return <div key={c.ParameterKey} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderBottom:"1px solid var(--border)",fontSize:12,
                      background:hasError?"rgba(239,68,68,.04)":"transparent"}}>
                      <span style={{color:hasError?v("danger"):"var(--text-2)",fontWeight:500,minWidth:110}}>{c.fieldLabel||c.ParameterKey}</span>
                      <span style={{fontSize:9,color:"var(--text-3)",fontFamily:MONO,minWidth:80}}>{c.ParameterKey}</span>
                      <div style={{flex:1,position:"relative"}}>
                        <input value={c.newValue} onChange={e=>{updateArtifactConfig(cfgKey,c.ParameterKey,e.target.value);}}
                          placeholder={isEmpty?c.defaultValue||"":""}
                          style={{width:"100%",boxSizing:"border-box",
                            background:isFromTarget?"var(--config-target-bg, rgba(59,142,216,.08))":"var(--surface-0)",
                            border:`1px solid ${hasError?v("danger"):isFromTarget?"var(--accent)":"var(--border)"}`,
                            borderRadius:4,padding:"4px 8px",color:"var(--text-1)",fontSize:11,outline:"none",fontFamily:MONO}}/>
                      </div>
                      <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,whiteSpace:"nowrap",
                        background:hasError?v("status-fail-bg"):isFromTarget?v("status-total-bg"):c.newValue&&c.newValue!==c.defaultValue?v("surface-2"):"transparent",
                        color:hasError?v("danger"):isFromTarget?v("accent"):c.newValue&&c.newValue!==c.defaultValue?v("warning"):"var(--text-3)"}}>
                        {hasError?t("transport.badgePflichtfeld"):isFromTarget?t("transport.badgeZiel"):c.newValue&&c.newValue!==c.defaultValue?t("transport.badgeGeaendert"):isEmpty?t("transport.badgeLeer"):t("transport.badgeStandard")}</span>
                    </div>;}
                  )}
                  {pkgTrConfigLoading&&<div style={{padding:8,color:"var(--text-3)",fontSize:10,textAlign:"center"}}>{t("transport.ladeKurz")}</div>}
                </div>;})():<div style={{gridColumn:"1/-1",padding:16,textAlign:"center",color:"var(--text-3)",fontSize:12,background:"var(--surface-1)",borderTop:"1px solid var(--border)"}}>{t("transport.ladeParameter")}</div>)}
              </Fragment>;
            };

            return <div>
            {/* Instance-only tabs */}
            {hasWerkeChecks&&instIds.length>0&&<div style={{display:"flex",gap:2,marginBottom:14,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
              {instIds.map(iId=>{
                const inst=allInstances.find(i=>i.id===iId);
                const keys=instGroups[iId]||[];
                const totalArts=keys.reduce((s,k)=>{const c=pkgTrMultiChecks[k];return s+(c?.comparison?.length||0);},0);
                return <button key={iId} onClick={()=>{setPkgTrActiveTab(keys[0]||iId);setPkgTrConfigOpen(null);}} style={{
                  padding:"8px 16px",borderRadius:"8px 8px 0 0",border:"none",fontSize:12,cursor:"pointer",
                  background:activeInstId===iId?"var(--surface-2)":"transparent",
                  color:activeInstId===iId?"var(--text-1)":"var(--text-3)",
                  fontWeight:activeInstId===iId?600:400,
                  borderBottom:activeInstId===iId?"2px solid var(--accent)":"2px solid transparent",
                  whiteSpace:"nowrap"
                }}>{inst?.name||iId} <span style={{fontSize:9,marginLeft:4,opacity:.6}}>{totalArts}</span></button>;
              })}
            </div>}

            {pkgTrLoading&&<div style={{padding:40,textAlign:"center",color:"var(--text-3)"}}>{t("transport.vergleiche")}</div>}

            {/* Non-werke: classic single view */}
            {!hasWerkeChecks&&!pkgTrLoading&&pkgTrComparison&&!pkgTrComparison.error&&<div>
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <div style={{padding:"8px 14px",borderRadius:8,background:v("status-ok-bg"),border:`1px solid ${v("status-ok-border")}`,fontSize:12,color:v("success")}}>{t("transport.zaehlerNeu",{count:pkgTrComparison?.newCount})}</div>
                <div style={{padding:"8px 14px",borderRadius:8,background:"var(--surface-2)",border:`1px solid ${v("accent")}40`,fontSize:12,color:v("accent")}}>{t("transport.zaehlerUpdate",{count:pkgTrComparison?.updateCount})}</div>
                <div style={{padding:"8px 14px",borderRadius:8,background:"var(--surface-2)",border:"1px solid var(--border)",fontSize:12,color:"var(--text-3)"}}>{t("transport.zaehlerIdentisch",{count:pkgTrComparison?.identicalCount})}</div>
                {pkgTrComparison?.orphanCount>0&&<div style={{padding:"8px 14px",borderRadius:8,background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,fontSize:12,color:v("danger")}}>{t("transport.zaehlerVeraltet",{count:pkgTrComparison.orphanCount})}</div>}
              </div>
              <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px 70px 40px",padding:"8px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                  <div></div><div>{t("transport.spalteArtifact")}</div><div>{t("transport.spalteTyp")}</div><div>{t("transport.spalteQuelle")}</div><div>{t("transport.spalteZiel")}</div><div></div>
                </div>
                {pkgTrComparison?.comparison?.map(art=>renderArtRow(art,null,null))}
              </div>

              {/* Orphan Detection - Artefakte nur im Ziel */}
              {pkgTrComparison?.orphans?.length>0&&<div style={{marginTop:16}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:13,fontWeight:600,color:v("danger"),cursor:"help"}} title={t("transport.veraltetHilfe")}>{t("transport.veraltetTitel")}</span>
                  <span style={{fontSize:11,color:v("danger"),opacity:.7}}>{t("transport.veraltetAnzahl",{count:pkgTrComparison.orphans.length})}</span>
                </div>
                <div style={{fontSize:11,color:"var(--text-3)",marginBottom:8,lineHeight:1.5}}>
                  {t("transport.veraltetHilfe")}
                </div>
                <div style={{display:"flex",gap:6,marginBottom:6}}>
                  <button onClick={()=>{const sel={};pkgTrComparison.orphans.forEach(o=>{sel[o.Id]=true;});setPkgTrOrphanSel(prev=>({...prev,...sel}));}}
                    style={{padding:"2px 8px",border:"1px solid var(--border)",borderRadius:4,background:"transparent",color:"var(--text-3)",fontSize:10,cursor:"pointer"}}>{t("transport.alle")}</button>
                  <button onClick={()=>{const sel={};pkgTrComparison.orphans.forEach(o=>{sel[o.Id]=false;});setPkgTrOrphanSel(prev=>({...prev,...sel}));}}
                    style={{padding:"2px 8px",border:"1px solid var(--border)",borderRadius:4,background:"transparent",color:"var(--text-3)",fontSize:10,cursor:"pointer"}}>{t("transport.keine")}</button>
                </div>
                <div style={{border:`1px solid ${v("danger")}30`,borderRadius:8,overflow:"hidden",background:`${v("danger")}06`}}>
                  <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px",padding:"8px 12px",background:`${v("danger")}0a`,fontSize:10,color:v("danger"),fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                    <div></div><div>{t("transport.spalteArtifact")}</div><div>{t("transport.spalteTyp")}</div><div>{t("transport.spalteStatus")}</div>
                  </div>
                  {pkgTrComparison.orphans.map(orphan=>{
                    const isSelected=pkgTrOrphanSel[orphan.Id]!==false;
                    return <label key={orphan.Id} style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px",padding:"8px 12px",borderTop:`1px solid ${v("danger")}15`,cursor:"pointer",alignItems:"center",
                      background:isSelected?`${v("danger")}08`:"transparent"}}>
                      <input type="checkbox" checked={isSelected} onChange={()=>setPkgTrOrphanSel(prev=>({...prev,[orphan.Id]:!isSelected}))}
                        style={{width:14,height:14,accentColor:v("danger")}}/>
                      <div><div style={{fontSize:12,color:"var(--text-1)"}}>{orphan.Name||orphan.Id}</div><div style={{fontSize:10,color:"var(--text-3)"}}>{orphan.Id}</div></div>
                      <div style={{fontSize:11,color:"var(--text-3)"}}>{orphan.Type||"iFlow"}</div>
                      <div style={{fontSize:10,color:orphan.deployed?v("success"):v("danger")}}>{orphan.deployed?t("transport.zustandDeployed"):t("transport.zustandGestoppt")}</div>
                    </label>;
                  })}
                </div>
                {pkgTrComparison.orphans.filter(o=>pkgTrOrphanSel[o.Id]!==false).length>0&&<div style={{padding:"8px 12px",borderRadius:8,background:`${v("danger")}08`,border:`1px solid ${v("danger")}20`,fontSize:11,color:v("danger"),lineHeight:1.5,marginTop:8}}>
                  {t("transport.veraltetHinweis",{count:pkgTrComparison.orphans.filter(o=>pkgTrOrphanSel[o.Id]!==false).length})}
                </div>}
              </div>}

              {renderGlobalRefsSection()}
              {renderPipelineChecksSection()}


            </div>}
            {!hasWerkeChecks&&pkgTrComparison?.error&&<div style={{padding:16,background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,borderRadius:8,color:v("danger"),fontSize:13}}>{pkgTrComparison.error}</div>}

            {/* Werke accordion for active instance */}
            {hasWerkeChecks&&!pkgTrLoading&&activeWerkKeys.length>0&&<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {activeWerkKeys.map(werkKey=>{
                const chk=pkgTrMultiChecks[werkKey];
                const kennung=werkKey.split("__")[1]||null;
                const werkName=chk?.werk?.name||kennung||t("transport.werkStandard");
                const isExpanded=werkExpanded[werkKey]!==false;
                const artCount=chk?.comparison?.length||0;
                const selectedCount=chk?.comparison?.filter(a=>a.selected).length||0;
                return <div key={werkKey} style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                  <div onClick={()=>{setWerkExpanded(prev=>({...prev,[werkKey]:!isExpanded}));}}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--surface-1)",cursor:"pointer",userSelect:"none"}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                      style={{transition:"transform .15s",transform:isExpanded?"rotate(90deg)":"rotate(0deg)",flexShrink:0}}>
                      <polyline points="9 18 15 12 9 6"/></svg>
                    <div style={{flex:1}}>
                      <span style={{fontSize:13,fontWeight:600,color:"var(--text-1)"}}>{werkName}</span>
                      {kennung&&<span style={{marginLeft:6,fontSize:11,fontFamily:FONT,color:"var(--accent)"}}>_{kennung}</span>}
                    </div>
                    <div style={{display:"flex",gap:8,fontSize:11}}>
                      {chk?.newCount>0&&<span style={{color:v("success")}}>{t("transport.kurzNeu",{count:chk.newCount})}</span>}
                      {chk?.updateCount>0&&<span style={{color:v("accent")}}>{t("transport.kurzUpdate",{count:chk.updateCount})}</span>}
                      {chk?.identicalCount>0&&<span style={{color:"var(--text-3)"}}>{t("transport.kurzIdentisch",{count:chk.identicalCount})}</span>}
                      {chk?.orphanCount>0&&<span style={{color:v("danger"),cursor:"help"}} title={t("transport.veraltetHilfe")}>{t("transport.zaehlerVeraltet",{count:chk.orphanCount})}</span>}
                    </div>
                    <span style={{fontSize:10,color:"var(--text-3)"}}>{selectedCount}/{artCount}</span>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,
                      background:chk?.targetExists?v("status-total-bg"):v("status-warn-bg"),
                      border:`1px solid ${chk?.targetExists?v("status-total-border"):v("status-warn-border")}`,
                      color:chk?.targetExists?v("accent"):v("warning")}}>
                      {chk?.targetExists?t("transport.werkExistiert"):t("transport.werkNeu")}</span>
                  </div>
                  {isExpanded&&chk?.comparison&&<div>
                    <div style={{display:"flex",gap:6,padding:"6px 12px",borderTop:"1px solid var(--border)"}}>
                      <button onClick={()=>{setPkgTrMultiChecks(prev=>{const c={...prev};if(!c[werkKey]?.comparison)return prev;
                        c[werkKey]={...c[werkKey],comparison:c[werkKey].comparison.map(a=>({...a,selected:a.draft?false:true}))};return c;});}}
                        style={{padding:"2px 8px",border:"1px solid var(--border)",borderRadius:4,background:"transparent",color:"var(--text-3)",fontSize:10,cursor:"pointer"}}>{t("transport.alle")}</button>
                      <button onClick={()=>{setPkgTrMultiChecks(prev=>{const c={...prev};if(!c[werkKey]?.comparison)return prev;
                        c[werkKey]={...c[werkKey],comparison:c[werkKey].comparison.map(a=>({...a,selected:false}))};return c;});}}
                        style={{padding:"2px 8px",border:"1px solid var(--border)",borderRadius:4,background:"transparent",color:"var(--text-3)",fontSize:10,cursor:"pointer"}}>{t("transport.keine")}</button>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px 70px 40px",padding:"6px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                      <div></div><div>{t("transport.spalteArtifact")}</div><div>{t("transport.spalteTyp")}</div><div>{t("transport.spalteQuelle")}</div><div>{t("transport.spalteZiel")}</div><div></div>
                    </div>
                    {chk.comparison.map(art=>renderArtRow(art,werkKey,kennung))}
                    {chk.orphans?.length>0&&<div style={{borderTop:"1px solid var(--border)",padding:"8px 12px"}}>
                      <div style={{fontSize:11,fontWeight:600,color:v("danger"),marginBottom:6,cursor:"help"}} title={t("transport.veraltetHilfe")}>{t("transport.veraltetTitelZahl",{count:chk.orphans.length})}</div>
                      {chk.orphans.map(orphan=>{
                        const selKey=`${orphan.Id}__${werkKey}`;
                        const isSelected=pkgTrOrphanSel[selKey]!==false;
                        return <label key={orphan.Id} style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px",padding:"6px 0",cursor:"pointer",alignItems:"center"}}>
                          <input type="checkbox" checked={isSelected} onChange={()=>setPkgTrOrphanSel(prev=>({...prev,[selKey]:!isSelected}))}
                            style={{width:14,height:14,accentColor:v("danger")}}/>
                          <div style={{fontSize:12,color:"var(--text-1)"}}>{orphan.Name||orphan.Id}</div>
                          <div style={{fontSize:11,color:"var(--text-3)"}}>{orphan.Type||"iFlow"}</div>
                          <div style={{fontSize:10,color:orphan.deployed?v("success"):v("danger")}}>{orphan.deployed?t("transport.zustandDeployed"):t("transport.zustandGestoppt")}</div>
                        </label>;
                      })}
                    </div>}
                  </div>}
                  {isExpanded&&pkgTrDeploy&&(pkgTrWerkeRuntimes[werkKey]?.length||0)>1&&(
                    <div style={{marginTop:8,padding:"8px 12px",background:"var(--surface-1)",borderRadius:6,border:"1px solid var(--border)"}}>
                      <div style={{fontSize:11,color:"var(--text-3)",marginBottom:4}}>{t("transport.deployZiele")}</div>
                      {(pkgTrWerkeRuntimes[werkKey]||[]).map((rt,ri)=>(
                        <label key={rt.locationId||"cloud"} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text-1)",cursor:"pointer",padding:"2px 0"}}>
                          <input type="checkbox" checked={rt.selected} onChange={()=>{
                            setPkgTrWerkeRuntimes(prev=>{const upd={...prev};upd[werkKey]=[...upd[werkKey]];upd[werkKey][ri]={...upd[werkKey][ri],selected:!upd[werkKey][ri].selected};return upd;});
                          }}/>
                          {rt.name}{!rt.locationId&&t("transport.runtimeStandard")}
                        </label>
                      ))}
                    </div>
                  )}
                  {isExpanded&&chk?.error&&<div style={{padding:12,background:v("status-fail-bg"),color:v("danger"),fontSize:12}}>{chk.error}</div>}
                </div>;
              })}
            </div>}


            {renderGlobalRefsSection()}
            {renderPipelineChecksSection()}


              <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
                <MuiButton variant="outlined" onClick={()=>{setPkgTrStep(0);setPkgTrTarget("");setPkgTrComparison(null);setPkgTrMultiChecks({});setWerkExpanded({});setPkgTrOrphanSel({});}}>{t("gemeinsam:aktion.zurueck")}</MuiButton>
                <MuiButton onClick={()=>setPkgTrStep(2)} disabled={!hasAnySelected||pkgTrHasDraftDeps} variant="contained">
                  {pkgTrHasDraftDeps?t("transport.draftAbhaengigkeit"):t("gemeinsam:aktion.weiter")}
                </MuiButton>
              </div>
          </div>;})()}
          {pkgTrStep===2&&<div>
            <div style={{marginBottom:16}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:8}}>
                <input type="checkbox" checked={pkgTrCustomId} onChange={e=>setPkgTrCustomId(e.target.checked)} style={{width:14,height:14,accentColor:"var(--accent)"}}/>
                <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.basisIdAnpassen")}</span>
              </label>
              {pkgTrCustomId&&<div style={{display:"flex",gap:8,marginLeft:22}}>
                <input value={pkgTrTargetId} onChange={e=>setPkgTrTargetId(e.target.value)} placeholder={t("transport.paketIdPlatzhalter")}
                  style={{flex:1,background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",color:"var(--text-1)",fontSize:12,outline:"none",fontFamily:FONT}}/>
                <input value={pkgTrTargetName} onChange={e=>setPkgTrTargetName(e.target.value)} placeholder={t("transport.paketNamePlatzhalter")}
                  style={{flex:1,background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",color:"var(--text-1)",fontSize:12,outline:"none"}}/>
              </div>}
            </div>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:pkgTrDeploy?8:16}}>
              <input type="checkbox" checked={pkgTrDeploy} onChange={e=>{setPkgTrDeploy(e.target.checked);if(!e.target.checked){setPkgTrUndeploy(false);setPkgTrUndeploySel([]);}}} style={{width:14,height:14,accentColor:"var(--accent)"}}/>
              <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.deployNachher")}</span>
            </label>
            {pkgTrDeploy&&pkgTrDeployRuntimes.length>1&&(
              <div style={{marginLeft:28,marginTop:6,padding:"8px 12px",background:v("surface-1"),borderRadius:6,border:`1px solid ${v("border")}`,marginBottom:10}}>
                <div style={{fontSize:11,color:v("text-3"),marginBottom:6}}>{t("transport.deployZieleWaehlen")}</div>
                {pkgTrDeployRuntimes.map((rt,i)=>(
                  <label key={rt.locationId||"cloud"} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:v("text-1"),cursor:"pointer",padding:"2px 0"}}>
                    <input type="checkbox" checked={rt.selected} onChange={()=>{
                      const upd=[...pkgTrDeployRuntimes];upd[i]={...upd[i],selected:!upd[i].selected};setPkgTrDeployRuntimes(upd);
                    }}/>
                    {rt.name}{!rt.locationId&&t("transport.runtimeStandard")}
                  </label>
                ))}
              </div>
            )}

            {/* Undeploy option - nested under deploy */}
            {pkgTrDeploy&&<div style={{marginLeft:22,borderLeft:"2px solid var(--border)",paddingLeft:14,marginBottom:16}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:8}}>
                <input type="checkbox" checked={pkgTrUndeploy} onChange={e=>{
                  setPkgTrUndeploy(e.target.checked);
                  if(e.target.checked&&pkgTrUndeployList.length===0){
                    // Resolve target: single=pkgTrTarget, multi=first active tab instId
                    let tid=pkgTrTarget;
                    if(!tid&&pkgTrActiveTab)tid=pkgTrActiveTab.split("__")[0];
                    if(!tid){const k=Object.keys(pkgTrMultiChecks)[0];if(k)tid=k.split("__")[0];}
                    if(tid)loadTargetRuntime(tid);
                  }
                  if(!e.target.checked)setPkgTrUndeploySel([]);
                }} style={{width:14,height:14,accentColor:"var(--warning)"}}/>
                <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.undeployVorher")}</span>
              </label>
              {pkgTrUndeploy&&<div>
                <div style={{fontSize:11,color:"var(--text-3)",marginBottom:8}}>{t("transport.undeployHinweis")}</div>
                {pkgTrUndeployLoading?<div style={{padding:12,color:"var(--text-3)",fontSize:12}}>{t("transport.undeployLaedt")}</div>:
                pkgTrUndeployList.length===0?<div style={{padding:12,fontSize:12}}>
                  <div style={{color:"var(--text-3)",marginBottom:6}}>{t("transport.undeployLeerGeladen")}</div>
                  <button onClick={()=>{let tid=pkgTrTarget;if(!tid&&pkgTrActiveTab)tid=pkgTrActiveTab.split("__")[0];if(!tid){const k=Object.keys(pkgTrMultiChecks)[0];if(k)tid=k.split("__")[0];}if(tid)loadTargetRuntime(tid);}}
                    style={{padding:"4px 12px",borderRadius:6,border:"1px solid var(--border)",background:"var(--surface-2)",color:"var(--text-2)",fontSize:11,cursor:"pointer"}}>{t("transport.undeployErneutLaden")}</button>
                </div>:
                <div>
                  <input value={pkgTrUndeploySearch} onChange={e=>setPkgTrUndeploySearch(e.target.value)} placeholder={t("transport.undeploySuche")}
                    style={{width:"100%",background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"7px 10px",color:"var(--text-1)",fontSize:12,outline:"none",marginBottom:6,fontFamily:FONT}}/>
                  <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",marginBottom:8,maxHeight:220,overflowY:"auto"}}>
                    <div style={{display:"grid",gridTemplateColumns:"32px 1fr 80px",padding:"6px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",position:"sticky",top:0}}>
                      <span/><span>{t("transport.undeploySpalteIflow")}</span><span>{t("transport.spalteStatus")}</span>
                    </div>
                    {pkgTrUndeployList.filter(f=>{
                      if(pkgTrUndeploySearch){const s=pkgTrUndeploySearch.toLowerCase();return (f.Name||f.Id).toLowerCase().includes(s)||f.Id.toLowerCase().includes(s);}
                      return true;
                    }).map(f=>{
                      const isStarted=f.Status==="STARTED"||f.Status==="Started"||f.Status==="deployed";
                      return <label key={f.Id} style={{display:"grid",gridTemplateColumns:"32px 1fr 80px",padding:"8px 12px",borderTop:"1px solid var(--border)",cursor:"pointer",alignItems:"center",
                        background:pkgTrUndeploySel.includes(f.Id)?"rgba(245,158,11,.04)":"transparent"}}>
                        <input type="checkbox" checked={pkgTrUndeploySel.includes(f.Id)} onChange={()=>setPkgTrUndeploySel(p=>p.includes(f.Id)?p.filter(x=>x!==f.Id):[...p,f.Id])}
                          style={{width:14,height:14,accentColor:"var(--warning)"}}/>
                        <div><div style={{fontSize:12,color:"var(--text-1)"}}>{f.Name||f.Id}</div><div style={{fontSize:10,color:"var(--text-3)",fontFamily:MONO}}>{f.Id}</div></div>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,
                          background:isStarted?v("status-ok-bg"):"var(--surface-2)",
                          color:isStarted?v("success"):"var(--text-3)",
                          border:`1px solid ${isStarted?v("success"):"var(--border)"}`}}>{f.Status}</span>
                        {f.runtimeLocations?.length>1&&<div style={{fontSize:9,color:"var(--text-3)",gridColumn:"2/4",paddingTop:2}}>
                          {f.runtimeLocations.map(r=>r.name).join(", ")}
                        </div>}
                      </label>;})}
                  </div>
                  <div style={{fontSize:10,color:"var(--text-3)"}}>{t("transport.undeployAnzahl",{count:pkgTrUndeployList.length})}{pkgTrUndeploySearch?t("transport.undeployTreffer",{count:pkgTrUndeployList.filter(f=>(f.Name||f.Id).toLowerCase().includes(pkgTrUndeploySearch.toLowerCase())).length}):""}</div>
                </div>}
                {pkgTrUndeploySel.length>0&&<div style={{padding:"8px 12px",borderRadius:8,background:"rgba(245,158,11,.06)",border:"1px solid rgba(245,158,11,.2)",fontSize:11,color:v("warning"),lineHeight:1.5,marginTop:8}}>
                  {t("transport.undeploySelektiert",{count:pkgTrUndeploySel.length})}
                </div>}
              </div>}
            </div>}

            {/* Werke kennung overview - editable */}
            {(()=>{
              const werkeEntries=Object.entries(pkgTrMultiChecks).filter(([k])=>k.includes("__"));
              if(!werkeEntries.length)return null;
              return <div style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text-1)",marginBottom:8}}>{t("transport.werkskennungen")}</div>
                <div style={{background:"var(--surface-0)",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                  {werkeEntries.map(([key,chk],i)=>{
                    const parts=key.split("__");const iId=parts[0];const kennung=parts[1];
                    const inst=allInstances.find(x=>x.id===iId);
                    const basePkg=pkgTrCustomId?pkgTrTargetId:pkgTransport.Id;
                    return <div key={key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderTop:i?"1px solid var(--border)":"none",fontSize:12}}>
                      <span style={{color:"var(--text-3)",minWidth:100}}>{inst?.name}</span>
                      <span style={{color:"var(--text-2)",fontWeight:500}}>{chk?.werk?.name||kennung}</span>
                      <span style={{color:"var(--text-3)"}}>→</span>
                      <code style={{fontFamily:FONT,color:"var(--accent)",fontSize:11}}>{basePkg}{kennung}</code>
                    </div>;
                  })}
                </div>
              </div>;
            })()}

            {/* Summary */}
            <div style={{background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,padding:14,marginBottom:16,fontSize:12,color:"var(--text-2)",lineHeight:1.8}}>
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeQuelle")}</span> <span style={{color:"var(--text-1)"}}>{pkgTransport.Name}</span></div>
              {(()=>{
                const hasWerkeChecks=Object.keys(pkgTrMultiChecks).length>0;
                if(!hasWerkeChecks&&pkgTrComparison){
                  return <>
                    <div><span style={{color:"var(--text-3)"}}>{t("transport.summeZielInstanz")}</span> <span style={{color:"var(--text-1)"}}>{allInstances.find(i=>i.id===pkgTrTarget)?.name}</span></div>
                    <div><span style={{color:"var(--text-3)"}}>{t("transport.summeArtifacts")}</span> <span style={{color:"var(--text-1)"}}>{t("transport.summeAusgewaehlt",{count:pkgTrComparison?.comparison?.filter(a=>a.selected).length||0})}</span></div>
                    <div><span style={{color:"var(--text-3)"}}>{t("transport.summeZielPaket")}</span> <span style={{color:"var(--text-1)"}}>{pkgTrCustomId?`${pkgTrTargetName} (${pkgTrTargetId})`:pkgTransport.Id}</span></div>
                    {pkgTrComparison?.orphans?.filter(o=>pkgTrOrphanSel[o.Id]!==false).length>0&&<div>
                      <span style={{color:v("danger")}}>{t("transport.summeLoeschen")}</span> <span style={{color:v("danger")}}>{t("transport.summeLoeschenWert",{count:pkgTrComparison.orphans.filter(o=>pkgTrOrphanSel[o.Id]!==false).length})}</span>
                    </div>}
                  </>;
                }
                const checkKeys=Object.keys(pkgTrMultiChecks);
                const totalArts=checkKeys.reduce((s,k)=>{const c=pkgTrMultiChecks[k];return s+(c?.comparison?.filter(a=>a.selected).length||0);},0);
                const totalOrphans=checkKeys.reduce((s,k)=>{const c=pkgTrMultiChecks[k];return s+(c?.orphans?.filter(o=>pkgTrOrphanSel[`${o.Id}__${k}`]!==false).length||0);},0);
                return <>
                  <div><span style={{color:"var(--text-3)"}}>{t("transport.summeZiele")}</span> <span style={{color:"var(--text-1)"}}>{t("transport.summeZieleWert",{count:checkKeys.length})}</span></div>
                  <div><span style={{color:"var(--text-3)"}}>{t("transport.summeArtifactsGesamt")}</span> <span style={{color:"var(--text-1)"}}>{t("transport.summeAusgewaehlt",{count:totalArts})}</span></div>
                  {totalOrphans>0&&<div><span style={{color:v("danger")}}>{t("transport.summeLoeschen")}</span> <span style={{color:v("danger")}}>{t("transport.summeLoeschenWert",{count:totalOrphans})}</span></div>}
                </>;
              })()}
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeDeploy")}</span> <span style={{color:pkgTrDeploy?v("success"):"var(--text-3)"}}>{pkgTrDeploy?t("transport.ja"):t("transport.nein")}</span></div>
            </div>

            {/* Partner Directory Einträge mittransportieren */}
            {(()=>{
              const pdDeps=(pkgTrMode==="multi"?pkgTrMultiChecks[pkgTrActiveTab]:pkgTrComparison)?.pdDependencies;
              const isPipeline=instance.hasPipelineConcept&&pdDeps?.length>0;
              if(isPipeline){
                // Pipeline mode: show BPMN-referenced PIDs with target status
                const missing=pdDeps.filter(pd=>!pd.existsOnTarget);
                const present=pdDeps.filter(pd=>pd.existsOnTarget);
                return <div style={{marginTop:16,padding:"12px 14px",borderRadius:8,border:`1px solid ${v("border")}`,background:v("surface-1")}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                    <span style={{fontSize:13,fontWeight:600,color:v("text-1")}}>{t("transport.pdBpmnTitel")}</span>
                    <span style={{fontSize:10,color:missing.length>0?v("danger"):v("success"),marginLeft:"auto"}}>
                      {missing.length>0?t("transport.pdFehlenImZiel",{count:missing.length}):t("transport.pdAlleVorhanden")}
                    </span>
                  </div>
                  {missing.length>0&&<div style={{padding:"8px 12px",borderRadius:6,marginBottom:8,fontSize:11,
                    background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,color:v("danger")}}>
                    {t("transport.pdAutoRepliziert")}
                  </div>}
                  <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:"28px 1fr 100px 90px",padding:"6px 10px",background:v("surface-2"),fontSize:10,color:v("text-3"),fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                      <div></div><div>PID</div><div>{t("transport.pdSpalteReferenzen")}</div><div>{t("transport.pdSpalteZielStatus")}</div>
                    </div>
                    {pdDeps.map(pd=>(
                      <div key={pd.pid} style={{display:"grid",gridTemplateColumns:"28px 1fr 100px 90px",padding:"7px 10px",
                        borderTop:"1px solid var(--border)",alignItems:"center",
                        background:!pd.existsOnTarget?v("status-fail-bg"):"transparent"}}>
                        <div>
                          {!pd.existsOnTarget?<input type="checkbox"
                            checked={pkgTrPdEntries.find(e=>e.Pid===pd.pid)?.selected||false}
                            onChange={()=>setPkgTrPdEntries(prev=>prev.map(e=>e.Pid===pd.pid?{...e,selected:!e.selected}:e))}
                            style={{width:14,height:14,accentColor:"var(--accent)",cursor:"pointer"}}
                          />:<CheckIcon sx={{fontSize:14,color:v("success")}}/>}
                        </div>
                        <div>
                          <span style={{fontSize:12,fontWeight:500,color:pd.existsOnTarget?v("text-1"):v("danger")}}>{pd.pid}</span>
                          {pd.referencedParams?.length>0&&<div style={{fontSize:10,color:v("text-3"),marginTop:1}}>
                            {t("transport.pdKeys",{keys:pd.referencedParams.slice(0,3).join(", ")})}{pd.referencedParams.length>3&&t("transport.pdKeysMehr",{count:pd.referencedParams.length-3})}
                          </div>}
                        </div>
                        <div style={{fontSize:11,color:v("text-2")}}>{t("transport.pdReferenziertVon",{count:pd.referencedBy?.length||0})}</div>
                        <div style={{fontSize:11,fontWeight:500,color:pd.existsOnTarget?v("success"):v("danger")}}>
                          {pd.existsOnTarget?t("transport.pdVorhanden"):t("transport.pdFehlt")}
                        </div>
                      </div>
                    ))}
                  </div>
                  {missing.length>0&&<div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                    <span style={{fontSize:10,color:v("text-3")}}>
                      {t("transport.pdZurReplikation",{gewaehlt:pkgTrPdEntries.filter(e=>e.selected).length,gesamt:missing.length})}
                    </span>
                  </div>}
                </div>;
              }
              // Non-pipeline mode: show all source PDs with manual load
              return <div style={{marginTop:16,padding:"12px 14px",borderRadius:8,border:`1px solid ${v("border")}`,background:v("surface-1")}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:pkgTrPdEnabled?10:0}}>
                  <input type="checkbox" checked={pkgTrPdEnabled} onChange={e=>{
                    setPkgTrPdEnabled(e.target.checked);
                    if(e.target.checked&&pkgTrPdEntries.length===0) loadPdEntriesForTransport();
                  }} style={{accentColor:"var(--accent)",width:14,height:14}}/>
                  <span style={{fontSize:13,fontWeight:500,color:v("text-1")}}>{t("transport.pdMitnehmen")}</span>
                </label>
                {pkgTrPdEnabled&&<>
                  {pkgTrPdLoading&&<div style={{color:v("text-3"),fontSize:12,padding:"4px 0"}}>{t("transport.pdLaedt")}</div>}
                  {!pkgTrPdLoading&&pkgTrPdEntries.length===0&&<div style={{color:v("text-3"),fontSize:12}}>{t("transport.pdKeineEintraege")}</div>}
                  {!pkgTrPdLoading&&pkgTrPdEntries.length>0&&<div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:200,overflow:"auto"}}>
                    <div style={{display:"flex",gap:8,marginBottom:4}}>
                      <button onClick={()=>setPkgTrPdEntries(e=>e.map(p=>({...p,selected:true})))}
                        style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:`1px solid ${v("border")}`,background:"transparent",color:v("accent"),cursor:"pointer"}}>{t("transport.alle")}</button>
                      <button onClick={()=>setPkgTrPdEntries(e=>e.map(p=>({...p,selected:false})))}
                        style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:`1px solid ${v("border")}`,background:"transparent",color:v("text-3"),cursor:"pointer"}}>{t("transport.keine")}</button>
                      <span style={{fontSize:10,color:v("text-3"),marginLeft:"auto"}}>
                        {t("transport.pdGewaehlt",{gewaehlt:pkgTrPdEntries.filter(e=>e.selected).length,gesamt:pkgTrPdEntries.length})}
                      </span>
                    </div>
                    {pkgTrPdEntries.map(entry=>(
                      <label key={entry.Pid} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,background:v("surface-0"),cursor:"pointer"}}>
                        <input type="checkbox" checked={entry.selected} onChange={e=>{
                          setPkgTrPdEntries(prev=>prev.map(p=>p.Pid===entry.Pid?{...p,selected:e.target.checked}:p));
                        }} style={{accentColor:"var(--accent)",flexShrink:0}}/>
                        <span style={{fontSize:11,color:v("text-1"),flex:1,overflow:"hidden",textOverflow:"ellipsis"}}>{entry.Pid}</span>
                        <span style={{fontSize:10,color:v("text-3"),flexShrink:0}}>
                          {[entry.stringCount>0&&t("transport.pdStr",{count:entry.stringCount}),entry.binaryCount>0&&t("transport.pdBin",{count:entry.binaryCount}),entry.altCount>0&&t("transport.pdAlt",{count:entry.altCount})].filter(Boolean).join(", ")}
                        </span>
                      </label>
                    ))}
                  </div>}
                </>}
              </div>;
            })()}

            {/* ─ Pipeline Konzept: Banner + Pre-flight + Deploy Reihenfolge ─ */}
            {instance.hasPipelineConcept&&(()=>{
              const _sel=pkgTrComparison?.comparison?.filter(a=>a.selected)||[];
              const _pipelineArts=_sel.filter(a=>_classifyPipelineRole(a.Name||"")!=="normal");
              if(!_pipelineArts.length)return null;
              const _ordered=[..._sel].sort((a,b)=>
                (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(a.Name||"")]??5)-
                (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(b.Name||"")]??5)
              );
              const _roleColor=(role)=>role==="sai"?v("warning"):role==="rai"?v("accent"):role==="generic"||role==="script"?"#7c6fe0":v("text-2");
              return <div style={{marginTop:16}}>
                <MuiAlert severity="warning" sx={{mb:1.5}}>
                  <div style={{fontWeight:600,marginBottom:3,fontSize:13}}>{t("transport.pipelineErkannt")}</div>
                  <div style={{fontSize:12}}>{pkgTrDeploy?t("transport.pipelineErkanntMitDeploy"):t("transport.pipelineErkanntOhneDeploy")}</div>
                </MuiAlert>
                <div style={{padding:"10px 14px",borderRadius:8,background:v("surface-2"),border:`1px solid ${v("border")}`,marginBottom:8,fontSize:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <span style={{fontWeight:600,color:v("text-1")}}>{t("transport.preflightTitel")}</span>
                    <button onClick={()=>runPreflightCheck(pkgTrTarget)} disabled={preflightLoading||!pkgTrTarget}
                      style={{padding:"3px 10px",borderRadius:5,border:`1px solid ${v("accent")}40`,background:"transparent",color:v("accent"),fontSize:10,fontWeight:600,cursor:"pointer",fontFamily:"inherit",opacity:preflightLoading||!pkgTrTarget?.5:1}}>
                      {preflightLoading?t("transport.preflightLaeuft"):t("transport.preflightJetzt")}
                    </button>
                  </div>
                  {!preflightChecks&&!preflightLoading&&(
                    <div style={{color:v("text-3"),fontSize:11,fontStyle:"italic"}}>{t("transport.preflightLeer")}</div>
                  )}
                  {preflightChecks&&preflightChecks.map((item,i)=>{
                    const icon=item.status==="ok"
                      ?<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{color:v("success")}}><polyline points="20 6 9 17 4 12"/></svg>
                      :item.status==="missing"||item.status==="error"
                      ?<WarningAmberIcon sx={{fontSize:13,color:item.critical?v("danger"):v("warning")}}/>
                      :item.status==="warning"
                      ?<WarningAmberIcon sx={{fontSize:13,color:v("warning")}}/>
                      :<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4m0-4h.01"/></svg>;
                    const col=item.status==="ok"?v("success"):item.status==="missing"&&item.critical?v("danger"):item.status==="error"?v("danger"):item.status==="warning"||item.status==="missing"?v("warning"):v("text-3");
                    return <div key={item.id||i} style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:5}}>
                      <span style={{color:col,flexShrink:0,marginTop:1}}>{icon}</span>
                      <div>
                        <span style={{fontWeight:600,color:col,marginRight:4}}>[{item.category}]</span>
                        <span style={{color:item.status==="ok"?v("text-2"):v("text-1")}}>{item.detail}</span>
                      </div>
                    </div>;
                  })}
                </div>
                {pkgTrDeploy&&<div style={{padding:"10px 14px",borderRadius:8,background:v("surface-1"),border:`1px solid ${v("border")}`,fontSize:12}}>
                  <div style={{fontWeight:600,color:v("text-1"),marginBottom:6}}>{t("transport.deployReihenfolgeTitel")}</div>
                  {_ordered.map((a,i)=>{
                    const role=_classifyPipelineRole(a.Name||"");
                    return <div key={a.Id} style={{display:"flex",gap:8,alignItems:"center",marginBottom:3}}>
                      <span style={{fontFamily:FONT,color:v("text-3"),fontSize:10,minWidth:18}}>{i+1}.</span>
                      <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",color:v("text-1")}}>{a.Name||a.Id}</span>
                      <span style={{fontSize:10,color:_roleColor(role),fontWeight:600,textTransform:"uppercase",flexShrink:0}}>{role}</span>
                    </div>;
                  })}
                </div>}
              </div>;
            })()}

            <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
              <MuiButton variant="outlined" size="small" onClick={()=>setPkgTrStep(1)} disabled={pkgTrLoading}>{t("gemeinsam:aktion.zurueck")}</MuiButton>
              {!slzApprover&&<div style={{flex:1,minWidth:200,marginRight:10}}><textarea value={pkgTrApprovalNote} onChange={e=>setPkgTrApprovalNote(e.target.value)} placeholder={t("transport.begruendungPlatzhalter")} rows={1} style={{width:"100%",boxSizing:"border-box",resize:"vertical",minHeight:34,border:"1px solid var(--border)",borderRadius:6,background:"var(--surface-1)",color:"var(--text-1)",font:"inherit",fontSize:12,padding:"7px 10px",outline:"none"}}/><div style={{fontSize:10,color:pkgTrApprovalNote.trim().length>=10?v("success"):"var(--text-3)",textAlign:"right"}}>{t("transport.begruendungZaehler",{count:pkgTrApprovalNote.trim().length})}{pkgTrApprovalNote.trim().length>=10?"":t("transport.begruendungMindest")}</div></div>}<MuiButton variant="contained" onClick={()=>executePkgTransportPipeline()} disabled={pkgTrLoading||pkgTrHasUnconfiguredNew||(!slzApprover&&pkgTrApprovalNote.trim().length<10)}>{pkgTrHasUnconfiguredNew?t("transport.knopfKonfigurieren"):slzApprover?t("transport.knopfTransportieren"):t("transport.knopfAnfragen")}</MuiButton>
            </div>
          </div>}

          {/* ===== STEP 3: TRANSPORT PIPELINE (Concept C - Pipeline Cards) ===== */}
          {pkgTrStep===3&&<div>
            <div style={{fontSize:14,fontWeight:600,color:"var(--text-1)",marginBottom:4}}>{t("transport.laeuftTitel")}</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:16}}>{pkgTransport.Name} → {allInstances.find(i=>i.id===pkgTrTarget)?.name||t("transport.laeuftZiel")}</div>

            {/* Pipeline Cards */}
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
              {pkgTrPhases.map(phase=>{
                const isDone=phase.status==="done";const isActive=phase.status==="active";const isError=phase.status==="error";const isWait=phase.status==="waiting";const isWarn=phase.status==="warning";
                const borderColor=isDone?"var(--border)":isWarn?v("warning"):isActive?v("accent"):isError?v("danger"):"var(--border)";
                const bgColor=isDone?"var(--surface-0)":isWarn?"var(--surface-0)":isActive?"var(--surface-0)":isError?v("status-fail-bg"):"var(--surface-0)";
                const textColor=isDone?"var(--text-1)":isWarn?v("warning"):isActive?v("accent"):isError?v("danger"):"var(--text-3)";
                return <div key={phase.id} style={{display:"flex",gap:12,alignItems:"center",padding:"14px 16px",borderRadius:8,
                  border:`1px solid ${borderColor}`,background:bgColor,transition:"all .3s ease",opacity:isWait?.5:1}}>
                  {/* Icon */}
                  {isDone&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  {isWarn&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={v("warning")} strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                  {isActive&&<div style={{width:18,height:18,borderRadius:"50%",border:"2px solid #3b82f6",borderTopColor:"transparent",animation:"spin .8s linear infinite"}}/>}
                  {isError&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
                  {isWait&&<div style={{width:18,height:18,borderRadius:"50%",background:"var(--surface-2)",border:"1px solid var(--border)"}}/>}
                  {/* Content */}
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:textColor}}>{phase.label}</div>
                    <div style={{fontSize:11,color:isWait?"var(--text-3)":"var(--text-2)",marginTop:2}}>{phase.detail}</div>
                  </div>
                  {/* Duration */}
                  {phase.duration&&<span style={{fontSize:11,fontFamily:MONO,color:textColor}}>{phase.duration}</span>}
                </div>;
              })}
            </div>

            {/* Overall progress bar */}
            {(()=>{
              const total=pkgTrPhases.length;const done=pkgTrPhases.filter(p=>p.status==="done"||p.status==="warning").length;
              const pct=total>0?Math.round((done/total)*100):0;
              return <div style={{background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,padding:14,marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:12,color:"var(--text-2)"}}>{t("transport.gesamtfortschritt")}</span>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <span style={{fontSize:11,fontFamily:MONO,color:"var(--text-3)"}}>{Math.floor(pkgTrElapsed/60)}:{String(pkgTrElapsed%60).padStart(2,"0")}</span>
                    <span style={{fontSize:12,fontFamily:MONO,fontWeight:600,color:"var(--text-1)"}}>{pct}%</span>
                  </div>
                </div>
                <div style={{width:"100%",height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}>
                  <div style={{width:`${pct}%`,height:"100%",borderRadius:3,background:pkgTrPhases.some(p=>p.status==="error")?"var(--danger)":pct>=100?"var(--success)":"var(--accent)",
                    transition:"width .5s cubic-bezier(.4,0,.2,1)"}}/>
                </div>
              </div>;
            })()}

            {/* Result - only when transport finished */}
            {pkgTrResult&&!pkgTrLoading&&(pkgTrResult.success
              ?<div style={{padding:12,borderRadius:8,marginBottom:16,fontSize:13,background:v("status-ok-bg"),border:`1px solid ${v("status-ok-border")}`,color:v("success")}}>
                {pkgTrResult.queued
                  ?t("transport.eingereicht",{id:pkgTrResult.requestId})
                  :<>{t("transport.abgeschlossen")}{pkgTrResult.multi?t("transport.abgeschlossenZiele",{count:Object.keys(pkgTrResult.results||{}).length})
                    :`${t("transport.abgeschlossenZahlen",{erstellt:pkgTrResult.summary?.created||0,aktualisiert:pkgTrResult.summary?.updated||0})}${pkgTrResult.summary?.deployed?t("transport.abgeschlossenDeployed",{count:pkgTrResult.summary.deployed}):""}`}
                  {pkgTrResult.globalResults?.length>0&&t("transport.abgeschlossenGlobal",{count:pkgTrResult.globalResults.length})}</>}
              </div>
              :<div style={{padding:12,borderRadius:8,marginBottom:16,fontSize:13,background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,color:v("danger")}}>
                {pkgTrResult.multi
                  ?t("transport.fehlgeschlagenZiele",{count:Object.values(pkgTrResult.results||{}).filter(r=>!r.success).length})
                  :pkgTrResult.error?t("transport.fehler",{fehler:pkgTrResult.error}):t("transport.fehlgeschlagen")}
                {pkgTrResult.multi&&<div style={{marginTop:8,fontSize:11}}>
                  {Object.entries(pkgTrResult.results||{}).filter(([_,r])=>!r.success).map(([wk,r])=>{
                    const kennung=wk.split("__")[1]||wk;
                    const failedDeploys=(r.results||[]).filter(x=>x.deployError);
                    return <div key={wk} style={{padding:"4px 0",borderTop:"1px solid var(--border)"}}>
                      <span style={{fontWeight:600}}>{kennung}</span>: {r.error||t("transport.deployFehlerAnzahl",{count:failedDeploys.length})}
                      {failedDeploys.length>0&&<div style={{marginLeft:12,fontSize:10,color:v("danger")}}>
                        {failedDeploys.map(fd=><div key={fd.id||fd.Id}>{fd.name||fd.id||fd.Id}: {fd.deployError}{fd.runtime?` (${fd.runtime})`:""}</div>)}
                      </div>}
                    </div>;
                  })}
                </div>}
              </div>
            )}

            {/* Global reference results detail */}
            {pkgTrResult?.globalResults?.length>0&&!pkgTrLoading&&<div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",marginBottom:12}}>
              <div style={{padding:"8px 12px",background:"var(--surface-2)",fontSize:11,fontWeight:600,color:"var(--text-2)",display:"flex",alignItems:"center",gap:6}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                {t("transport.globaleReferenzen")}
              </div>
              {pkgTrResult.globalResults.map(g=>(
                <div key={g.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",borderTop:"1px solid var(--border-subtle)",fontSize:11}}>
                  {g.status==="failed"
                    ?<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={v("danger")} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                    :<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={v("success")} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  <span style={{fontWeight:500,color:"var(--text-1)"}}>{g.name}</span>
                  <span style={{fontSize:9,color:"var(--text-3)",fontFamily:MONO}}>{g.type}</span>
                  <span style={{marginLeft:"auto",fontSize:10,color:g.status==="failed"?v("danger"):v("success")}}>
                    {g.status==="failed"?g.error||t("gemeinsam:zustand.fehler"):g.status==="created"?t("transport.globalNeuErstellt"):g.status==="updated"?t("transport.globalAktualisiert"):t("transport.globalOk")}
                    {g.deployed&&t("transport.globalPlusDeployed")}
                  </span>
                </div>
              ))}
            </div>}

            {(()=>{
              // Collect deleteResults from single-mode or multi-mode sub-results
              const allDelResults=pkgTrResult?.deleteResults||[];
              const multiDelResults=pkgTrResult?.multi?Object.entries(pkgTrResult.results||{}).flatMap(([wk,r])=>(r?.deleteResults||[]).map(dr=>({...dr,werkKey:wk}))):[];
              const combinedDel=[...allDelResults,...multiDelResults];
              if(!combinedDel.length||pkgTrLoading)return null;
              return <div style={{marginTop:12}}>
                <div style={{fontSize:12,fontWeight:600,color:v("danger"),marginBottom:6}}>{t("transport.bereinigungTitel")}</div>
                {combinedDel.map(dr=><div key={dr.id+(dr.werkKey||"")} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:12}}>
                  <span style={{width:16,height:16,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                    background:dr.status==="deleted"?v("status-ok-bg"):v("status-fail-bg"),
                    color:dr.status==="deleted"?v("success"):v("danger"),
                    border:`1px solid ${dr.status==="deleted"?v("status-ok-border"):v("status-fail-border")}`}}>
                    {dr.status==="deleted"?"\u2713":"\u2717"}
                  </span>
                  <span style={{color:"var(--text-1)"}}>{dr.name||dr.id}</span>
                  {dr.werkKey&&<span style={{fontSize:10,color:"var(--text-3)"}}>{dr.werkKey.split("__")[1]||dr.werkKey}</span>}
                  {dr.wasDeployed&&<span style={{fontSize:10,color:"var(--text-3)"}}>{t("transport.bereinigungUndeployed")}</span>}
                  {dr.error&&<span style={{fontSize:10,color:v("danger")}}>{dr.error}</span>}
                </div>)}
              </div>;
            })()}

            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              {!pkgTrLoading&&<MuiButton variant="contained" color={pkgTrResult?.success?"success":"inherit"} onClick={()=>setPkgTransport(null)}>{t("gemeinsam:aktion.schliessen")}</MuiButton>}
            </div>
          </div>}
      </DialogContent>
    </MuiDialog>}

    <BpmnCompareDialog open={!!pkgTrBpmnCompare} onClose={()=>setPkgTrBpmnCompare(null)}
      sourceInstanceId={pkgTrBpmnCompare?.sourceInstanceId} targetInstanceId={pkgTrBpmnCompare?.targetInstanceId}
      artifactId={pkgTrBpmnCompare?.artifactId} targetArtifactId={pkgTrBpmnCompare?.targetArtifactId}
      artifactName={pkgTrBpmnCompare?.artifactName} sourcePackageId={pkgTrBpmnCompare?.sourcePackageId}
      sourceVersion={pkgTrBpmnCompare?.sourceVersion}
      targetVersion={pkgTrBpmnCompare?.targetVersion}/>

    </div>
  );
  }

  // === Flow List with Filter + Search ===
  if (!selFlow) {
    const pkgHidden = hiddenFlowsByPkg[selPkg.Id] || [];
    const visibleFlows = flowList.filter(f => !pkgHidden.includes(f.Id));
    const filteredFlows = pkgSearch ? visibleFlows.filter(f => f.Name?.toLowerCase().includes(pkgSearch.toLowerCase()) || f.Id?.toLowerCase().includes(pkgSearch.toLowerCase())) : visibleFlows;
    // When Pipeline Konzept active: sort by stage (Consumer first, then SAI last)
    const searchedFlows = instance.hasPipelineConcept
      ? [...filteredFlows].sort((a,b) => (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(a.Name||"")]??5) - (_PIPELINE_ROLE_ORDER[_classifyPipelineRole(b.Name||"")]??5))
      : filteredFlows;
    return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <MuiButton onClick={() => { setSelPkg(null); setPkgSearch(""); }} variant="outlined" size="small">{t("artefakt.zurueck")}</MuiButton>
          <div><span style={{ fontSize: 18, fontWeight: 700, fontFamily: FONT }}>{selPkg.Name}</span><span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: 8 }}>{t("artefakt.anzahl", { count: visibleFlows.length })}</span></div>
        </div>
        <div style={{ position: "relative" }}>
          <button onClick={() => setShowFlowFilter(!showFlowFilter)} style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text-2)", padding: "6px 14px", fontSize: 12, cursor: "pointer", fontFamily: FONT }}>
            <FilterListIcon sx={{fontSize:14,verticalAlign:"middle",mr:0.5}}/>{pkgHidden.length > 0 ? t("artefakt.filterAusgeblendet", { count: pkgHidden.length }) : t("artefakt.filter")}
          </button>
          {showFlowFilter && <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 8, width: 380, maxWidth: "90vw", maxHeight: 400, overflow: "auto", background: "var(--surface-0)", border: `1px solid ${v("border")}`, borderRadius: 12, padding: 12, zIndex: 50, boxShadow: v("shadow-lg") }}>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 8, fontWeight: 600 }}>{t("artefakt.filterTitel", { paket: selPkg.Name })}</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <MuiButton onClick={() => { const u = { ...hiddenFlowsByPkg, [selPkg.Id]: [] }; setHiddenFlowsByPkg(u); saveFilters(hiddenPkgs, u); }} variant="outlined" size="small">{t("paket.alleAnzeigen")}</MuiButton>
              <MuiButton onClick={() => { const u = { ...hiddenFlowsByPkg, [selPkg.Id]: flowList.map(f => f.Id) }; setHiddenFlowsByPkg(u); saveFilters(hiddenPkgs, u); }} variant="outlined" size="small">{t("paket.alleAusblenden")}</MuiButton>
            </div>
            {flowList.map(f => (
              <label key={f.Id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", borderRadius: 6, cursor: "pointer", fontSize: 12, color: "var(--text-1)" }}>
                <input type="checkbox" checked={!pkgHidden.includes(f.Id)} onChange={() => toggleFlowInPkg(selPkg.Id, f.Id)} style={{ accentColor: "var(--accent)" }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.Name}</span>
                <span style={{ fontSize: 10, color: "var(--text-3)" }}>{contentVersion(f.Version, t)}</span>
              </label>
            ))}
          </div>}
        </div>
      </div>
      <input value={pkgSearch} onChange={e => setPkgSearch(e.target.value)} placeholder={t("artefakt.suche")} style={{ width: "100%", maxWidth: 400, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 6, padding: "8px 14px", color: "var(--iio-text-1)", fontSize: 13, outline: "none", marginBottom: 16, fontFamily: FONT }} />
      {/* Flow Table - Konzept D: Pipeline-Tags raus, MONO raus, Glow raus */}
      {documentation.error && <div className="iflow-doc-index-error" role="alert">{t("dokumente.listeFehler")}<button type="button" className="btn btn--secondary btn--sm" onClick={documentation.refresh}>{t("dokumente.erneut")}</button></div>}
      <div className="iflow-content-runtime-note">
        <span>{t("laufzeit.hinweis")}</span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={contentRuntime.refresh} disabled={contentRuntime.status === "loading"}>{t("laufzeit.aktualisieren")}</button>
      </div>
      {contentRuntime.status === "error" && <div className="iflow-doc-index-error" role="alert">{t("laufzeit.fehler")}</div>}
      <div className="iflow-content-list" style={{ borderRadius: 8, border: "1px solid var(--iio-border)", overflow: "hidden", background: "var(--iio-bg-2)" }}>
        <div className="iflow-content-row" style={{ padding: "10px 16px", background: "var(--iio-bg-3)", fontSize: 10, fontWeight: 500, color: "var(--iio-text-3)", textTransform: "uppercase", letterSpacing: ".14em", borderBottom: "1px solid var(--iio-border-subtle)" }}>
          <span/><span>{t("artefakt.spalteIflow")}</span><span>{t("dokumente.spalte")}</span><span>{t("artefakt.spalteTyp")}</span><span>{t("laufzeit.designTime")}</span><span>{t("laufzeit.runtime")}</span><span>{t("artefakt.spalteDeployed")}</span>
        </div>
        {searchedFlows.map(f => {
          const versions = contentRuntimeDisplay(f, contentRuntime, t, gebiet);
          return (
          <div key={f.Id} className="iflow-content-row" onClick={() => { selectFlow(f); setPkgSearch(""); }} style={{ padding: "12px 16px", alignItems: "center", background: "transparent", borderTop: "1px solid var(--iio-border-subtle)", cursor: "pointer", transition: "background .12s" }}
            onMouseEnter={e=>e.currentTarget.style.background="var(--iio-bg-1)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <span aria-hidden="true" style={{ width: 2, height: 18, background: "var(--iio-border)" }}/>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "var(--iio-text-1)" }}>{f.Name}</div>
              {f.Sender&&<div style={{ fontSize: 11, color: "var(--iio-text-3)", marginTop: 2 }}>{f.Sender} → {f.Receiver}</div>}
              <div className="iflow-content-mobile-meta">
                <span>{f.Type === "Integration Flow" ? "iFlow" : f.Type || "iFlow"}</span>
                <span>{t("laufzeit.designTime")}: {versions.designVersion}</span>
                <span>{t("laufzeit.runtime")}: {versions.runtimeVersion}{versions.different ? ` (${t("laufzeit.abweichend")})` : ""}</span>
                <span>{t("artefakt.spalteDeployed")}: {versions.deployed}</span>
              </div>
            </div>
            <div>{documentation.loading ? <span className="iio-spinner" role="status" aria-label={t("dokumente.listeLaedt")} /> : documentation.error ? <span className="iflow-doc-empty">{t("dokumente.nichtGeladen")}</span> : <IflowDocumentationButton documents={documentation.documents.filter(doc => doc.flowId === f.Id)} flowName={f.Name || f.Id} onOpen={setDocumentationPreview} />}</div>
            <span style={{ fontSize: 11, color: "var(--iio-text-3)" }}>{f.Type === "Integration Flow" ? "iFlow" : f.Type || "iFlow"}</span>
            <span className="iflow-content-version">{versions.designVersion}</span>
            <span className="iflow-content-version" aria-busy={contentRuntime.status === "loading"}>{versions.runtimeVersion}{versions.different && <small>{t("laufzeit.abweichend")}</small>}</span>
            <span className="iflow-content-deployed" aria-busy={contentRuntime.status === "loading"}>{versions.date ? <time dateTime={versions.date.iso}>{versions.deployed}</time> : versions.deployed}</span>
          </div>
        );})}
      </div>
      {searchedFlows.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--iio-text-3)" }}>{pkgSearch ? t("artefakt.leerSuche") : t("artefakt.leerAusgeblendet")}</div>}
      {documentationPreview?.instanceId === instance.id && <IflowDocumentationPreview key={instance.id} instanceId={instance.id} document={documentationPreview}
        documents={documentation.documents.filter(doc => doc.flowId === documentationPreview.flowId)} onSelect={setDocumentationPreview} onClose={() => setDocumentationPreview(null)} />}
    </div>
  );
  }

  // === iFlow Detail - gerendert vom Einheits-Renderer IflowDiagram ===
  const rightW = rightPanel ? (isMobile ? "100%" : rightWidth) : 0;
  const bottomH = bottomPanel ? (isMobile ? "100%" : docHeight + "%") : "0";
  const selectedRuntime = contentRuntimeDisplay(selFlow, contentRuntime, t, gebiet);

  return (
    <div className="iio" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)", position: "relative" }}>
      {/* iFlow Header - mock-v2-a */}
      <div className="iflow-head iflow-content-head">
        <button className="ih-back" title={t("detail.zurueck")} onClick={() => { setSelFlow(null); setExtracted(null); setBottomPanel(false); setRightPanel(false); useDashboardStore.getState().clearCurrentFlowContext(); }}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="ih-title">
          <span className="ih-name">{selFlow.Name}</span>
          <span className="ih-version">{t("laufzeit.designTime")}: {selectedRuntime.designVersion}</span>
        </div>
        <div className="ih-divider" />
        <span className="iflow-content-detail-runtime" aria-busy={contentRuntime.status === "loading"}>
          {t("laufzeit.runtime")}: {selectedRuntime.runtimeVersion}
          {selectedRuntime.state === "present" && <small>{t("artefakt.spalteDeployed")}: {selectedRuntime.deployed}</small>}
        </span>
        {contentRuntime.status === "error" && <button type="button" className="btn btn--ghost btn--sm" onClick={contentRuntime.refresh}>{t("laufzeit.aktualisieren")}</button>}

        <div className="ih-spacer" />

        <div className="ih-actions">
          <button className={`ih-toggle${rightPanel ? " active" : ""}`} onClick={() => setRightPanel(!rightPanel)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            {t("detail.aiChat")}
          </button>
          <button className={`ih-toggle${docPanel ? " active" : ""}`} onClick={() => { if (docContent) { setDocPanel(p => !p); } else { setShowDocStylePicker(!showDocStylePicker); setShowRegenPicker(false); } }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="14" y2="17"/></svg>
            {t("detail.doku")}{docContent ? <CheckIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }} /> : ""}
          </button>
          <span className="ih-sep" />
          {permissions.alerting!==false&&<button className={`ih-toggle${alertList.some(a=>a.enabled) ? " active" : ""}`} onClick={()=>{setShowAlertModal(true);setAlertDebugLog("");}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            {t("detail.alerting")}{alertList.filter(a=>a.enabled).length>0 ? <span className="mono-stamp" style={{ marginLeft: 4, color: "var(--iio-success)" }}>({alertList.filter(a=>a.enabled).length})</span> : ""}
          </button>}
          {/* Transport-Button entfernt - nur noch ueber Package-Transport erreichbar */}
        </div>
      {/* Doc Style Picker Dropdown - positioned below header */}
      {showDocStylePicker && <div style={{ position: "absolute", right: 12, top: "100%", width: 360, maxWidth: "calc(100% - 24px)", boxSizing: "border-box", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: 12, zIndex: 100, boxShadow: "var(--iio-shadow-md)", maxHeight: 500, overflow: "auto" }}>
        <div style={{ fontSize: 12, color: "var(--iio-text-2)", marginBottom: 10, fontWeight: 600 }}>{t("doku.stilWaehlen")}</div>
        {Object.entries(DOC_STYLES).map(([key, { label, desc, icon }]) => (
          <div key={key} onClick={() => loadOrGenerateDoc(key, false)}
            style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 4, background: docStyle === key && docContent ? "var(--iio-bg-3)" : "transparent", border: "1px solid " + (docStyle === key && docContent ? "var(--iio-border)" : "transparent") }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--iio-text-2)" }}>{DOC_ICONS[icon]}</span>
              <span style={{ fontSize: 14, color: "var(--iio-text-1)", fontWeight: 600 }}>{label}</span>
              {docCache[key] && <span style={{ fontSize: 10, color: "var(--iio-success)", display: "inline-flex", alignItems: "center", gap: 2 }}><CheckIcon sx={{fontSize:12}}/>{t("doku.vorhanden")}</span>}
            </div>
            <div style={{ fontSize: 11, color: "var(--iio-text-2)", marginTop: 2, marginLeft: 22 }}>{desc}</div>
          </div>
        ))}
        {customTemplates.length>0&&<div style={{ height: 1, background: "var(--iio-border)", margin: "8px 0" }}/>}
        {customTemplates.map(vorlage=>(
          <div key={vorlage.id} onClick={() => loadOrGenerateDoc(`custom_${vorlage.id}`, false)}
            style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 4, background: docStyle === `custom_${vorlage.id}` && docContent ? "var(--iio-bg-3)" : "transparent", border: "1px solid transparent", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--iio-text-2)" }}>{DOC_ICONS.custom}</span>
            <span style={{ fontSize: 14, color: "var(--iio-text-1)", fontWeight: 600, flex: 1 }}>{vorlage.name}</span>
            {docCache[`custom_${vorlage.id}`] && <span style={{ fontSize: 10, color: "var(--iio-success)", display: "inline-flex", alignItems: "center", gap: 2 }}><CheckIcon sx={{fontSize:12}}/>{t("doku.vorhanden")}</span>}
            <MuiIconButton size="small" onClick={(e)=>{e.stopPropagation();deleteCustomTemplate(vorlage.id);}} sx={{color:"error.main",p:.25}}>×</MuiIconButton>
          </div>
        ))}
        <div style={{ height: 1, background: "var(--iio-border)", margin: "8px 0" }}/>
        {!showCustomForm?<div onClick={()=>setShowCustomForm(true)} style={{ padding: "10px 12px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--iio-text-2)" }}>{DOC_ICONS.custom}</span>
          <span style={{ fontSize: 13, color: "var(--iio-text-2)" }}>{t("doku.customErstellen")}</span>
        </div>:
        <div style={{ padding: "10px 12px", borderRadius: 8, background: "var(--iio-bg-3)", border: "1px solid var(--iio-border)" }}>
          <input value={customName} onChange={e=>setCustomName(e.target.value)} placeholder={t("doku.customNamePlatzhalter")} style={{ width: "100%", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 6, padding: "6px 10px", color: "var(--iio-text-1)", fontSize: 12, marginBottom: 6 }}/>
          <textarea value={customPrompt} onChange={e=>setCustomPrompt(e.target.value)} placeholder={t("doku.customPromptPlatzhalter")} rows={4} style={{ width: "100%", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 6, padding: "6px 10px", color: "var(--iio-text-1)", fontSize: 11, resize: "vertical", lineHeight: 1.5 }}/>
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <MuiButton onClick={addCustomTemplate} variant="contained" size="small">{t("gemeinsam:aktion.speichern")}</MuiButton>
            <MuiButton onClick={()=>{setShowCustomForm(false);setCustomName("");setCustomPrompt("");}} variant="outlined" size="small">{t("gemeinsam:aktion.abbrechen")}</MuiButton>
          </div>
        </div>}
      </div>}

      </div>

      {loadingFlow ? <div className="shimmer-bg" style={{ flex: 1, borderRadius: 8 }} /> : !extracted && error ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ maxWidth: 520, textAlign: "center", padding: 32 }}>
            <div style={{ width: 56, height: 56, borderRadius: 8, background: "rgba(200,54,46,.08)", border: "1px solid var(--iio-critical)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--iio-critical)" strokeWidth="1.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--iio-text-1)", marginBottom: 8 }}>{t("detail.inhaltNichtVerfuegbar")}</div>
            <div style={{ fontSize: 13, color: "var(--iio-text-2)", lineHeight: 1.6, marginBottom: 16 }}>
              {/403|Forbidden/i.test(error)
                ? t("detail.fehler403")
                : t("detail.fehlerAllgemein", { fehler: error })}
            </div>
            {/403|Forbidden/i.test(error) && <div style={{ textAlign: "left", padding: 16, borderRadius: 8, background: "var(--iio-bg-1)", border: "1px solid var(--iio-border)", fontSize: 12, color: "var(--iio-text-2)", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: "var(--iio-text-1)" }}>{t("detail.behebenTitel")}</div>
              <div><Trans t={t} i18nKey="detail.behebenS1" /></div>
              <div>{t("detail.behebenS2")}</div>
              <div><Trans t={t} i18nKey="detail.behebenS3" /></div>
              <div style={{ margin: "6px 0", padding: "6px 10px", borderRadius: 4, background: "var(--iio-bg-3)", fontWeight: 600 }}>AccessAllAccessPoliciesArtifacts</div>
              <div><Trans t={t} i18nKey="detail.behebenS4" /></div>
            </div>}
          </div>
        </div>
      ) : extracted && (
        <div className="stage-body">
          <div className="main-col">
            {/* BPMN Canvas */}
            <div className="bpmn-wrap">
              <span className="bpmn-hint">{t("detail.canvasHinweis")}</span>
              <IflowDiagram bpmnXml={extracted.bpmn} onElementClick={(el) => handleElementClick({ id: el.id, name: el.name, type: el.type || "task", subtype: el.activityType || el.type || "task", props: el.properties || {} })} selectedId={selectedStep?.id} />

            {/* DOCUMENTATION SLIDE-UP PANEL */}
            <div style={{
              position: isMobile ? "fixed" : "absolute", left: 0, right: 0, bottom: 0, top: isMobile && docPanel ? 0 : "auto",
              height: isMobile ? (docPanel ? "100%" : "0") : (docPanel ? docHeight + "%" : "0"), overflow: "hidden",
              transition: docDrag.current ? "none" : "height 0.3s ease", background: "var(--iio-bg-2)", borderTop: docPanel ? "2px solid var(--iio-brand-ink)" : "none",
              borderRadius: isMobile ? 0 : "8px 8px 0 0", display: "flex", flexDirection: "column", zIndex: isMobile ? 200 : 15,
            }}>
              {docPanel && <>
                {/* Drag handle */}
                {!isMobile && <div onMouseDown={onDocDragStart} style={{ height: 6, cursor: "ns-resize", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><div style={{ width: 40, height: 3, borderRadius: 2, background: "var(--iio-border)" }} /></div>}
                <div className="dock-head">
                  <span className="title">{t("doku.panelTitel", { name: selFlow.Name })}</span>
                  <span className="spacer" />
                  {docUsage && (
                    <button onClick={() => setShowDocDebug(p=>!p)} title={t("doku.debugTitel")}
                      style={{ background: showDocDebug ? "var(--iio-brand-ink)" : "var(--iio-bg-3)", border: `1px solid ${showDocDebug ? "var(--iio-brand-ink)" : "var(--iio-border)"}`, borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontSize: 11, color: showDocDebug ? "#fff" : "var(--iio-text-2)", display:"flex", alignItems:"center", gap:4 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      {t("doku.debug")}
                    </button>
                  )}
                  {docContent && <div style={{ position: "relative" }}>
                    <MuiButton onClick={() => { setShowRegenPicker(!showRegenPicker); setShowDocStylePicker(false); }} variant="outlined" size="small" startIcon={<RefreshIcon sx={{fontSize:14}}/>}>{t("doku.neuGenerieren", { stil: DOC_STYLES[docStyle]?.label||customTemplates.find(ct=>`custom_${ct.id}`===docStyle)?.name||t("doku.stilEigen") })}</MuiButton>
                    {showRegenPicker && <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 8, width: 300, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: 10, zIndex: 100, boxShadow: "var(--iio-shadow-md)" }}>
                      {Object.entries(DOC_STYLES).map(([key, { label, icon }]) => (
                        <div key={key} onClick={() => loadOrGenerateDoc(key, true)}
                          style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: docStyle === key ? "var(--iio-bg-3)" : "transparent", color: "var(--iio-text-1)", fontSize: 13, marginBottom: 2, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ color: "var(--iio-text-2)" }}>{DOC_ICONS[icon]}</span> {label}
                        </div>
                      ))}
                      {customTemplates.map(t=>(
                        <div key={t.id} onClick={() => loadOrGenerateDoc(`custom_${t.id}`, true)}
                          style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", background: docStyle === `custom_${t.id}` ? "var(--iio-bg-3)" : "transparent", color: "var(--iio-text-1)", fontSize: 13, marginBottom: 2, display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ color: "var(--iio-text-2)" }}>{DOC_ICONS.custom}</span> {t.name}
                        </div>
                      ))}
                    </div>}
                  </div>}
                  {docContent && <MuiButton onClick={publishToConfluence} disabled={confluencePublishing} title={t("doku.confluenceTitel")} variant="outlined" size="small">
                    {DOC_ICONS.confluence} {confluencePublishing?t("doku.confluenceLaeuft"):t("doku.confluence")}
                  </MuiButton>}
                  {confluenceResult && <span style={{ fontSize: 10, color: confluenceResult.success ? "var(--iio-success)" : "var(--iio-critical)" }}>
                    {confluenceResult.success ? <a href={confluenceResult.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--iio-success)", display: "inline-flex", alignItems: "center", gap: 2 }}><CheckIcon sx={{fontSize:12}}/>{t("doku.confluenceFertig")}</a> : confluenceResult.error}
                  </span>}
                  {docContent && <button title={t("doku.pdfTitel")} onClick={() => {
                    const renderMdPdf = (text) => text
                      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
                      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
                      .replace(/`([^`]+)`/g, '<code style="background:#eef2ff;padding:1px 5px;border-radius:3px;font-size:11px">$1</code>');
                    const pdfLines = docContent.split("\n");
                    const htmlParts = [];
                    let idx = 0;
                    while (idx < pdfLines.length) {
                      const line = pdfLines[idx];
                      // Table
                      if (line.trim().startsWith("|") && idx + 1 < pdfLines.length && /^\|[\s-:|]+\|/.test(pdfLines[idx+1].trim())) {
                        const tblLines = [];
                        while (idx < pdfLines.length && pdfLines[idx].trim().startsWith("|")) { tblLines.push(pdfLines[idx]); idx++; }
                        const hdr = tblLines[0].split("|").filter(c=>c.trim()).map(c=>c.trim());
                        const rows = tblLines.slice(2).map(r=>r.split("|").filter(c=>c.trim()).map(c=>c.trim()));
                        htmlParts.push('<table style="width:100%;border-collapse:collapse;margin:8px 0;font-size:12px"><thead><tr>' +
                          hdr.map(c=>`<th style="background:#f0f0ff;padding:6px 10px;border:1px solid #ddd;text-align:left;font-weight:600">${renderMdPdf(c)}</th>`).join("") +
                          '</tr></thead><tbody>' +
                          rows.map(r=>'<tr>'+r.map(c=>`<td style="padding:5px 10px;border:1px solid #ddd;line-height:1.6">${renderMdPdf(c)}</td>`).join("")+'</tr>').join("") +
                          '</tbody></table>');
                        continue;
                      }
                      // Code block
                      if (line.trim().startsWith("```")) { idx++; const cl=[];
                        while(idx<pdfLines.length&&!pdfLines[idx].trim().startsWith("```")){cl.push(pdfLines[idx]);idx++;}
                        if(idx<pdfLines.length)idx++;
                        htmlParts.push(`<pre style="background:#f5f5ff;border:1px solid #ddd;border-radius:6px;padding:10px;font-size:11px;overflow:auto;white-space:pre-wrap;margin:8px 0">${cl.join("\n")}</pre>`);
                        continue;
                      }
                      if (/^###\s/.test(line)) htmlParts.push(`<h4 style="color:#4f46e5;margin:12px 0 4px">${renderMdPdf(line.replace(/^###\s*/, ""))}</h4>`);
                      else if (/^##\s/.test(line)) htmlParts.push(`<h3 style="border-bottom:1px solid #ddd;padding-bottom:6px;margin:18px 0 8px">${renderMdPdf(line.replace(/^##\s*/, ""))}</h3>`);
                      else if (/^#\s/.test(line)) htmlParts.push(`<h2 style="margin:20px 0 10px">${renderMdPdf(line.replace(/^#\s*/, ""))}</h2>`);
                      else if (/^---+$/.test(line.trim())) htmlParts.push('<hr style="border:none;border-top:1px solid #ddd;margin:12px 0"/>');
                      else if (line.trim() === "") htmlParts.push("<br/>");
                      else htmlParts.push(`<p style="margin:2px 0;line-height:1.7">${renderMdPdf(line)}</p>`);
                      idx++;
                    }
                    const pdfTitel = String(selFlow.Name || "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
                    const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t("doku.pdfSeitenTitel", { name: pdfTitel })}</title>
                      <style>body{font-family:'Segoe UI','Source Sans Pro',sans-serif;max-width:800px;margin:40px auto;padding:0 20px;color:#1a1a2e;font-size:13px}
                      h2{font-size:20px;color:#1a1a2e}h3{font-size:16px;color:#2d3748}h4{font-size:14px;color:#4f46e5}
                      code{background:#eef2ff;padding:1px 5px;border-radius:3px;font-size:11px}
                      strong{color:#4338ca}
                      @media print{body{margin:20px}}</style></head><body>${sauberesHtml(htmlParts.join("\n"))}
                      <hr style="margin-top:40px;border:none;border-top:1px solid #ddd"/>
                      <p style="color:#999;font-size:10px">${t("doku.pdfFuss", { datum: new Date().toLocaleDateString(gebiet) })}</p>
                      </body></html>`;
                    const w = window.open("", "_blank");
                    w.document.write(fullHtml);
                    w.document.close();
                    setTimeout(() => { w.print(); }, 500);
                  }} style={{ background: "var(--iio-bg-3)", border: "1px solid var(--iio-border)", borderRadius: 6, color: "var(--iio-text-2)", padding: "4px 10px", fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}><FileDownloadIcon sx={{fontSize:14}}/>{t("doku.pdfExport")}</button>}
                  <button className="pill-x" onClick={() => setDocPanel(false)} title={t("doku.schliessenTitel")}>×</button>
                </div>
                {/* Debug-Panel: Token-Usage */}
                {showDocDebug && docUsage && (
                  <div style={{ padding: "10px 16px", background: "var(--iio-bg-3)", borderBottom: "1px solid var(--iio-border)", display: "flex", gap: 24, flexShrink: 0, flexWrap: "wrap", fontSize: 12 }}>
                    {(() => {
                      const IN_PRICE  = 15;   // $/1M Input-Tokens (Claude Opus 4.6 Bedrock, ca.)
                      const OUT_PRICE = 75;   // $/1M Output-Tokens
                      const costUsd = (docUsage.inputTokens * IN_PRICE + docUsage.outputTokens * OUT_PRICE) / 1_000_000;
                      const costEur = costUsd * 0.92;
                      return <>
                        <div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{t("doku.inputTokens")}</div>
                          <div style={{ color: "var(--iio-text-1)", fontWeight: 600, fontFamily: FONT }}>{docUsage.inputTokens.toLocaleString(gebiet)}</div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10 }}>${((docUsage.inputTokens * IN_PRICE)/1_000_000).toFixed(4)}</div>
                        </div>
                        <div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{t("doku.outputTokens")}</div>
                          <div style={{ color: "var(--iio-text-1)", fontWeight: 600, fontFamily: FONT }}>{docUsage.outputTokens.toLocaleString(gebiet)}</div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10 }}>${((docUsage.outputTokens * OUT_PRICE)/1_000_000).toFixed(4)}</div>
                        </div>
                        <div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{t("doku.mcpRunden")}</div>
                          <div style={{ color: "var(--iio-text-1)", fontWeight: 600, fontFamily: FONT }}>{docUsage.rounds}</div>
                        </div>
                        <div style={{ borderLeft: "1px solid var(--iio-border)", paddingLeft: 24 }}>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{t("doku.gesamtkosten")}</div>
                          <div style={{ color: "var(--iio-signal)", fontWeight: 700, fontSize: 14, fontFamily: FONT }}>{costEur.toFixed(4)} €</div>
                          <div style={{ color: "var(--iio-text-3)", fontSize: 10 }}>${costUsd.toFixed(4)} · {t("doku.preishinweis")}</div>
                        </div>
                      </>;
                    })()}
                  </div>
                )}
                {docSaveError && <div className="iflow-doc-index-error" role="alert">{t("dokumente.speichernFehler")}</div>}
                <div style={{ flex: 1, overflow: "auto", display: "flex" }}>
                  {docLoading && !docContent ? (
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, padding: 32 }}>
                      <div style={{ width: 36, height: 36, border: "3px solid var(--iio-border)", borderTopColor: "var(--iio-brand-ink)", borderRadius: "50%", animation: "iio-spin 1s linear infinite" }} />
                      <div style={{ color: "var(--iio-text-1)", fontSize: 14, fontWeight: 600 }}>{t("doku.wirdGeneriert")}</div>
                      {docEstimate && <>
                        <div style={{ color: "var(--iio-brand-ink)", fontSize: 13, fontWeight: 600 }}>
                          {t("doku.dauer", { dauer: docEstimate.label })}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center", maxWidth: 320 }}>
                          {docEstimate.phases.map((p, i) => (
                            <span key={i} style={{ background: "var(--iio-bg-3)", border: "1px solid var(--iio-border)", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "var(--iio-text-2)" }}>{p}</span>
                          ))}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--iio-text-3)", textAlign: "center", maxWidth: 280 }}>
                          {t("doku.mcpHinweis")}
                        </div>
                      </>}
                    </div>
                  ) : docContent ? (() => {
                    // Parse headings for TOC
                    const lines = docContent.split("\n");
                    const headings = lines.filter(l => /^#{1,3}\s/.test(l)).map((l, i) => ({ level: l.match(/^(#+)/)[1].length, text: l.replace(/^#+\s*/, ""), id: "doc-h-" + i }));
                    return (
                      <>
                        {/* TOC Sidebar - hidden on mobile */}
                        {!isMobile && <div style={{ width: 220, flexShrink: 0, borderRight: "1px solid var(--iio-border)", overflowY: "auto", padding: "12px 10px" }}>
                          <div style={{ fontSize: 10, color: "var(--iio-text-3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>{t("doku.inhalt")}</div>
                          {headings.map((h, i) => (
                            <a key={i} href={`#${h.id}`} style={{ display: "block", padding: "4px 8px", paddingLeft: 8 + (h.level - 1) * 12, fontSize: 12, color: h.level === 1 ? "var(--iio-text-1)" : h.level === 2 ? "var(--iio-text-2)" : "var(--iio-text-3)", textDecoration: "none", borderRadius: 4, marginBottom: 2 }}
                              onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth" }); }}>
                              {h.text.substring(0, 35)}{h.text.length > 35 ? "…" : ""}
                            </a>
                          ))}
                        </div>}
                        {/* Doc Content */}
                        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
                          {/* iFlow Diagram at top of doc */}
                          {extracted?.bpmn && <div style={{ marginBottom: 20, border: "1px solid var(--iio-border)", borderRadius: 8, overflow: "hidden" }}>
                            <div style={{ fontSize: 11, color: "var(--iio-text-3)", padding: "6px 12px", background: "var(--iio-bg-3)", borderBottom: "1px solid var(--iio-border)", fontWeight: 600 }}>{t("doku.iflowUebersicht", { name: selFlow.Name })}</div>
                            <IflowDiagram bpmnXml={extracted.bpmn} height="220px" />
                          </div>}
                          {(() => {
                            let hIdx = 0;
                            const renderMd = (text) => sauberesHtml(String(text ?? "")
                              .replace(/\*\*([^*]+)\*\*/g, '<strong style="color:var(--iio-brand-ink)">$1</strong>')
                              .replace(/\*([^*]+)\*/g, '<em>$1</em>')
                              .replace(/`([^`]+)`/g, '<code style="background:var(--iio-bg-3);padding:1px 5px;border-radius:3px;font-size:11px;color:var(--iio-text-1)">$1</code>')
                              .replace(/\[([^\]]+)\]/g, '<code style="background:var(--iio-bg-3);padding:1px 5px;border-radius:3px;font-size:11px;color:var(--iio-brand-ink)">[$1]</code>'));
                            // Pre-process: detect table blocks
                            const elements = [];
                            let i = 0;
                            while (i < lines.length) {
                              // Detect markdown table (line with | and next line with |---|)
                              if (lines[i].trim().startsWith("|") && i + 1 < lines.length && /^\|[\s-:|]+\|/.test(lines[i+1].trim())) {
                                const tableLines = [];
                                while (i < lines.length && lines[i].trim().startsWith("|")) {
                                  tableLines.push(lines[i]);
                                  i++;
                                }
                                // Parse table
                                const headerCells = tableLines[0].split("|").filter(c => c.trim() !== "").map(c => c.trim());
                                const bodyRows = tableLines.slice(2).map(row => row.split("|").filter(c => c.trim() !== "").map(c => c.trim()));
                                elements.push({ type: "table", headerCells, bodyRows, key: "tbl-" + elements.length });
                              }
                              // Code block
                              else if (lines[i].trim().startsWith("```")) {
                                const lang = lines[i].trim().replace(/^```/, "").trim();
                                i++;
                                const codeLines = [];
                                while (i < lines.length && !lines[i].trim().startsWith("```")) {
                                  codeLines.push(lines[i]);
                                  i++;
                                }
                                if (i < lines.length) i++; // skip closing ```
                                elements.push({ type: "code", lang, content: codeLines.join("\n"), key: "code-" + elements.length });
                              }
                              // Headings
                              else if (/^###\s/.test(lines[i])) { elements.push({ type: "h3", text: lines[i].replace(/^###\s*/, ""), id: "doc-h-" + (hIdx++), key: "h3-" + i }); i++; }
                              else if (/^##\s/.test(lines[i])) { elements.push({ type: "h2", text: lines[i].replace(/^##\s*/, ""), id: "doc-h-" + (hIdx++), key: "h2-" + i }); i++; }
                              else if (/^#\s/.test(lines[i])) { elements.push({ type: "h1", text: lines[i].replace(/^#\s*/, ""), id: "doc-h-" + (hIdx++), key: "h1-" + i }); i++; }
                              // HR
                              else if (/^---+$/.test(lines[i].trim())) { elements.push({ type: "hr", key: "hr-" + i }); i++; }
                              // Lists
                              else if (/^[-*]\s/.test(lines[i])) { elements.push({ type: "ul", text: lines[i].replace(/^[-*]\s*/, ""), key: "ul-" + i }); i++; }
                              else if (/^\d+\.\s/.test(lines[i])) { const num = lines[i].match(/^(\d+)\./)[1]; elements.push({ type: "ol", num, text: lines[i].replace(/^\d+\.\s*/, ""), key: "ol-" + i }); i++; }
                              // Empty
                              else if (lines[i].trim() === "") { elements.push({ type: "empty", key: "e-" + i }); i++; }
                              // Paragraph
                              else { elements.push({ type: "p", text: lines[i], key: "p-" + i }); i++; }
                            }
                            return elements.map(el => {
                              if (el.type === "table") return (
                                <div key={el.key} style={{ overflowX: "auto", marginBottom: 12, borderRadius: 8, border: "1px solid var(--iio-border)" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead><tr style={{ background: "var(--iio-bg-3)" }}>
                                      {el.headerCells.map((c, j) => <th key={j} style={{ padding: "8px 12px", color: "var(--iio-brand-ink)", fontWeight: 600, textAlign: "left", borderBottom: "2px solid var(--iio-border)", whiteSpace: "nowrap" }} dangerouslySetInnerHTML={{ __html: renderMd(c) }} />)}
                                    </tr></thead>
                                    <tbody>{el.bodyRows.map((row, r) => (
                                      <tr key={r} style={{ background: r % 2 === 0 ? "var(--iio-bg-3)" : "var(--iio-bg-1)" }}>
                                        {row.map((c, j) => <td key={j} style={{ padding: "6px 12px", color: "var(--iio-text-2)", borderBottom: "1px solid var(--iio-border)", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: renderMd(c) }} />)}
                                      </tr>
                                    ))}</tbody>
                                  </table>
                                </div>
                              );
                              if (el.type === "code") return <pre key={el.key} style={{ background: "var(--iio-bg-0)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: 14, overflow: "auto", fontSize: 12, color: "var(--iio-text-1)", fontFamily: FONT, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 12 }}>{el.content}</pre>;
                              if (el.type === "h1") return <h2 key={el.key} id={el.id} style={{ fontSize: 20, fontWeight: 700, color: "var(--iio-text-1)", marginTop: 24, marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} />;
                              if (el.type === "h2") return <h3 key={el.key} id={el.id} style={{ fontSize: 16, fontWeight: 700, color: "var(--iio-text-1)", marginTop: 24, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--iio-border)" }} dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} />;
                              if (el.type === "h3") return <h4 key={el.key} id={el.id} style={{ fontSize: 14, fontWeight: 600, color: "var(--iio-brand-ink)", marginTop: 16, marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} />;
                              if (el.type === "hr") return <hr key={el.key} style={{ border: "none", borderTop: "1px solid var(--iio-border)", margin: "16px 0" }} />;
                              if (el.type === "ul") return <div key={el.key} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--iio-text-2)", lineHeight: 1.7, marginBottom: 2, paddingLeft: 8 }}><span style={{ color: "var(--iio-signal)", flexShrink: 0 }}>•</span><span dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} /></div>;
                              if (el.type === "ol") return <div key={el.key} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--iio-text-2)", lineHeight: 1.7, marginBottom: 2, paddingLeft: 8 }}><span style={{ color: "var(--iio-signal)", flexShrink: 0, fontWeight: 600, minWidth: 16 }}>{el.num}.</span><span dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} /></div>;
                              if (el.type === "empty") return <div key={el.key} style={{ height: 8 }} />;
                              return <p key={el.key} style={{ fontSize: 13, color: "var(--iio-text-2)", lineHeight: 1.7, marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: renderMd(el.text) }} />;
                            });
                          })()}
                        </div>
                      </>
                    );
                  })() : null}
                </div>
              </>}
            </div>

            {/* BOTTOM STEP INSPECTOR */}
            <div className={`inspector${bottomPanel ? " open" : ""}`} style={{
              position: isMobile ? "fixed" : "absolute", left: 0, right: 0, bottom: 0, top: isMobile && bottomPanel ? 0 : "auto",
              height: isMobile ? (bottomPanel ? "100%" : "0") : bottomH, minHeight: bottomPanel ? 220 : 0, overflow: "hidden",
              zIndex: isMobile ? 200 : 10,
            }}>
              {bottomPanel && <>
                <div className="inspector-head">
                  <div className="ti">
                    {selectedStep && <span className="ti-name">{selectedStep.name.substring(0, 20)}</span>}
                    {selectedStep && <span className="ti-type">{selectedStep.subtype} · {selectedStep.type}</span>}
                  </div>
                  <div className="inspector-tabs">
                    {selectedStep && <button className={`inspector-tab${bottomTab==="details"?" active":""}`} onClick={() => setBottomTab("details")}>{t("inspektor.properties")}</button>}
                    {selectedStep && <button className={`inspector-tab${bottomTab==="stepChat"?" active":""}`} onClick={() => setBottomTab("stepChat")}>{t("inspektor.stepFragen")}</button>}
                    <button className={`inspector-tab${bottomTab==="config"?" active":""}`} onClick={() => setBottomTab("config")}>{t("inspektor.config")}</button>
                  </div>
                  <button className="inspector-close" onClick={() => { setBottomPanel(false); setSelectedStep(null); }} title={t("gemeinsam:aktion.schliessen")}>×</button>
                </div>
                <div className="inspector-body">
                  {/* Step Details */}
                  {bottomTab === "details" && selectedStep && (
                    <div>

                      {/* Properties Table */}
                      {Object.keys(selectedStep.props).length > 0 ? (
                        <div style={{ marginBottom: 16 }}>
                          <div className="kv-table">
                            {Object.entries(selectedStep.props).filter(([k]) => k && k !== "undefined").map(([k, val]) => {
                              const tableRows = parseContentModifierTable(String(val));
                              if (tableRows) {
                                return (
                                  <div key={k} style={{ borderBottom: "1px solid var(--iio-border-subtle)", fontSize: 12, gridColumn: "1 / -1" }}>
                                    <div style={{ padding: "6px 10px", color: "var(--iio-brand-ink)", fontFamily: FONT, fontWeight: 500, background: "var(--iio-bg-1)" }}>{k}</div>
                                    <div style={{ padding: "6px 10px" }}><ContentModifierTable rows={tableRows} /></div>
                                  </div>
                                );
                              }
                              return [
                                <div key={k+"-k"} className="k">{k}</div>,
                                <div key={k+"-v"} className="v">{String(val).length > 500 ? String(val).substring(0, 500) + "..." : String(val) || "\u2014"}</div>
                              ];
                            })}
                          </div>
                        </div>
                      ) : (
                        <div style={{ color: "var(--iio-text-3)", marginBottom: 16, padding: "12px", background: "var(--iio-bg-1)", borderRadius: 8, border: "1px dashed var(--iio-border)", fontSize: 12 }}>
                          <div style={{ marginBottom: 4 }}>{t("inspektor.keineProperties")}</div>
                          <div style={{ fontSize: 10, color: "var(--iio-text-3)" }}>{t("inspektor.id")} <code style={{ fontFamily: FONT }}>{selectedStep.id}</code></div>
                          <div style={{ fontSize: 10, color: "var(--iio-text-3)" }}>{t("inspektor.bpmnTyp")} <code style={{ fontFamily: FONT }}>{selectedStep.subtype || selectedStep.type}</code></div>
                        </div>
                      )}

                      {/* Linked Script - shown inline at the step */}
                      {(() => {
                        const script = selectedStep.linkedScript || (() => {
                          const stepName = selectedStep.name?.toLowerCase() || "";
                          const stepId = selectedStep.id?.toLowerCase() || "";
                          return extracted?.scripts?.find(s => {
                            const sName = s.name.toLowerCase().replace(/\.(groovy|gsh|js)$/i, "");
                            return stepName.includes(sName) || sName.includes(stepName.replace(/\s+/g, "")) || stepId.includes(sName);
                          });
                        })();
                        if (!script) return null;
                        return (
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iio-warning)" strokeWidth="1.5" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                              <span style={{ fontSize: 12, color: "var(--iio-warning)", fontWeight: 600, flex: 1 }}>{script.name}</span>
                              <button onClick={() => setScriptModal({ name: script.name, content: script.content })}
                                title={t("inspektor.vollbild")}
                                style={{ background: "none", border: "1px solid var(--iio-border)", borderRadius: 4, cursor: "pointer", padding: "2px 6px", color: "var(--iio-text-3)", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
                                {t("inspektor.vollbild")}
                              </button>
                            </div>
                            <pre style={{ background: "var(--iio-bg-0)", border: "1px solid var(--iio-border)", borderRadius: 8, padding: 14, overflow: "auto", fontSize: 11, fontFamily: MONO, lineHeight: 1.7, whiteSpace: "pre-wrap", maxHeight: 300, margin: 0 }}>
                              {highlightGroovyScript(script.content)}
                            </pre>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Step-specific Chat */}
                  {bottomTab === "stepChat" && selectedStep && (
                    <div style={{ height: "100%", margin: "-10px -14px" }}>
                      <ChatPanel key={selectedStep.id}
                        systemContext={{ instanceId: instance.id, flowId: selFlow.Id, flowName: selFlow.Name, focusStep: selectedStep.name, focusStepType: selectedStep.subtype, focusStepProps: selectedStep.props, bpmn: extracted.bpmn, scripts: extracted.scripts, configurations: extracted.configurations }}
                        placeholder={t("chat.platzhalterStep", { name: selectedStep.name })} />
                    </div>
                  )}
                  {/* Config */}
                  {bottomTab === "config" && (
                    extracted.configurations?.length > 0 ? extracted.configurations.map(c => (
                      <div key={c.ParameterKey} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--iio-border)", fontSize: 13 }}>
                        <span style={{ color: "var(--iio-brand-ink)", fontFamily: FONT }}>{c.ParameterKey}</span>
                        <span style={{ color: "var(--iio-text-1)", fontFamily: FONT }}>{c.ParameterValue}</span>
                      </div>
                    )) : <div style={{ color: "var(--iio-text-3)" }}>{t("inspektor.keineKonfigurationen")}</div>
                  )}
                </div>
              </>}
            </div>
            {/* close bpmn-wrap */}
            </div>
            {/* close main-col */}
          </div>

          {/* RIGHT RAIL - icon strip (always 44px) */}
          <aside className="right-rail">
            <button className={`rail-btn${rightPanel ? " active" : ""}`} onClick={() => setRightPanel(!rightPanel)} title={t("detail.aiChat")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </button>
            <button className={`rail-btn${docPanel ? " active" : ""}`} onClick={() => { if (docContent) { setDocPanel(p => !p); } else { setShowDocStylePicker(!showDocStylePicker); setShowRegenPicker(false); } }} title={t("detail.dokuTitel")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </button>
            <div className="rail-sep" />
            {permissions.alerting!==false&&<button className="rail-btn" onClick={()=>{setShowAlertModal(true);setAlertDebugLog("");}} title={t("detail.alerting")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </button>}
            {/* Transport nur noch ueber Package-Transport erreichbar */}
          </aside>

          {/* RIGHT DOCK - slides open for Chat */}
          <aside className={`dock${rightPanel ? " open" : ""}`}>
          <div className="dock-inner">
          <div className="dock-section only">
            {rightPanel && <>
              <div className="dock-head">
                <span className="title">{t("chat.titel")}</span>
                <span className="spacer" />
                <span className="mono-stamp">claude opus 4.7</span>
                <button className="pill-x" onClick={() => setRightPanel(false)} title={t("chat.schliessen")}>×</button>
              </div>
              <ChatPanel key={"full-" + selFlow.Id}
                systemContext={{ instanceId: instance.id, flowId: selFlow.Id, flowName: selFlow.Name, bpmn: extracted.bpmn, scripts: extracted.scripts, configurations: extracted.configurations }}
                placeholder={t("chat.platzhalterFlow", { name: selFlow.Name })} />
            </>}
          </div>
        </div>
        </aside>
        {/* close stage-body */}
      </div>
      )}

      {/* Script Fullscreen Modal */}
      {scriptModal && (
        <MuiDialog open={!!scriptModal} onClose={() => setScriptModal(null)} maxWidth="lg" fullWidth PaperProps={{sx:{borderRadius:2,maxHeight:"90vh",background:"var(--iio-bg-2)"}}}>
          <div style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderBottom: "1px solid var(--iio-border)", gap: 10 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--iio-warning)" strokeWidth="1.5" strokeLinecap="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--iio-text-1)", flex: 1 }}>{scriptModal.name}</span>
            <MuiIconButton size="small" onClick={() => setScriptModal(null)} sx={{color:"text.secondary"}}><CloseIcon fontSize="small"/></MuiIconButton>
          </div>
          <DialogContent sx={{p:0,overflow:"auto"}}>
            <pre style={{ margin: 0, padding: 20, fontFamily: MONO, fontSize: 12, lineHeight: 1.7, background: "var(--iio-bg-0)", whiteSpace: "pre-wrap", wordBreak: "break-word", minHeight: 300 }}>
              {highlightGroovyScript(scriptModal.content)}
            </pre>
          </DialogContent>
        </MuiDialog>
      )}

      {/* Transport Wizard Modal */}
      {showTransport && (
        <MuiDialog open={showTransport} onClose={() => !transportLoading && setShowTransport(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:2,maxHeight:"90vh"}}}>
          <DialogContent sx={{p:0}}><div style={{ padding: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>
              <div style={{ fontWeight: 700, fontSize: 18, color: "var(--text-1)", flex: 1 }}>{t("iflowTransport.titel", { name: selFlow?.Name })}</div>
              <MuiIconButton size="small" onClick={() => setShowTransport(false)} sx={{color:"text.secondary"}}><CloseIcon fontSize="small"/></MuiIconButton>
            </div>
            {/* Step indicators */}
            <div style={{ display: "flex", gap: 4, marginBottom: 24 }}>
              {(transportWithCreds ? [t("iflowTransport.schrittParameter"), t("iflowTransport.schrittZiel"), t("iflowTransport.schrittBestaetigung"), t("iflowTransport.schrittCredentials")] : [t("iflowTransport.schrittParameter"), t("iflowTransport.schrittZiel"), t("iflowTransport.schrittBestaetigung")]).map((s, i) => (
                <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: transportStep >= i ? v("accent") : v("surface-2"), transition: "background .2s" }} />
              ))}
            </div>
            {/* Step 0: Parameters */}
            {transportStep === 0 && (<div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", marginBottom: 12 }}>{t("iflowTransport.parameterTitel")}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 16 }}>{t("iflowTransport.parameterHinweis")}</div>
              {transportConfigs.length === 0 ? <div style={{ color: "var(--text-3)", fontSize: 13, padding: 20, textAlign: "center" }}>{t("iflowTransport.parameterLeer")}</div> :
              <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid var(--border-subtle)", borderRadius: 8 }}>
                {transportConfigs.map((c, i) => (
                  <div key={c.ParameterKey} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
                    <span style={{ flex: 1, fontSize: 12, color: "var(--text-2)", fontWeight: 500, minWidth: 150 }}>{c.ParameterKey}</span>
                    <input value={c.newValue || ""} onChange={e => { const n = [...transportConfigs]; n[i] = { ...n[i], newValue: e.target.value }; setTransportConfigs(n); }}
                      style={{ flex: 2, background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 6, padding: "6px 10px", color: c.newValue !== c.ParameterValue ? v("warning") : "var(--text-1)", fontSize: 12 }} />
                  </div>
                ))}
              </div>}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <MuiButton onClick={() => setTransportStep(1)} variant="contained" size="small">{t("gemeinsam:aktion.weiter")}</MuiButton>
              </div>
            </div>)}
            {/* Step 1: Target */}
            {transportStep === 1 && (<div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", marginBottom: 12 }}>{t("iflowTransport.zielTitel")}</div>
              {transportError && <div style={{ padding: "10px 14px", borderRadius: 8, background: v("status-fail-bg"), border: `1px solid ${v("status-fail-border")}`, color: v("danger"), fontSize: 12, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>{transportError}</span>
                <MuiIconButton size="small" onClick={() => setTransportError("")} sx={{color:"error.main",p:0.25}}><CloseIcon fontSize="small"/></MuiIconButton>
              </div>}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, color: "var(--text-2)", display: "block", marginBottom: 6, fontWeight: 500 }}>{t("iflowTransport.labelZielInstanz")}</label>
                <div style={{ position: "relative" }}>
                  <select value={transportTarget} onChange={e => loadTargetPackages(e.target.value)} style={{ width: "100%", background: "var(--surface-1)", border: `1px solid ${v("border")}`, borderRadius: 8, padding: "12px 14px", color: "var(--text-1)", fontSize: 13, appearance: "none", cursor: "pointer" }}>
                    <option value="">{t("iflowTransport.instanzWaehlen")}</option>
                    {allInstances.map(ii => <option key={ii.id} value={ii.id}>{ii.name} ({ii.spaceName})</option>)}
                  </select>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>
              {transportTarget && <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, color: "var(--text-2)", display: "block", marginBottom: 6, fontWeight: 500 }}>{t("iflowTransport.labelZielPaket")}</label>
                {transportPackages.length === 0 ? <div style={{ color: "var(--text-3)", fontSize: 12, padding: "12px 14px", background: "var(--surface-1)", border: `1px solid ${v("border")}`, borderRadius: 10 }}>{t("iflowTransport.paketeLaden")}</div> : <>
                  <div style={{ position: "relative" }}>
                    <select value={transportPkg} onChange={e => setTransportPkg(e.target.value)} style={{ width: "100%", background: "var(--surface-1)", border: `1px solid ${v("border")}`, borderRadius: 8, padding: "12px 14px", color: "var(--text-1)", fontSize: 13, appearance: "none", cursor: "pointer" }}>
                      <option value="">{t("iflowTransport.paketWaehlen", { count: transportPackages.length })}</option>
                      {transportPackages.map(p => { const co=p.Mode&&p.Mode!=="EDIT_ALLOWED"; return <option key={p.Id} value={p.Id} disabled={co}>{p.Name}{co?t("iflowTransport.paketConfigureOnly"):""}</option>; })}
                    </select>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><polyline points="6 9 12 15 18 9"/></svg>
                  </div>
                </>}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, padding: "10px 0", borderTop: "1px solid var(--border-subtle)" }}>
                  <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 500 }}>{t("iflowTransport.neuesPaket")}</span>
                  <input value={transportNewPkg} onChange={e => setTransportNewPkg(e.target.value)} placeholder={t("iflowTransport.neuesPaketName")} style={{ background: "var(--surface-1)", border: `1px solid ${v("border")}`, borderRadius: 8, padding: "8px 12px", color: "var(--text-1)", fontSize: 12 }} />
                  <input value={transportNewPkgDesc} onChange={e => setTransportNewPkgDesc(e.target.value)} placeholder={t("iflowTransport.neuesPaketBeschreibung")} style={{ background: "var(--surface-1)", border: `1px solid ${v("border")}`, borderRadius: 8, padding: "8px 12px", color: "var(--text-1)", fontSize: 12 }} />
                  {transportNewPkg && transportNewPkgDesc && <button onClick={createTargetPackage} style={{ padding: "8px 14px", borderRadius: 8, background: v("accent"), color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", alignSelf: "flex-start" }}>{t("iflowTransport.erstellen")}</button>}
                </div>
              </div>}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                <MuiButton onClick={() => setTransportStep(0)} variant="outlined" size="small">{t("gemeinsam:aktion.zurueck")}</MuiButton>
                <button onClick={runPrecheck} disabled={!transportTarget || !transportPkg || precheckLoading} style={{ padding: "8px 20px", borderRadius: 8, background: transportTarget && transportPkg ? v("accent") : v("surface-2"), color: "var(--text-1)", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: transportTarget && transportPkg ? 1 : .4 }}>
                  {precheckLoading ? t("iflowTransport.pruefe") : t("gemeinsam:aktion.weiter")}
                </button>
              </div>
            </div>)}
            {/* Step 2: Confirm */}
            {transportStep === 2 && (<div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", marginBottom: 16 }}>{t("iflowTransport.bestaetigenTitel")}</div>
              {precheck?.pkgReadOnly && <div style={{ padding: "12px 16px", borderRadius: 8, background: v("status-fail-bg"), border: `1px solid ${v("status-fail-border")}`, color: v("danger"), fontSize: 12, marginBottom: 12 }}>{t("iflowTransport.paketReadOnly", { modus: precheck.pkgMode })}</div>}
              {precheck?.pkgLocked && <div style={{ padding: "12px 16px", borderRadius: 8, background: v("status-fail-bg"), border: `1px solid ${v("danger")}`, color: v("danger"), fontSize: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 700 }}>{t("iflowTransport.paketGesperrt")}{precheck.pkgLockedBy ? t("iflowTransport.paketGesperrtVon", { name: precheck.pkgLockedBy }) : ""}</div>
                <div style={{ color: v("danger"), marginTop: 4 }}>{t("iflowTransport.paketGesperrtHinweis")}</div>
              </div>}
              {precheck?.flowExists && <div style={{ padding: "12px 16px", borderRadius: 8, background: v("surface-2"), border: `1px solid ${v("accent")}40`, color: v("accent"), fontSize: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{t("iflowTransport.flowExistiert", { id: selFlow?.Id })}</div>
                <div style={{ color: v("text-3"), marginBottom: 8 }}>{t("iflowTransport.flowExistiertHinweis")}</div>
                <MuiButton onClick={() => setTransportFlowIdOverride(selFlow?.Id + "_v2")} variant="outlined" size="small" color="primary">{t("iflowTransport.flowAlsV2", { id: selFlow?.Id })}</MuiButton>
                {transportFlowIdOverride && <span style={{ color: v("success"), fontSize: 11, marginLeft: 8 }}>{t("iflowTransport.flowIdGesetzt", { id: transportFlowIdOverride })}</span>}
              </div>}
              <div style={{ background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 16px", fontSize: 13 }}>
                  <span style={{ color: "var(--text-2)" }}>{t("iflowTransport.summeIflow")}</span><span style={{ color: "var(--text-1)", fontWeight: 600 }}>{selFlow?.Name}{transportFlowIdOverride ? ` (${transportFlowIdOverride})` : ""}</span>
                  <span style={{ color: "var(--text-2)" }}>{t("iflowTransport.summeQuelle")}</span><span style={{ color: "var(--text-1)" }}>{instance.name}</span>
                  <span style={{ color: "var(--text-2)" }}>{t("iflowTransport.summeZiel")}</span><span style={{ color: "var(--text-1)" }}>{allInstances.find(ii => ii.id === transportTarget)?.name}</span>
                  <span style={{ color: "var(--text-2)" }}>{t("iflowTransport.summePaket")}</span><span style={{ color: "var(--text-1)" }}>{transportPackages.find(p => p.Id === transportPkg)?.Name || transportPkg}</span>
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "12px 16px", background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, marginBottom: 12 }}>
                <input type="checkbox" checked={transportDeployAfter} onChange={e => setTransportDeployAfter(e.target.checked)} style={{ width: 18, height: 18, accentColor: "var(--accent)" }} />
                <div>
                  <div style={{ fontSize: 13, color: "var(--text-1)", fontWeight: 600 }}>{t("iflowTransport.deployTitel")}</div>
                  <div style={{ fontSize: 11, color: "var(--text-2)" }}>{t("iflowTransport.deployDesc")}</div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "12px 16px", background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, marginBottom: 12 }}>
                <input type="checkbox" checked={transportWithCreds} onChange={e => { setTransportWithCreds(e.target.checked); if(e.target.checked && transportCreds.length===0) setTransportCreds(credAliases.length?credAliases.map(a=>({name:a.name||a,kind:a.authType||"basic",user:"",password:"",tokenUrl:"",clientId:"",clientSecret:"",scope:"",value:"",adapterType:a.adapterType||""})):[{name:"",kind:"basic",user:"",password:"",tokenUrl:"",clientId:"",clientSecret:"",scope:"",value:""}]); }} style={{ width: 18, height: 18, accentColor: "var(--accent)" }} />
                <div>
                  <div style={{ fontSize: 13, color: "var(--text-1)", fontWeight: 600 }}>{t("iflowTransport.credsTitel")}{credAliases.length>0?t("iflowTransport.credsGefunden", { count: credAliases.length }):""}</div>
                  <div style={{ fontSize: 11, color: "var(--text-2)" }}>{t("iflowTransport.credsDesc")}</div>
                </div>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "12px 16px", background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, marginBottom: 12 }}>
                <input type="checkbox" checked={transportScheduleEnabled} onChange={e => setTransportScheduleEnabled(e.target.checked)} style={{ width: 18, height: 18, accentColor: "var(--accent)" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "var(--text-1)", fontWeight: 600 }}>{t("iflowTransport.terminTitel")}</div>
                  <div style={{ fontSize: 11, color: "var(--text-2)" }}>{t("iflowTransport.terminDesc")}</div>
                </div>
              </label>
              {transportScheduleEnabled && <div style={{ padding: "12px 16px", background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, marginBottom: 12 }}>
                <input type="datetime-local" value={transportSchedule} onChange={e => setTransportSchedule(e.target.value)}
                  style={{ width: "100%", background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 8, padding: "8px 12px", color: "var(--text-1)", fontSize: 12 }} />
                {transportSchedule && <div style={{ fontSize: 11, color: v("accent"), marginTop: 4 }}>{t("iflowTransport.terminHinweis", { zeit: new Date(transportSchedule).toLocaleString(gebiet) })}</div>}
              </div>}
              {transportResult && <div style={{ padding: "12px 16px", borderRadius: 8, marginBottom: 12, background: transportResult.success ? v("status-ok-bg") : v("status-fail-bg"), border: `1px solid ${transportResult.success ? v("success") : v("danger")}`, fontSize: 13 }}>
                {transportResult.success
                  ? <span style={{ color: v("success") }}>{transportResult.queued ? t("transport.eingereicht", { id: transportResult.requestId }) : transportResult.scheduled ? t("iflowTransport.ergebnisTerminiert", { zeit: new Date(transportResult.record?.scheduledAt).toLocaleString(gebiet) }) : `${transportResult.existed ? t("iflowTransport.ergebnisErfolgAktualisiert") : t("iflowTransport.ergebnisErfolgNeu")}${transportResult.deployed ? t("iflowTransport.ergebnisUndDeployed") : "."}`}</span>
                  : <div style={{ color: v("danger") }}>{t("transport.fehler", { fehler: transportResult.error })}</div>}
              </div>}
              {!slzApprover&&<div style={{marginBottom:10}}><textarea value={transportApprovalNote} onChange={e=>setTransportApprovalNote(e.target.value)} placeholder={t("transport.begruendungPlatzhalter")} rows={2} style={{width:"100%",boxSizing:"border-box",resize:"vertical",border:"1px solid var(--border)",borderRadius:6,background:"var(--surface-1)",color:"var(--text-1)",font:"inherit",fontSize:12,padding:"7px 10px",outline:"none"}}/><div style={{fontSize:10,color:transportApprovalNote.trim().length>=10?v("success"):"var(--text-3)",textAlign:"right"}}>{t("transport.begruendungZaehler",{count:transportApprovalNote.trim().length})}{transportApprovalNote.trim().length>=10?"":t("transport.begruendungMindest")}</div></div>}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <MuiButton onClick={() => setTransportStep(1)} variant="outlined" size="small">{t("gemeinsam:aktion.zurueck")}</MuiButton>
                {transportWithCreds && !transportResult
                  ? <MuiButton onClick={() => setTransportStep(3)} variant="contained" size="small">{t("iflowTransport.weiterZuCreds")}</MuiButton>
                  : <button onClick={executeTransport} disabled={transportLoading || precheck?.pkgReadOnly || precheck?.pkgLocked || (!slzApprover&&transportApprovalNote.trim().length<10)} style={{ padding: "10px 28px", borderRadius: 8, background: (transportLoading || precheck?.pkgReadOnly || precheck?.pkgLocked) ? v("surface-2") : v("accent"), color: "var(--text-1)", border: "none", fontSize: 14, fontWeight: 700, cursor: (transportLoading || precheck?.pkgReadOnly || precheck?.pkgLocked) ? "not-allowed" : "pointer" }}>
                    {transportLoading ? t("iflowTransport.transportiert") : !slzApprover ? t("transport.knopfAnfragen") : transportScheduleEnabled && transportSchedule ? t("iflowTransport.terminieren") : t("transport.knopfTransportieren")}
                  </button>
                }
              </div>
            </div>)}
            {/* Step 3: Credentials */}
            {transportStep === 3 && (<div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-1)", marginBottom: 12 }}>{t("iflowTransport.credsAnlegenTitel")}</div>
              <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 16 }}>{t("iflowTransport.credsAnlegenHinweis")}</div>
              {transportCreds.map((cred, i) => (
                <div key={i} style={{ background: "var(--surface-1)", border: "1px solid var(--border-subtle)", borderRadius: 8, padding: 14, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 600 }}>{cred.name || t("iflowTransport.credName", { nummer: i+1 })}{cred.adapterType ? ` (${cred.adapterType})` : ""}</span>
                    {transportCreds.length > 1 && <MuiIconButton size="small" onClick={() => setTransportCreds(c => c.filter((_, j) => j !== i))} sx={{color:"error.main"}}>{t("iflowTransport.credEntfernen")}</MuiIconButton>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <input value={cred.name} onChange={e => { const n=[...transportCreds];n[i]={...n[i],name:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credNamePlatzhalter")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                    <div style={{ position: "relative" }}>
                      <select value={cred.kind} onChange={e => { const n=[...transportCreds];n[i]={...n[i],kind:e.target.value};setTransportCreds(n); }} style={{ width: "100%", background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12, appearance: "none" }}>
                        <option value="basic">{t("iflowTransport.credBasic")}</option>
                        <option value="oauth2_client_credentials">{t("iflowTransport.credClientCredentials")}</option>
                        <option value="oauth2_saml_bearer">{t("iflowTransport.credSamlBearer")}</option>
                        <option value="oauth2_authorization_code">{t("iflowTransport.credAuthCode")}</option>
                        <option value="secure_param">{t("iflowTransport.credSecureParam")}</option>
                        <option value="none">{t("iflowTransport.credKeine")}</option>
                      </select>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><polyline points="6 9 12 15 18 9"/></svg>
                    </div>
                    {cred.kind === "basic" && <>
                      <input value={cred.user||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],user:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credBenutzer")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input type="password" value={cred.password||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],password:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credPasswort")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                    </>}
                    {cred.kind === "oauth2_client_credentials" && <>
                      <input value={cred.tokenUrl||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],tokenUrl:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credTokenUrl")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input value={cred.clientId||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],clientId:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credClientId")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input type="password" value={cred.clientSecret||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],clientSecret:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credClientSecret")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input value={cred.scope||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],scope:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credScopeOptional")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                    </>}
                    {(cred.kind === "oauth2_saml_bearer" || cred.kind === "oauth2_authorization_code") && <>
                      <input value={cred.tokenUrl||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],tokenUrl:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credTokenUrl")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input value={cred.clientId||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],clientId:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credClientId")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input type="password" value={cred.clientSecret||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],clientSecret:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credClientSecret")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                      <input value={cred.scope||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],scope:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credScopeAudience")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />
                    </>}
                    {cred.kind === "secure_param" && <input type="password" value={cred.value||""} onChange={e => { const n=[...transportCreds];n[i]={...n[i],value:e.target.value};setTransportCreds(n); }} placeholder={t("iflowTransport.credSecureWert")} style={{ background: v("surface-1"), border: `1px solid ${v("border")}`, borderRadius: 6, padding: "8px 10px", color: "var(--text-1)", fontSize: 12 }} />}
                    {cred.kind === "none" && <div style={{ fontSize: 11, color: "var(--text-3)", padding: "4px 0" }}>{t("iflowTransport.credUebersprungen")}</div>}
                  </div>
                </div>
              ))}
              <MuiButton onClick={() => setTransportCreds(c => [...c, { name: "", kind: "basic", user: "", password: "", tokenUrl: "", clientId: "", clientSecret: "", scope: "", value: "" }])} variant="outlined" size="small" sx={{mb:2}}>{t("iflowTransport.credWeitere")}</MuiButton>
              {transportResult && <div style={{ padding: "12px 16px", borderRadius: 8, marginBottom: 12, background: transportResult.success ? v("status-ok-bg") : v("status-fail-bg"), border: `1px solid ${transportResult.success ? v("success") : v("danger")}`, fontSize: 13 }}>
                {transportResult.success
                  ? <span style={{ color: v("success") }}>{transportResult.queued ? t("transport.eingereicht", { id: transportResult.requestId }) : `${transportResult.existed ? t("iflowTransport.ergebnisErfolgAktualisiert") : t("iflowTransport.ergebnisErfolgNeu")}${transportResult.deployed ? t("iflowTransport.ergebnisUndDeployed") : "."}`}</span>
                  : <div style={{ color: v("danger") }}>{t("transport.fehler", { fehler: transportResult.error })}</div>}
              </div>}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <MuiButton onClick={() => setTransportStep(2)} variant="outlined" size="small">{t("gemeinsam:aktion.zurueck")}</MuiButton>
                <button onClick={executeTransport} disabled={transportLoading || (!slzApprover&&transportApprovalNote.trim().length<10)} style={{ padding: "10px 28px", borderRadius: 8, background: transportLoading ? v("surface-2") : v("accent"), color: "var(--text-1)", border: "none", fontSize: 14, fontWeight: 700, cursor: transportLoading ? "wait" : "pointer" }}>
                  {transportLoading ? t("iflowTransport.transportiert") : slzApprover ? t("transport.knopfTransportieren") : t("transport.knopfAnfragen")}
                </button>
              </div>
            </div>)}
          </div></DialogContent>
        </MuiDialog>
      )}

      {/* Alert Configuration Modal */}
      {showAlertModal && (
        <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setShowAlertModal(false); }}>
          <div className="modal modal--alert">
            <div className="modal-banner">
              <h3 style={{margin:0,fontSize:15,fontWeight:600,color:"var(--iio-text-1)"}}>{t("alerting.titel", { name: selFlow?.Name })}</h3>
              <span style={{flex:1}}/>
              <button onClick={() => setShowAlertModal(false)} style={{height:26,width:26,border:"1px solid var(--iio-border)",background:"var(--iio-bg-2)",color:"var(--iio-text-2)",borderRadius:4,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:14}}>×</button>
            </div>
            <div className="modal-body">
              <span className={`status ${alertList.some(a=>a.enabled) ? "status--ok" : "status--idle"}`} style={{marginBottom:14,display:"flex"}}>{alertList.some(a=>a.enabled) ? t("alerting.aktiv", { count: alertList.filter(a=>a.enabled).length }) : t("alerting.keineAktiven")}</span>

              {alertList.length > 0 && (
                <div style={{marginBottom:20,marginTop:14}}>
                  <div style={{fontSize:10,color:"var(--iio-text-3)",fontWeight:600,marginBottom:8,textTransform:"uppercase",letterSpacing:".12em"}}>{t("alerting.aktiveSubs")}</div>
                  {alertList.map(al => (
                    <div key={al.id} style={{background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:8,padding:12,marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,color:"var(--iio-text-1)",fontWeight:500}}>{al.emails.join(", ")}</div>
                        <div style={{fontSize:10,color:"var(--iio-text-2)",marginTop:2}}>
                          {al.useAi ? t("alerting.kiAnalyse") : t("alerting.nurFehlertext")}{al.customText ? t("alerting.plusEigenerText") : ""} · {al.enabled ? t("alerting.zustandAktiv") : t("alerting.zustandPausiert")}
                        </div>
                      </div>
                      <button onClick={() => toggleAlert(al.id, !al.enabled)} style={{padding:"4px 10px",borderRadius:6,border:"none",fontSize:10,fontWeight:600,cursor:"pointer",
                        background:al.enabled?"rgba(45,138,90,.1)":"var(--iio-bg-3)",color:al.enabled?"var(--iio-success)":"var(--iio-text-3)"}}>
                        {al.enabled ? t("alerting.zustandAktiv") : t("alerting.zustandPausiert")}
                      </button>
                      <button onClick={() => deleteAlert(al.id)} style={{background:"none",border:"none",color:"var(--iio-critical)",cursor:"pointer",fontSize:14,fontWeight:700}}>×</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{fontSize:10,color:"var(--iio-text-3)",fontWeight:600,marginBottom:8,textTransform:"uppercase",letterSpacing:".12em"}}>{t("alerting.neueSub")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div className="field">
                  <label style={{fontSize:10,color:"var(--iio-text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:".12em"}}>{t("alerting.emails")}</label>
                  <input value={alertEmail} onChange={e => setAlertEmail(e.target.value)} placeholder={t("alerting.emailsPlatzhalter")}
                    style={{width:"100%",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:4,padding:"8px 12px",color:"var(--iio-text-1)",fontSize:12,outline:"none"}} />
                </div>

                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"10px 14px",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:8}}>
                  <input type="checkbox" checked={alertUseAi} onChange={e => setAlertUseAi(e.target.checked)} style={{width:16,height:16,accentColor:"var(--iio-signal)"}} />
                  <div>
                    <div style={{fontSize:12,color:"var(--iio-text-1)",fontWeight:600}}>{t("alerting.kiAnalyseTitel")}</div>
                    <div style={{fontSize:10,color:"var(--iio-text-2)"}}>{t("alerting.kiAnalyseDesc")}</div>
                  </div>
                </label>

                <div className="field">
                  <label style={{fontSize:10,color:"var(--iio-text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:".12em"}}>{t("alerting.eigenerTextLabel")}</label>
                  <textarea value={alertCustomText} onChange={e => setAlertCustomText(e.target.value)} placeholder={t("alerting.eigenerTextPlatzhalter")}
                    rows={2} style={{width:"100%",background:"var(--iio-bg-1)",border:"1px solid var(--iio-border)",borderRadius:4,padding:"8px 12px",color:"var(--iio-text-1)",fontSize:12,outline:"none",resize:"vertical"}} />
                </div>

                <button onClick={createAlert} disabled={alertSaving || !alertEmail.trim()} className="btn btn--signal" style={{width:"100%",height:40,opacity:alertSaving||!alertEmail.trim()?0.5:1,cursor:alertSaving||!alertEmail.trim()?"not-allowed":"pointer"}}>
                  {alertSaving ? t("alerting.speichernLaeuft") : t("alerting.speichern")}
                </button>
              </div>

              <div style={{marginTop:16,padding:12,background:"var(--iio-bg-1)",borderRadius:8,fontSize:11,color:"var(--iio-text-2)",lineHeight:1.6}}>
                {alertUseAi ? t("alerting.hinweisMitKi", { id: selFlow?.Id }) : t("alerting.hinweisOhneKi", { id: selFlow?.Id })}
              </div>
              {alertList.length > 0 && <button onClick={async () => {
                setAlertDebugLog(t("alerting.pruefe"));
                try {
                  const r = await api(`/api/${instance.id}/alerts/check-now`, { method: "POST" });
                  setAlertDebugLog((r.log || []).join("\n"));
                } catch(e) { setAlertDebugLog(t("alerting.fehler", { fehler: e.message })); }
              }} style={{marginTop:8,padding:"8px 16px",borderRadius:8,border:"1px solid var(--iio-border)",background:"var(--iio-bg-1)",color:"var(--iio-text-2)",fontSize:12,cursor:"pointer",width:"100%"}}>
                {t("alerting.jetztPruefen")}
              </button>}
              {alertDebugLog && (
                <pre style={{marginTop:8,background:"var(--iio-bg-0)",border:"1px solid var(--iio-border)",borderRadius:8,padding:12,fontSize:10,color:"var(--iio-text-2)",whiteSpace:"pre-wrap",maxHeight:300,overflow:"auto",lineHeight:1.6}}>{alertDebugLog}</pre>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn--tertiary" onClick={() => setShowAlertModal(false)}>{t("gemeinsam:aktion.schliessen")}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PACKAGE TRANSPORT WIZARD ═══ */}
      {pkgTransport&&<MuiDialog open={!!pkgTransport} onClose={()=>setPkgTransport(null)} maxWidth="md" fullWidth PaperProps={{sx:{maxHeight:"90vh"}}}>
        <DialogContent sx={{p:3}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div>
              <div style={{fontSize:18,fontWeight:700,color:"var(--text-1)",fontFamily:FONT}}>{t("transport.titel")}</div>
              <div style={{fontSize:12,color:"var(--text-3)",marginTop:2}}>{pkgTransport.Name} ({pkgTransport.Id})</div>
            </div>
            <MuiIconButton size="small" onClick={()=>setPkgTransport(null)} sx={{color:"text.secondary"}}><CloseIcon fontSize="small"/></MuiIconButton>
          </div>

          {/* Step indicators */}
          <div style={{display:"flex",gap:4,marginBottom:20}}>
            {[t("transport.schrittZiel"),t("transport.schrittAbgleich"),t("transport.schrittOptionen"),t("transport.schrittTransport")].map((s,i)=>(
              <div key={i} style={{flex:1,padding:"6px 0",textAlign:"center",fontSize:11,fontWeight:pkgTrStep===i?600:400,
                color:pkgTrStep===i?v("accent"):v("text-3"),borderBottom:`2px solid ${pkgTrStep===i?v("accent"):pkgTrStep>i?v("success"):v("border")}`,
                fontFamily:FONT}}>{i+1}. {s}</div>
            ))}
          </div>

          {/* ── Step 0: Target Instance ── */}
          {pkgTrStep===0&&<div>
            <div style={{fontSize:12,color:"var(--text-2)",marginBottom:12}}>{t("transport.zielWaehlenLang")}</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {allInstances.map(ii=>(
                <button key={ii.id} onClick={()=>{
                  setPkgTrTarget(ii.id);setPkgTrStep(1);pkgTrRunPrecheck(ii.id);
                  const defaultRuntimes=[{name:"Cloud Integration",locationId:null,selected:true}];
                  if(ii.runtimeLocations?.length){defaultRuntimes.push(...ii.runtimeLocations.map(rl=>({name:rl.name,locationId:rl.locationId,selected:false})));}
                  setPkgTrDeployRuntimes(defaultRuntimes);
                }} style={{
                  padding:"10px 18px",borderRadius:6,border:"1px solid var(--border)",background:"var(--surface-2)",
                  color:"var(--text-1)",fontSize:13,cursor:"pointer",fontFamily:FONT
                }}>{ii.name} <span style={{fontSize:10,color:"var(--text-3)",marginLeft:4}}>{ii.spaceName}</span></button>
              ))}
            </div>
            {allInstances.length===0&&<div style={{padding:20,color:"var(--text-3)",fontSize:13}}>{t("transport.keineInstanzen")}</div>}
          </div>}

          {/* ── Step 1: Content Comparison ── */}
          {pkgTrStep===1&&<div>
            {pkgTrLoading&&<div style={{padding:40,textAlign:"center",color:"var(--text-3)"}}>
              <div style={{marginBottom:8}}>{t("transport.vergleiche")}</div>
              <div style={{fontSize:11}}>{t("transport.vergleicheSub")}</div>
            </div>}

            {pkgTrComparison?.error&&<div style={{padding:16,background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,borderRadius:8,color:v("danger"),fontSize:13,marginBottom:12}}>{pkgTrComparison.error}</div>}

            {pkgTrComparison&&!pkgTrComparison?.error&&!pkgTrLoading&&<div>
              {/* Summary */}
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <div style={{padding:"8px 14px",borderRadius:8,background:v("status-ok-bg"),border:`1px solid ${v("status-ok-border")}`,fontSize:12,color:v("success")}}>
                  {t("transport.zaehlerNeu",{count:pkgTrComparison?.newCount})}</div>
                <div style={{padding:"8px 14px",borderRadius:8,background:"var(--surface-2)",border:`1px solid ${v("accent")}40`,fontSize:12,color:v("accent")}}>
                  {t("transport.zaehlerUpdate",{count:pkgTrComparison?.updateCount})}</div>
                <div style={{padding:"8px 14px",borderRadius:8,background:"var(--surface-2)",border:"1px solid var(--border)",fontSize:12,color:"var(--text-3)"}}>
                  {t("transport.zaehlerIdentisch",{count:pkgTrComparison?.identicalCount})}</div>
                <div style={{padding:"8px 14px",borderRadius:8,background:pkgTrComparison?.targetExists?v("surface-2"):v("surface-2"),
                  border:`1px solid ${pkgTrComparison?.targetExists?v("border"):v("warning")}`,fontSize:12,
                  color:pkgTrComparison?.targetExists?v("text-2"):v("warning")}}>
                  {pkgTrComparison?.targetExists?t("transport.paketExistiert"):t("transport.paketNeu")}</div>
              </div>

              {/* Select all / none */}
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <MuiButton onClick={()=>pkgTrSelectAll(true)} variant="outlined" size="small">{t("transport.alleAuswaehlen")}</MuiButton>
                <MuiButton onClick={()=>pkgTrSelectAll(false)} variant="outlined" size="small">{t("transport.keineAuswaehlen")}</MuiButton>
              </div>

              {/* Artifact table */}
              <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px 70px 40px",padding:"8px 12px",background:"var(--surface-2)",fontSize:10,color:"var(--text-3)",fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                  <div></div><div>{t("transport.spalteArtifact")}</div><div>{t("transport.spalteTyp")}</div><div>{t("transport.spalteQuelle")}</div><div>{t("transport.spalteZiel")}</div><div></div>
                </div>
                {pkgTrComparison?.comparison?.map(art=>(
                  <Fragment key={art.Id}>
                  <div style={{display:"grid",gridTemplateColumns:"32px 1fr 90px 70px 70px 40px",padding:"8px 12px",
                    borderTop:`1px solid ${v("border")}`,alignItems:"center",background:art.selected?v("surface-2"):"transparent",
                    opacity:art.draft?0.4:1}}>
                    <input type="checkbox" checked={art.selected} onChange={()=>!art.draft&&pkgTrToggleArtifact(art.Id)}
                      disabled={art.draft} style={{width:14,height:14,accentColor:"var(--accent)",cursor:art.draft?"not-allowed":"pointer"}}/>
                    <div>
                      <div style={{fontSize:13,color:art.draft?v("text-3"):"var(--text-1)",fontWeight:500}}>{art.Name}</div>
                      <div style={{fontSize:10,color:"var(--text-3)",fontFamily:FONT}}>{art.Id}</div>
                    </div>
                    <div style={{fontSize:11,color:"var(--text-2)"}}>{(art.Type||"").replace("Integration Flow","iFlow")}</div>
                    <div style={{fontSize:11,color:art.draft?v("danger"):"var(--text-1)",fontFamily:FONT}}>{art.draft?t("transport.draft"):art.Version||"-"}</div>
                    <div style={{fontSize:11,fontFamily:FONT}}>
                      {art.status==="new"&&<span style={{color:art.draft?v("text-3"):v("success")}}>{t("transport.statusNeu")}</span>}
                      {art.status==="update"&&<span style={{color:v("accent")}}>{art.targetVersion}</span>}
                      {art.status==="identical"&&<span style={{color:"var(--text-3)"}}>{art.targetVersion}</span>}
                    </div>
                    <div>{art.selected&&!art.draft&&<button onClick={(e)=>{e.stopPropagation();loadArtifactConfigs(art.Id);}} title={t("transport.paramPflichtTitel")} style={{
                      background:"none",border:"none",color:pkgTrConfigs[art.Id]?.some(c=>c.newValue!==c.ParameterValue)?v("accent"):v("text-3"),cursor:"pointer",padding:2}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </button>}</div>
                  </div>
                  {renderDraftReleaseStrip(art)}
                  </Fragment>
                ))}
              </div>

              {/* SAP Configure-style editor */}
              {pkgTrConfigOpen&&pkgTrConfigs[pkgTrConfigOpen]&&(()=>{
                const cfgs=pkgTrConfigs[pkgTrConfigOpen];
                const groups={sender:cfgs.filter(c=>c.group==="sender"),receiver:cfgs.filter(c=>c.group==="receiver"),other:cfgs.filter(c=>c.group==="other"||!c.group)};
                const tabs=[];
                if(groups.sender.length)tabs.push({key:"sender",label:t("transport.tabSender"),items:groups.sender,adapter:groups.sender[0]?.adapterType||""});
                if(groups.receiver.length)tabs.push({key:"receiver",label:t("transport.tabReceiver"),items:groups.receiver,adapter:groups.receiver[0]?.adapterType||""});
                if(groups.other.length)tabs.push({key:"other",label:tabs.length?t("transport.tabSonstige"):t("transport.tabParameter"),items:groups.other,adapter:""});
                if(!tabs.length)tabs.push({key:"empty",label:t("transport.tabParameter"),items:[],adapter:""});
                const curTab=tabs.find(t=>t.key===(window._cfgTab||tabs[0]?.key))||tabs[0];
                const comp=pkgTrMode==="multi"?pkgTrMultiChecks[pkgTrActiveTab]:pkgTrComparison;
                const artName=comp?.comparison?.find(a=>pkgTrConfigOpen.startsWith(a.Id))?.Name||"";
                return <div style={{marginTop:12,background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:`1px solid ${v("border")}`}}>
                    <div style={{fontSize:14,fontWeight:600,color:"var(--text-1)",fontFamily:FONT}}>{t("transport.configureTitel",{name:artName})}</div>
                    <MuiIconButton size="small" onClick={()=>setPkgTrConfigOpen(null)} sx={{color:"text.secondary"}}>×</MuiIconButton>
                  </div>
                  {tabs.length>1&&<div style={{display:"flex",gap:0,borderBottom:`1px solid ${v("border")}`,background:"var(--surface-0)"}}>
                    {tabs.map(t=><button key={t.key} onClick={()=>{window._cfgTab=t.key;setPkgTrConfigOpen(pkgTrConfigOpen);}} style={{
                      padding:"10px 20px",border:"none",fontSize:12,cursor:"pointer",fontWeight:curTab.key===t.key?600:400,
                      background:"transparent",color:curTab.key===t.key?v("accent"):v("text-3"),
                      borderBottom:curTab.key===t.key?`2px solid ${v("accent")}`:"2px solid transparent",
                      fontFamily:FONT}}>{t.label}</button>)}
                  </div>}
                  {curTab.adapter&&<div style={{padding:"8px 16px",fontSize:11,color:"var(--text-3)",borderBottom:`1px solid ${v("border")}`}}>
                    {t("transport.adapter")} <span style={{color:"var(--text-2)",fontWeight:600}}>{curTab.adapter}</span></div>}
                  <div style={{padding:"4px 0"}}>
                    {curTab.items.length===0&&<div style={{padding:16,color:"var(--text-3)",fontSize:12}}>{t("transport.keineParameter")}</div>}
                    {curTab.items.map(c=>(
                      <div key={c.ParameterKey} style={{padding:"10px 16px",borderBottom:"1px solid #1a1d25"}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                          <span style={{fontSize:12,color:"var(--text-2)",fontWeight:500}}>{c.fieldLabel||c.ParameterKey}</span>
                          <span style={{fontSize:9,padding:"2px 6px",borderRadius:3,
                            background:c.targetValue&&c.newValue===c.targetValue?v("status-ok-bg"):c.newValue!==c.ParameterValue?v("surface-2"):"transparent",
                            color:c.targetValue&&c.newValue===c.targetValue?v("success"):c.newValue!==c.ParameterValue?v("warning"):v("text-3"),
                            border:`1px solid ${c.targetValue&&c.newValue===c.targetValue?v("success"):c.newValue!==c.ParameterValue?v("warning"):"transparent"}`}}>
                            {c.targetValue&&c.newValue===c.targetValue?t("transport.badgeZiel"):c.newValue!==c.ParameterValue?t("transport.badgeGeaendertLang"):t("transport.badgeDefault")}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:9,color:"var(--text-3)",fontFamily:FONT,minWidth:100}}>{c.ParameterKey}</span>
                          <input value={c.newValue} onChange={e=>updateArtifactConfig(pkgTrConfigOpen,c.ParameterKey,e.target.value)}
                            style={{flex:1,background:"var(--surface-0)",border:"1px solid var(--border)",borderRadius:4,padding:"6px 10px",color:"var(--text-1)",fontSize:12,outline:"none",fontFamily:FONT}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                  {pkgTrConfigLoading&&<div style={{padding:12,color:"var(--text-3)",fontSize:11,textAlign:"center"}}>{t("transport.ladeParameter")}</div>}
                </div>;})()}


              {/* Draft warning */}
              {pkgTrComparison?.draftCount>0&&<div style={{marginTop:12,padding:"0 12px",borderRadius:8,background:v("surface-2"),border:`1px solid ${v("warning")}`,fontSize:12,color:v("warning"),lineHeight:1.5}}>
                <WarningAmberIcon sx={{fontSize:14,verticalAlign:"middle",mr:0.5}}/>
                {t("transport.draftWarnung",{count:pkgTrComparison?.draftCount})}
              </div>}

              <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
                <button onClick={()=>{setPkgTrStep(0);setPkgTrTarget("");setPkgTrComparison(null);setPkgTrOrphanSel({});}} style={{
                  padding:"0 16px",height:"var(--ctrl-h)",borderRadius:"var(--ctrl-radius)",border:`1px solid var(--border)`,background:"transparent",color:"var(--text-2)",fontSize:13,cursor:"pointer"}}>{t("gemeinsam:aktion.zurueck")}</button>
                <button onClick={()=>setPkgTrStep(2)} disabled={!pkgTrComparison?.comparison?.some(a=>a.selected)||pkgTrHasDraftDeps} style={{
                  padding:"0 16px",height:"var(--ctrl-h)",borderRadius:"var(--ctrl-radius)",border:"none",fontSize:13,fontWeight:600,cursor:"pointer",
                  background:(pkgTrComparison?.comparison?.some(a=>a.selected)&&!pkgTrHasDraftDeps)?v("accent"):v("surface-3"),
                  color:"var(--text-1)",opacity:(pkgTrComparison?.comparison?.some(a=>a.selected)&&!pkgTrHasDraftDeps)?1:0.4}}>{pkgTrHasDraftDeps?t("transport.draftAbhaengigkeit"):t("gemeinsam:aktion.weiter")}</button>
              </div>
            </div>}
          </div>}

          {/* ── Step 2: Options & Execute ── */}
          {pkgTrStep===2&&<div>
            {/* Package name override */}
            <div style={{marginBottom:16}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:8}}>
                <input type="checkbox" checked={pkgTrCustomId} onChange={e=>setPkgTrCustomId(e.target.checked)} style={{width:14,height:14,accentColor:"var(--accent)"}}/>
                <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.idAnpassen")}</span>
              </label>
              {pkgTrCustomId&&<div style={{display:"flex",gap:8,marginLeft:22}}>
                <input value={pkgTrTargetId} onChange={e=>setPkgTrTargetId(e.target.value)} placeholder={t("transport.paketIdPlatzhalter")}
                  style={{flex:1,background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",color:"var(--text-1)",fontSize:12,outline:"none",fontFamily:FONT}}/>
                <input value={pkgTrTargetName} onChange={e=>setPkgTrTargetName(e.target.value)} placeholder={t("transport.paketNamePlatzhalter")}
                  style={{flex:1,background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"6px 10px",color:"var(--text-1)",fontSize:12,outline:"none"}}/>
              </div>}
            </div>

            {/* Deploy option */}
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:pkgTrDeploy?8:16}}>
              <input type="checkbox" checked={pkgTrDeploy} onChange={e=>{setPkgTrDeploy(e.target.checked);if(!e.target.checked){setPkgTrUndeploy(false);setPkgTrUndeploySel([]);}}} style={{width:14,height:14,accentColor:"var(--accent)"}}/>
              <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.deployNachher")}</span>
            </label>
            {pkgTrDeploy&&pkgTrDeployRuntimes.length>1&&(
              <div style={{marginLeft:28,marginTop:6,padding:"8px 12px",background:v("surface-1"),borderRadius:6,border:`1px solid ${v("border")}`,marginBottom:10}}>
                <div style={{fontSize:11,color:v("text-3"),marginBottom:6}}>{t("transport.deployZieleWaehlen")}</div>
                {pkgTrDeployRuntimes.map((rt,i)=>(
                  <label key={rt.locationId||"cloud"} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:v("text-1"),cursor:"pointer",padding:"2px 0"}}>
                    <input type="checkbox" checked={rt.selected} onChange={()=>{
                      const upd=[...pkgTrDeployRuntimes];upd[i]={...upd[i],selected:!upd[i].selected};setPkgTrDeployRuntimes(upd);
                    }}/>
                    {rt.name}{!rt.locationId&&t("transport.runtimeStandard")}
                  </label>
                ))}
              </div>
            )}
            {pkgTrDeploy&&<div style={{marginLeft:22,borderLeft:"2px solid var(--border)",paddingLeft:14,marginBottom:16}}>
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:8}}>
                <input type="checkbox" checked={pkgTrUndeploy} onChange={e=>{
                  setPkgTrUndeploy(e.target.checked);
                  if(e.target.checked&&pkgTrUndeployList.length===0&&pkgTrTarget)loadTargetRuntime(pkgTrTarget);
                  if(!e.target.checked)setPkgTrUndeploySel([]);
                }} style={{width:14,height:14,accentColor:"var(--warning)"}}/>
                <span style={{fontSize:13,color:"var(--text-1)"}}>{t("transport.undeployVorher")}</span>
              </label>
              {pkgTrUndeploy&&<div>
                <div style={{fontSize:11,color:"var(--text-3)",marginBottom:8}}>{t("transport.undeployHinweis")}</div>
                {pkgTrUndeployLoading?<div style={{padding:12,color:"var(--text-3)",fontSize:12}}>{t("transport.undeployLaedt")}</div>:
                pkgTrUndeployList.length===0?<div style={{padding:12,color:"var(--text-3)",fontSize:12}}>{t("transport.undeployLeer")}</div>:
                <div>
                  <input value={pkgTrUndeploySearch} onChange={e=>setPkgTrUndeploySearch(e.target.value)} placeholder="iFlow suchen..."
                    style={{width:"100%",background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:6,padding:"7px 10px",color:"var(--text-1)",fontSize:12,outline:"none",marginBottom:6,fontFamily:FONT}}/>
                  <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden",marginBottom:8,maxHeight:220,overflowY:"auto"}}>
                    {pkgTrUndeployList.filter(f=>!pkgTrUndeploySearch||(f.Name||f.Id).toLowerCase().includes(pkgTrUndeploySearch.toLowerCase())).map(f=>{
                      const isStarted=f.Status==="STARTED"||f.Status==="Started"||f.Status==="deployed";
                      return <label key={f.Id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderTop:"1px solid var(--border)",cursor:"pointer",
                        background:pkgTrUndeploySel.includes(f.Id)?"rgba(245,158,11,.04)":"transparent"}}>
                        <input type="checkbox" checked={pkgTrUndeploySel.includes(f.Id)} onChange={()=>setPkgTrUndeploySel(p=>p.includes(f.Id)?p.filter(x=>x!==f.Id):[...p,f.Id])}
                          style={{width:14,height:14,accentColor:"var(--warning)"}}/>
                        <span style={{flex:1,fontSize:12}}>{f.Name||f.Id}</span>
                        <span style={{fontSize:10,padding:"2px 8px",borderRadius:4,background:isStarted?v("status-ok-bg"):"var(--surface-2)",color:isStarted?v("success"):"var(--text-3)"}}>{f.Status}</span>
                      </label>;})}
                  </div>
                </div>}
              </div>}
            </div>}

            {/* Summary */}
            <div style={{background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,padding:14,marginBottom:16,fontSize:12,color:"var(--text-2)",lineHeight:1.8}}>
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeQuelle")}</span> <span style={{color:"var(--text-1)"}}>{pkgTransport.Name}</span></div>
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeZielInstanz")}</span> <span style={{color:"var(--text-1)"}}>{allInstances.find(i=>i.id===pkgTrTarget)?.name}</span></div>
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeZielPaket")}</span> <span style={{color:"var(--text-1)"}}>{pkgTrCustomId?`${pkgTrTargetName} (${pkgTrTargetId})`:pkgTransport.Id}</span></div>
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeArtifacts")}</span> <span style={{color:"var(--text-1)"}}>{t("transport.summeAusgewaehlt",{count:pkgTrComparison?.comparison?.filter(a=>a.selected).length||0})}</span></div>
              {pkgTrComparison?.orphans?.filter(o=>pkgTrOrphanSel[o.Id]!==false).length>0&&<div>
                <span style={{color:v("danger")}}>{t("transport.summeLoeschen")}</span> <span style={{color:v("danger")}}>{t("transport.summeLoeschenWert",{count:pkgTrComparison.orphans.filter(o=>pkgTrOrphanSel[o.Id]!==false).length})}</span>
              </div>}
              <div><span style={{color:"var(--text-3)"}}>{t("transport.summeDeploy")}</span> <span style={{color:pkgTrDeploy?v("success"):v("text-3")}}>{pkgTrDeploy?t("transport.ja"):t("transport.nein")}</span></div>
              {!pkgTrComparison?.targetExists&&<div style={{color:v("warning"),marginTop:4}}>{t("transport.paketWirdNeuErstellt")}</div>}
            </div>

            {/* Partner Directory Einträge mittransportieren */}
            {(()=>{
              const pdDeps=pkgTrComparison?.pdDependencies;
              const isPipeline=instance.hasPipelineConcept&&pdDeps?.length>0;
              if(isPipeline){
                const missing=pdDeps.filter(pd=>!pd.existsOnTarget);
                return <div style={{marginTop:16,padding:"12px 14px",borderRadius:8,border:`1px solid ${v("border")}`,background:v("surface-1")}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                    <span style={{fontSize:13,fontWeight:600,color:v("text-1")}}>{t("transport.pdBpmnTitel")}</span>
                    <span style={{fontSize:10,color:missing.length>0?v("danger"):v("success"),marginLeft:"auto"}}>
                      {missing.length>0?t("transport.pdFehlenImZiel",{count:missing.length}):t("transport.pdAlleVorhanden")}
                    </span>
                  </div>
                  {missing.length>0&&<div style={{padding:"8px 12px",borderRadius:6,marginBottom:8,fontSize:11,
                    background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,color:v("danger")}}>
                    {t("transport.pdAutoRepliziert")}
                  </div>}
                  <div style={{border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
                    <div style={{display:"grid",gridTemplateColumns:"28px 1fr 100px 90px",padding:"6px 10px",background:v("surface-2"),fontSize:10,color:v("text-3"),fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>
                      <div></div><div>PID</div><div>{t("transport.pdSpalteReferenzen")}</div><div>{t("transport.pdSpalteZielStatus")}</div>
                    </div>
                    {pdDeps.map(pd=>(
                      <div key={pd.pid} style={{display:"grid",gridTemplateColumns:"28px 1fr 100px 90px",padding:"7px 10px",
                        borderTop:"1px solid var(--border)",alignItems:"center",
                        background:!pd.existsOnTarget?v("status-fail-bg"):"transparent"}}>
                        <div>
                          {!pd.existsOnTarget?<input type="checkbox"
                            checked={pkgTrPdEntries.find(e=>e.Pid===pd.pid)?.selected||false}
                            onChange={()=>setPkgTrPdEntries(prev=>prev.map(e=>e.Pid===pd.pid?{...e,selected:!e.selected}:e))}
                            style={{width:14,height:14,accentColor:"var(--accent)",cursor:"pointer"}}
                          />:<CheckIcon sx={{fontSize:14,color:v("success")}}/>}
                        </div>
                        <div>
                          <span style={{fontSize:12,fontWeight:500,color:pd.existsOnTarget?v("text-1"):v("danger")}}>{pd.pid}</span>
                          {pd.referencedParams?.length>0&&<div style={{fontSize:10,color:v("text-3"),marginTop:1}}>
                            {t("transport.pdKeys",{keys:pd.referencedParams.slice(0,3).join(", ")})}{pd.referencedParams.length>3&&t("transport.pdKeysMehr",{count:pd.referencedParams.length-3})}
                          </div>}
                        </div>
                        <div style={{fontSize:11,color:v("text-2")}}>{t("transport.pdReferenziertVon",{count:pd.referencedBy?.length||0})}</div>
                        <div style={{fontSize:11,fontWeight:500,color:pd.existsOnTarget?v("success"):v("danger")}}>
                          {pd.existsOnTarget?t("transport.pdVorhanden"):t("transport.pdFehlt")}
                        </div>
                      </div>
                    ))}
                  </div>
                  {missing.length>0&&<div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                    <span style={{fontSize:10,color:v("text-3")}}>
                      {t("transport.pdZurReplikation",{gewaehlt:pkgTrPdEntries.filter(e=>e.selected).length,gesamt:missing.length})}
                    </span>
                  </div>}
                </div>;
              }
              return <div style={{marginTop:16,padding:"12px 14px",borderRadius:8,border:`1px solid ${v("border")}`,background:v("surface-1")}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginBottom:pkgTrPdEnabled?10:0}}>
                  <input type="checkbox" checked={pkgTrPdEnabled} onChange={e=>{
                    setPkgTrPdEnabled(e.target.checked);
                    if(e.target.checked&&pkgTrPdEntries.length===0) loadPdEntriesForTransport();
                  }} style={{accentColor:"var(--accent)",width:14,height:14}}/>
                  <span style={{fontSize:13,fontWeight:500,color:v("text-1")}}>{t("transport.pdMitnehmen")}</span>
                </label>
                {pkgTrPdEnabled&&<>
                  {pkgTrPdLoading&&<div style={{color:v("text-3"),fontSize:12,padding:"4px 0"}}>{t("transport.pdLaedt")}</div>}
                  {!pkgTrPdLoading&&pkgTrPdEntries.length===0&&<div style={{color:v("text-3"),fontSize:12}}>{t("transport.pdKeineEintraege")}</div>}
                  {!pkgTrPdLoading&&pkgTrPdEntries.length>0&&<div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:200,overflow:"auto"}}>
                    <div style={{display:"flex",gap:8,marginBottom:4}}>
                      <button onClick={()=>setPkgTrPdEntries(e=>e.map(p=>({...p,selected:true})))}
                        style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:`1px solid ${v("border")}`,background:"transparent",color:v("accent"),cursor:"pointer"}}>{t("transport.alle")}</button>
                      <button onClick={()=>setPkgTrPdEntries(e=>e.map(p=>({...p,selected:false})))}
                        style={{fontSize:10,padding:"2px 8px",borderRadius:4,border:`1px solid ${v("border")}`,background:"transparent",color:v("text-3"),cursor:"pointer"}}>{t("transport.keine")}</button>
                      <span style={{fontSize:10,color:v("text-3"),marginLeft:"auto"}}>
                        {t("transport.pdGewaehlt",{gewaehlt:pkgTrPdEntries.filter(e=>e.selected).length,gesamt:pkgTrPdEntries.length})}
                      </span>
                    </div>
                    {pkgTrPdEntries.map(entry=>(
                      <label key={entry.Pid} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,background:v("surface-0"),cursor:"pointer"}}>
                        <input type="checkbox" checked={entry.selected} onChange={e=>{
                          setPkgTrPdEntries(prev=>prev.map(p=>p.Pid===entry.Pid?{...p,selected:e.target.checked}:p));
                        }} style={{accentColor:"var(--accent)",flexShrink:0}}/>
                        <span style={{fontSize:11,color:v("text-1"),flex:1,overflow:"hidden",textOverflow:"ellipsis"}}>{entry.Pid}</span>
                        <span style={{fontSize:10,color:v("text-3"),flexShrink:0}}>
                          {[entry.stringCount>0&&t("transport.pdStr",{count:entry.stringCount}),entry.binaryCount>0&&t("transport.pdBin",{count:entry.binaryCount}),entry.altCount>0&&t("transport.pdAlt",{count:entry.altCount})].filter(Boolean).join(", ")}
                        </span>
                      </label>
                    ))}
                  </div>}
                </>}
              </div>;
            })()}

            <div style={{display:"flex",justifyContent:"flex-end",gap:8,marginTop:16}}>
              <MuiButton variant="outlined" onClick={()=>setPkgTrStep(1)} disabled={pkgTrLoading}>{t("gemeinsam:aktion.zurueck")}</MuiButton>
              {!slzApprover&&<div style={{flex:1,minWidth:200,marginRight:10}}><textarea value={pkgTrApprovalNote} onChange={e=>setPkgTrApprovalNote(e.target.value)} placeholder={t("transport.begruendungPlatzhalter")} rows={1} style={{width:"100%",boxSizing:"border-box",resize:"vertical",minHeight:34,border:"1px solid var(--border)",borderRadius:6,background:"var(--surface-1)",color:"var(--text-1)",font:"inherit",fontSize:12,padding:"7px 10px",outline:"none"}}/><div style={{fontSize:10,color:pkgTrApprovalNote.trim().length>=10?v("success"):"var(--text-3)",textAlign:"right"}}>{t("transport.begruendungZaehler",{count:pkgTrApprovalNote.trim().length})}{pkgTrApprovalNote.trim().length>=10?"":t("transport.begruendungMindest")}</div></div>}<MuiButton variant="contained" onClick={()=>executePkgTransportPipeline()} disabled={pkgTrLoading||pkgTrHasUnconfiguredNew||(!slzApprover&&pkgTrApprovalNote.trim().length<10)}>{pkgTrHasUnconfiguredNew?t("transport.knopfKonfigurieren"):slzApprover?t("transport.knopfTransportieren"):t("transport.knopfAnfragen")}</MuiButton>
            </div>
          </div>}

          {/* Step 3: Pipeline Cards */}
          {pkgTrStep===3&&<div>
            <div style={{fontSize:14,fontWeight:600,color:"var(--text-1)",marginBottom:4}}>{t("transport.laeuftTitel")}</div>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:16}}>{pkgTransport.Name} → {allInstances.find(i=>i.id===pkgTrTarget)?.name||t("transport.laeuftZiel")}</div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
              {pkgTrPhases.map(phase=>{
                const isDone=phase.status==="done",isActive=phase.status==="active",isError=phase.status==="error",isWait=phase.status==="waiting",isWarn=phase.status==="warning";
                const bc=isDone?"var(--border)":isWarn?v("warning"):isActive?v("accent"):isError?v("danger"):"var(--border)";
                const bg=isDone?"var(--surface-0)":isWarn?"var(--surface-0)":isActive?"var(--surface-0)":isError?v("status-fail-bg"):"var(--surface-0)";
                const tc=isDone?"var(--text-1)":isWarn?v("warning"):isActive?v("accent"):isError?v("danger"):"var(--text-3)";
                return <div key={phase.id} style={{display:"flex",gap:12,alignItems:"center",padding:"14px 16px",borderRadius:8,border:`1px solid ${bc}`,background:bg,opacity:isWait?.5:1,transition:"all .3s ease"}}>
                  {isDone&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
                  {isWarn&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={v("warning")} strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
                  {isActive&&<div style={{width:18,height:18,borderRadius:"50%",border:"2px solid #3b82f6",borderTopColor:"transparent",animation:"spin .8s linear infinite"}}/>}
                  {isError&&<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
                  {isWait&&<div style={{width:18,height:18,borderRadius:"50%",background:"var(--surface-2)",border:"1px solid var(--border)"}}/>}
                  <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600,color:tc}}>{phase.label}</div><div style={{fontSize:11,color:isWait?"var(--text-3)":"var(--text-2)",marginTop:2}}>{phase.detail}</div></div>
                  {phase.duration&&<span style={{fontSize:11,fontFamily:MONO,color:tc}}>{phase.duration}</span>}
                </div>;
              })}
            </div>
            {(()=>{const gesamt=pkgTrPhases.length,d=pkgTrPhases.filter(p=>p.status==="done"||p.status==="warning").length,pct=gesamt>0?Math.round((d/gesamt)*100):0;
              return <div style={{background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,padding:14,marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{fontSize:12,color:"var(--text-2)"}}>{t("transport.gesamtfortschritt")}</span>
                  <div style={{display:"flex",gap:12}}><span style={{fontSize:11,fontFamily:MONO,color:"var(--text-3)"}}>{Math.floor(pkgTrElapsed/60)}:{String(pkgTrElapsed%60).padStart(2,"0")}</span><span style={{fontSize:12,fontFamily:MONO,fontWeight:600}}>{pct}%</span></div>
                </div>
                <div style={{width:"100%",height:6,borderRadius:3,background:"var(--border)",overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",borderRadius:3,background:pkgTrPhases.some(p=>p.status==="error")?"var(--danger)":pct>=100?"var(--success)":"var(--accent)",transition:"width .5s cubic-bezier(.4,0,.2,1)"}}/></div>
              </div>;
            })()}
            {pkgTrResult&&!pkgTrLoading&&(pkgTrResult.success
              ?<div style={{padding:12,borderRadius:8,marginBottom:16,fontSize:13,background:v("status-ok-bg"),border:`1px solid ${v("status-ok-border")}`,color:v("success")}}>
                {pkgTrResult.queued
                  ?t("transport.eingereicht",{id:pkgTrResult.requestId})
                  :`${t("transport.abgeschlossen")}${pkgTrResult.multi?t("transport.abgeschlossenZieleKurz",{count:Object.keys(pkgTrResult.results||{}).length})
                    :`${t("transport.abgeschlossenZahlen",{erstellt:pkgTrResult.summary?.created||0,aktualisiert:pkgTrResult.summary?.updated||0})}${pkgTrResult.summary?.deployed?t("transport.abgeschlossenDeployed",{count:pkgTrResult.summary.deployed}):""}`}`}
              </div>
              :<div style={{padding:12,borderRadius:8,marginBottom:16,fontSize:13,background:v("status-fail-bg"),border:`1px solid ${v("status-fail-border")}`,color:v("danger")}}>
                {pkgTrResult.error?t("transport.fehler",{fehler:pkgTrResult.error}):t("transport.fehlgeschlagen")}
              </div>
            )}
            {pkgTrResult?.deleteResults?.length>0&&!pkgTrLoading&&<div style={{marginTop:12}}>
              <div style={{fontSize:12,fontWeight:600,color:v("danger"),marginBottom:6}}>Bereinigung (veraltete Artefakte)</div>
              {pkgTrResult.deleteResults.map(dr=><div key={dr.id} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",fontSize:12}}>
                <span style={{width:16,height:16,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                  background:dr.status==="deleted"?v("status-ok-bg"):v("status-fail-bg"),
                  color:dr.status==="deleted"?v("success"):v("danger"),
                  border:`1px solid ${dr.status==="deleted"?v("status-ok-border"):v("status-fail-border")}`}}>
                  {dr.status==="deleted"?"\u2713":"\u2717"}
                </span>
                <span style={{color:"var(--text-1)"}}>{dr.name||dr.id}</span>
                {dr.wasDeployed&&<span style={{fontSize:10,color:"var(--text-3)"}}>{t("transport.bereinigungUndeployed")}</span>}
                {dr.error&&<span style={{fontSize:10,color:v("danger")}}>{dr.error}</span>}
              </div>)}
            </div>}
            <div style={{display:"flex",justifyContent:"flex-end"}}>{!pkgTrLoading&&<MuiButton variant="contained" color={pkgTrResult?.success?"success":"inherit"} onClick={()=>setPkgTransport(null)}>{t("gemeinsam:aktion.schliessen")}</MuiButton>}</div>
          </div>}
      </DialogContent>
    </MuiDialog>}

    <BpmnCompareDialog open={!!pkgTrBpmnCompare} onClose={()=>setPkgTrBpmnCompare(null)}
      sourceInstanceId={pkgTrBpmnCompare?.sourceInstanceId} targetInstanceId={pkgTrBpmnCompare?.targetInstanceId}
      artifactId={pkgTrBpmnCompare?.artifactId} targetArtifactId={pkgTrBpmnCompare?.targetArtifactId}
      artifactName={pkgTrBpmnCompare?.artifactName} sourcePackageId={pkgTrBpmnCompare?.sourcePackageId}
      sourceVersion={pkgTrBpmnCompare?.sourceVersion}
      targetVersion={pkgTrBpmnCompare?.targetVersion}/>

    </div>
  );
}
